import * as Tone from 'tone';
import { state, loadGameData, saveGameData } from './state.js';
import {
    GRAVITY_CONSTANT,
    SHOT_POWER_MULTIPLIER,
    AIM_POWER_SENSITIVITY_FACTOR,
    TRAIL_MAX_LENGTH,
    BALL_RADIUS,
    GAME_SPEED_MULTIPLIER,
    PLAY_AREA_WIDTH,
    PLAY_AREA_HEIGHT,
    COLLISION_DAMPING,
    MIN_SPEED_TO_HALVE_AFTER_COLLISION,
    MIN_SPEED_TO_STOP_AFTER_COLLISION,
    MIN_SPEED_TO_STOP_IN_SPACE,
    MIN_GRAVITY_MAGNITUDE_TO_KEEP_MOVING,
    SINK_ANIMATION_DURATION,
    DYNAMIC_MAX_DRAG_FACTOR,
    POWER_CURVE_SCALER,
    MIN_DRAG_THRESHOLD_FACTOR,
    MAX_VIEW_SCALE,
    SCROLL_ZOOM_SENSITIVITY,
    ZOOM_RETURN_DELAY_MS,
    SCROLL_GESTURE_TIMEOUT,
    LERP_SPEED_SCALE,
    LERP_SPEED_CENTER,
    LERP_SPEED_CENTER_RETURN,
    VIEW_STABILITY_SCALE_THRESHOLD,
    VIEW_STABILITY_CENTER_THRESHOLD,
    INITIAL_COURSE_VIEW_DURATION_MS,
    ARCADE_MODE_ID,
    RANDOM_COURSE_ID,
    STAR_PARALLAX_FACTOR,
    STAR_GENERATION_AREA_MULTIPLIER,
    BUNKER_ESCAPE_GRACE_DURATION,
    BUNKER_STICK_DEPTH_FRAC,
    BUNKER_FLOOR_DEPTH_FRAC,
    BUNKER_LATERAL_CHECK_GRACE_FRAC,
    BUNKER_SHOT_POWER_REDUCTION,
    MULTI_BALL_COUNT,
    MULTI_BALL_SPREAD_ANGLE,
    BUNKER_COLOR_MAIN,
    BUNKER_DEFAULT_WALL_THICKNESS,
    ARCADE_STARTING_BUCKS,
    LAUNCH_ESCAPE_GRACE_DURATION
} from './constants.js';
import {
    playSound,
    synth,
    thudBounceSynth,
    kick,
    goalSound,
    openShopSound,
    buySound,
    cannotAffordSound,
    sandTrapSound,
    checkAudioContext,
    syncMuteState,
    triggerStartArcadeSequence,
    triggerStartArcadeModeSequence,
    triggerWinArcadeSequence,
    triggerGameOverSequence,
    playStickerSlap,
    transitionMusicToGame,
    transitionMusicToMenu,
    playMenuClickSound,
    playVoiceOver,
    playOutOfBoundsSound,
    playShotCancelledSound,
    playRandomFunPhrase,
    startStretchSound,
    updateStretchSound,
    stopStretchSound,
    stopMenuMusic,
    fadeAndStopMenuMusic,
    clickBeepSound
} from './audio.js';
import {
    calculateGravityAcceleration,
    resolveCollision,
    resolveBunkerCollision,
    checkGoal,
    predictTrajectory,
    handleSingleBallStopped,
    registerArcadeGameOverCallback,
    lerp,
    clamp,
    distSq
} from './physics.js';
import {
    generateSciFiName,
    generateLevel,
    generateCourse,
    createFireworks,
    initStars,
    saveCurrentLevel,
    loadSavedLevels,
    applyPlanetStyle
} from './level.js';
import {
    mpState,
    initHost,
    initClient,
    sendPacket,
    updateLobbyUI,
    disconnectMultiplayer,
    generateRoomId,
    registerMPMessageCallback
} from './multiplayer.js';
import {
    registerUICallbacks,
    updateInGameUIDisplay,
    showGoalScreen,
    triggerArcadeGameOver,
    updatePlayerName,
    saveHighScore,
    displayHighScores,
    populateHighScoreCourseList,
    updatePowerUpShopDisplay,
    buyPowerUp,
    updatePowerUpUIDisplay,
    updatePowerUpActivationButtons,
    togglePowerUp,
    ensurePlayerNameRegistered,
    setupPlayerNamePromptModalListeners,
    activeHighScoreCourseId,
    activeHighScoreSyncType,
    invalidateLeaderboardCache,
    syncLocalScoresToGlobal,
    triggerHoleBanner,
    preloadGlobalLeaderboardData,
    getPersonalBestComparison,
    getGlobalRankingForScore,
    updateGameOverRankStamp,
    triggerAchievement,
    setupTutorialModalListeners
} from './ui.js';
import { shadeColor } from './level.js';
import { initLocalBotMatch, updateBot, registerBotCallbacks, resetBotBallToHoleStart } from './bot.js';

// --- Canvas Configuration ---
const canvas = document.getElementById('gameCanvas');
const ctx = canvas.getContext('2d');
const debugInfo = document.getElementById('debugInfo');

// --- Logging Helper ---
function log(message) {
    console.log(message);
    if (debugInfo.style.display === 'block') {
        const maxLines = 50;
        let currentText = debugInfo.innerHTML.split('<br>');
        if (currentText.length >= maxLines) currentText = currentText.slice(currentText.length - maxLines + 1);
        debugInfo.innerHTML = currentText.join('<br>') + message + '<br>';
        debugInfo.scrollTop = debugInfo.scrollHeight;
    }
}

// --- Canvas Resizing ---
function resizeCanvas() {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    state.currentDynamicMaxDrag = Math.max(220, Math.min(canvas.width, canvas.height) * DYNAMIC_MAX_DRAG_FACTOR);
    
    const minScaleX = canvas.width / PLAY_AREA_WIDTH;
    const minScaleY = canvas.height / PLAY_AREA_HEIGHT;
    state.MIN_PLAY_AREA_VIEW_SCALE = Math.min(minScaleX, minScaleY) * 0.9; 

    initStars(); 
    log(`Canvas resized: ${canvas.width}x${canvas.height}. Max drag: ${state.currentDynamicMaxDrag.toFixed(0)}px. Min View Scale: ${state.MIN_PLAY_AREA_VIEW_SCALE.toFixed(2)}`);
}

// --- Initial Course Zoom check ---
function isViewStable() {
    const now = performance.now();
    if (state.isPinching || state.isInteractingZoom) return false; 
    if (state.isButtonZoomActive) return true; 
    if (state.zoomInteractionEndTime !== 0 && state.zoomInteractionEndTime !== Infinity && now - state.zoomInteractionEndTime <= ZOOM_RETURN_DELAY_MS) return false; 
    
    if (state.initialCourseViewActive) {
        if (Math.abs(state.viewCurrentScale - MAX_VIEW_SCALE) > VIEW_STABILITY_SCALE_THRESHOLD * 3.0 || 
            Math.hypot(state.viewCurrentCenterX - state.viewTargetCenterX, state.viewCurrentCenterY - state.viewTargetCenterY) > VIEW_STABILITY_CENTER_THRESHOLD * 3.0) {
            return false;
        }
    }

    if (Math.abs(state.viewCurrentScale - state.viewTargetScale) > VIEW_STABILITY_SCALE_THRESHOLD) return false;
    if (Math.hypot(state.viewCurrentCenterX - state.viewTargetCenterX, state.viewCurrentCenterY - state.viewTargetCenterY) > VIEW_STABILITY_CENTER_THRESHOLD) return false;
    
    return true;
}

// --- Game Logic Controllers ---
export function createBallState(id, x, y, r = BALL_RADIUS, vx = 0, vy = 0, moving = false) {
    return {
        id: id,
        x: x, y: y, vx: vx, vy: vy, r: r, moving: moving,
        prev_x: x, prev_y: y, 
        isSinking: false, sinkProgress: 0, initialSinkRadius: r,
        trail: [],
        currentShotStartX: x, currentShotStartY: y,
        currentShotDistanceTraveled: 0,
        isInBunker: false,
        isEscapingBunkerPlanetIdx: -1, 
        escapingBunkerGraceTime: 0,
        isEscapingPlanetIdx: -1,
        escapingPlanetGraceTime: 0,
        homingActiveThisShot: false,
        isStickyShot: false,
        colorR: 255,
        colorG: 255,
        colorB: 255,
        rotation: 0, // Visual rotation angle
        isOOBResetting: false,
        oobProgress: 1.0,
        spawnProtectionTimer: 0,
        impactTimer: 0,
        nauseaLevel: 0,
        accumulatedRotationInFlight: 0,
        vomitCooldown: 0,
        launchPlanetIdx: -1,
        bouncedPlanetIndices: [], // Tracks unique planet indices bounced off during the current shot
        potentialGrazePlanetIdx: -1, // Tracks the planet index we are currently successfully grazing
        nonGrazablePlanetIdx: -1 // Tracks the planet we started close to at takeoff, preventing false grazes
    };
}

function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}

function isBallTouchingPlanetSurface(ball) {
    if (ball.isInBunker) return true;
    if (!state.gameState.planets || state.gameState.planets.length === 0) return false;
    for (const planet of state.gameState.planets) {
        const dist = Math.hypot(ball.x - planet.x, ball.y - planet.y);
        const radiusSum = ball.r + planet.r;
        if (Math.abs(dist - radiusSum) < 1.8) {
            return true;
        }
    }
    return false;
}

export function drawBallSkin(ctx, x, y, r, rotation, skinName, colorR, colorG, colorB, impactTimer = 0, nauseaLevel = 0) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation || 0);

    // Dynamic sick color blending (gradually going green!)
    if (nauseaLevel > 0) {
        const blend = Math.min(0.75, Math.pow(nauseaLevel, 3.5) * 0.85); // up to 75% sick green, steep non-linear transition right before spewing!
        colorR = colorR * (1 - blend) + 57 * blend;
        colorG = colorG * (1 - blend) + 219 * blend;
        colorB = colorB * (1 - blend) + 57 * blend;
    }

    const baseColor = `rgb(${Math.round(colorR)}, ${Math.round(colorG)}, ${Math.round(colorB)})`;
    const isImpacted = impactTimer > 0;

    // Procedural spiral drawer for dizzy eyes!
    function drawSpiral(ctx, cx, cy, radius, thickness) {
        ctx.save();
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = thickness;
        ctx.lineCap = 'round';
        ctx.beginPath();
        for (let theta = 0; theta < Math.PI * 4; theta += 0.15) {
            const currentR = radius * (theta / (Math.PI * 4));
            const px = cx + Math.cos(theta) * currentR;
            const py = cy + Math.sin(theta) * currentR;
            if (theta === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
        }
        ctx.stroke();
        ctx.restore();
    }

    // 1. Draw base head circle
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fillStyle = baseColor;
    ctx.fill();

    // Draw bold outer sticker border around the base head circle immediately
    if (skinName === 'rave' || skinName === 'cat' || skinName === 'alien') {
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = r * 0.12;
        ctx.stroke();
    }

    // 2. Draw standard glossy highlight overlay BEHIND face details
    ctx.save();
    ctx.rotate(-rotation || 0); // Rotate back so light reflection is always pointing top-left!
    ctx.beginPath();
    ctx.arc(-r * 0.3, -r * 0.3, r * 0.4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.22)';
    ctx.fill();
    ctx.restore();

    // 3. Now draw skin-specific details ON TOP of the gloss highlight!
    if (skinName === 'golf') {
        // --- Dimpled Golf Ball (White/Color + Dimple texture!) ---
        ctx.fillStyle = 'rgba(0, 0, 0, 0.14)';
        const dimpleCount = 7;
        const dimpleRadius = r * 0.18;
        
        // Center dimple
        ctx.beginPath();
        ctx.arc(0, 0, dimpleRadius, 0, Math.PI * 2);
        ctx.fill();

        // Ring of dimples
        for (let i = 0; i < dimpleCount; i++) {
            const angle = (i * Math.PI * 2) / dimpleCount;
            const dist = r * 0.55;
            const dx = Math.cos(angle) * dist;
            const dy = Math.sin(angle) * dist;

            ctx.beginPath();
            ctx.arc(dx, dy, dimpleRadius * 0.95, 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (skinName === 'rave') {
        // --- Rave Acid Smiley ---
        ctx.fillStyle = '#0f172a';
        ctx.strokeStyle = '#0f172a';
        ctx.lineCap = 'round';

        const eyeOffset = r * 0.28;

        if (isImpacted) {
            // Crossed squinty eyes on impact bounce: > < (centered higher up, extra bold!)
            ctx.lineWidth = r * 0.22;
            
            // Left Eye ">"
            ctx.beginPath();
            ctx.moveTo(-eyeOffset - r * 0.14, -r * 0.40);
            ctx.lineTo(-eyeOffset + r * 0.14, -r * 0.26);
            ctx.lineTo(-eyeOffset - r * 0.14, -r * 0.12);
            ctx.stroke();
            
            // Right Eye "<"
            ctx.beginPath();
            ctx.moveTo(eyeOffset + r * 0.14, -r * 0.40);
            ctx.lineTo(eyeOffset - r * 0.14, -r * 0.26);
            ctx.lineTo(eyeOffset + r * 0.14, -r * 0.12);
            ctx.stroke();
        } else if (nauseaLevel > 0.5) {
            // Funny dizzy spiral eyes!
            drawSpiral(ctx, -eyeOffset, -r * 0.26, r * 0.22, r * 0.12);
            drawSpiral(ctx, eyeOffset, -r * 0.26, r * 0.22, r * 0.12);
        } else {
            // Nice classic circular eyes
            ctx.beginPath();
            ctx.arc(-eyeOffset, -r * 0.26, r * 0.22, 0, Math.PI * 2);
            ctx.fill();
            ctx.beginPath();
            ctx.arc(eyeOffset, -r * 0.26, r * 0.22, 0, Math.PI * 2);
            ctx.fill();
        }

        // Smile or Nausea Wavy Mouth
        if (nauseaLevel > 0.5) {
            ctx.lineWidth = r * 0.12;
            ctx.beginPath();
            ctx.moveTo(-r * 0.35, r * 0.15);
            ctx.bezierCurveTo(-r * 0.18, r * 0.03, r * 0.18, r * 0.27, r * 0.35, r * 0.15);
            ctx.stroke();
        } else {
            ctx.lineWidth = r * 0.15;
            ctx.beginPath();
            ctx.arc(0, r * 0.04, r * 0.52, 0.08 * Math.PI, 0.92 * Math.PI, false);
            ctx.stroke();
        }
    } else if (skinName === 'cat') {
        // --- Cute Custom Colored Retro Neon Cat Face ---
        ctx.beginPath();
        // Left ear
        ctx.moveTo(-r * 0.95, -r * 0.2);
        ctx.lineTo(-r * 0.7, -r * 1.4);
        ctx.lineTo(-r * 0.2, -r * 0.95);
        ctx.closePath();
        ctx.fillStyle = baseColor;
        ctx.fill();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.18)'; 
        ctx.fill();

        // Right ear border
        ctx.beginPath();
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = r * 0.12;
        ctx.lineJoin = 'round';
        ctx.moveTo(-r * 0.95, -r * 0.2);
        ctx.lineTo(-r * 0.7, -r * 1.4);
        ctx.lineTo(-r * 0.2, -r * 0.95);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(r * 0.95, -r * 0.2);
        ctx.lineTo(r * 0.7, -r * 1.4);
        ctx.lineTo(r * 0.2, -r * 0.95);
        ctx.closePath();
        ctx.fillStyle = baseColor;
        ctx.fill();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
        ctx.fill();

        // Right ear border stroke
        ctx.beginPath();
        ctx.moveTo(r * 0.95, -r * 0.2);
        ctx.lineTo(r * 0.7, -r * 1.4);
        ctx.lineTo(r * 0.2, -r * 0.95);
        ctx.stroke();

        // Inner ears
        ctx.beginPath();
        ctx.moveTo(-r * 0.85, -r * 0.3);
        ctx.lineTo(-r * 0.7, -r * 1.25);
        ctx.lineTo(-r * 0.3, -r * 0.85);
        ctx.closePath();
        ctx.fillStyle = 'rgba(255, 255, 255, 0.55)'; 
        ctx.fill();

        ctx.beginPath();
        ctx.moveTo(r * 0.85, -r * 0.3);
        ctx.lineTo(r * 0.7, -r * 1.25);
        ctx.lineTo(r * 0.3, -r * 0.85);
        ctx.closePath();
        ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
        ctx.fill();

        // Face details
        ctx.fillStyle = '#0f172a';
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = r * 0.08;

        const eyeOffset = r * 0.34;

        if (isImpacted) {
            // Closed happy arcs on bounce impact: ^ ^
            ctx.lineWidth = r * 0.22;
            ctx.lineCap = 'round';
            
            ctx.beginPath();
            ctx.arc(-eyeOffset, -r * 0.08, r * 0.16, Math.PI, 0, false);
            ctx.stroke();
            
            ctx.beginPath();
            ctx.arc(eyeOffset, -r * 0.08, r * 0.16, Math.PI, 0, false);
            ctx.stroke();
        } else if (nauseaLevel > 0.5) {
            // Spiral dizzy cat eyes!
            drawSpiral(ctx, -eyeOffset, -r * 0.10, r * 0.20, r * 0.08);
            drawSpiral(ctx, eyeOffset, -r * 0.10, r * 0.20, r * 0.08);
        } else {
            ctx.beginPath();
            ctx.arc(-eyeOffset, -r * 0.10, r * 0.20, 0, Math.PI * 2);
            ctx.fill();
            ctx.beginPath();
            ctx.arc(eyeOffset, -r * 0.10, r * 0.20, 0, Math.PI * 2);
            ctx.fill();
        }

        // Cute pink cat nose
        ctx.fillStyle = '#f472b6'; 
        ctx.beginPath();
        ctx.moveTo(-r * 0.08, r * 0.08);
        ctx.lineTo(r * 0.08, r * 0.08);
        ctx.lineTo(0, r * 0.15);
        ctx.closePath();
        ctx.fill();

        // Cute cat mouth (w-shape or nausea wavy shape)
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = r * 0.08;
        ctx.lineCap = 'round';
        if (nauseaLevel > 0.5) {
            ctx.beginPath();
            ctx.moveTo(-r * 0.25, r * 0.20);
            ctx.bezierCurveTo(-r * 0.12, r * 0.10, r * 0.12, r * 0.30, r * 0.25, r * 0.20);
            ctx.stroke();
        } else {
            ctx.beginPath();
            ctx.arc(-r * 0.1, r * 0.16, r * 0.1, 0, Math.PI);
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(r * 0.1, r * 0.16, r * 0.1, 0, Math.PI);
            ctx.stroke();
        }

        // Whisker strokes
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = r * 0.08;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(-r * 0.40, r * 0.08); ctx.lineTo(-r * 1.35, -r * 0.02);
        ctx.moveTo(-r * 0.40, r * 0.20); ctx.lineTo(-r * 1.40, r * 0.32);
        ctx.moveTo(r * 0.40, r * 0.08); ctx.lineTo(r * 1.35, -r * 0.02);
        ctx.moveTo(r * 0.40, r * 0.20); ctx.lineTo(r * 1.40, r * 0.32);
        ctx.stroke();
    } else if (skinName === 'alien') {
        // --- Retro Astro-Alien Face ---
        ctx.save();
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = r * 0.16;
        ctx.lineCap = 'round';
        
        const leftTipX = isImpacted ? -r * 0.70 : -r * 0.55;
        const leftTipY = isImpacted ? -r * 1.25 : -r * 1.45;
        const rightTipX = isImpacted ? r * 0.70 : r * 0.55;
        const rightTipY = isImpacted ? -r * 1.25 : -r * 1.45;
        
        // Left antenna stem
        ctx.beginPath();
        ctx.moveTo(-r * 0.22, -r * 0.85);
        if (isImpacted) {
            ctx.quadraticCurveTo(-r * 0.35, -r * 1.30, leftTipX, leftTipY);
        } else {
            ctx.lineTo(leftTipX, leftTipY);
        }
        ctx.stroke();
        
        // Right antenna stem
        ctx.beginPath();
        ctx.moveTo(r * 0.22, -r * 0.85);
        if (isImpacted) {
            ctx.quadraticCurveTo(r * 0.35, -r * 1.30, rightTipX, rightTipY);
        } else {
            ctx.lineTo(rightTipX, rightTipY);
        }
        ctx.stroke();
        
        // Left antenna fill
        ctx.strokeStyle = baseColor;
        ctx.lineWidth = r * 0.07;
        ctx.beginPath();
        ctx.moveTo(-r * 0.22, -r * 0.85);
        if (isImpacted) {
            ctx.quadraticCurveTo(-r * 0.35, -r * 1.30, leftTipX, leftTipY);
        } else {
            ctx.lineTo(leftTipX, leftTipY);
        }
        ctx.stroke();
        
        // Right antenna fill
        ctx.beginPath();
        ctx.moveTo(r * 0.22, -r * 0.85);
        if (isImpacted) {
            ctx.quadraticCurveTo(r * 0.35, -r * 1.30, rightTipX, rightTipY);
        } else {
            ctx.lineTo(rightTipX, rightTipY);
        }
        ctx.stroke();
        
        // Antenna bulbs
        ctx.fillStyle = baseColor;
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = r * 0.08;
        
        ctx.beginPath();
        ctx.arc(leftTipX, leftTipY, r * 0.16, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        
        ctx.beginPath();
        ctx.arc(rightTipX, rightTipY, r * 0.16, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();

        // Alien face details
        ctx.fillStyle = '#0f172a';
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = r * 0.08;
        ctx.lineCap = 'round';

        const eyeOffset = r * 0.33;

        if (isImpacted) {
            ctx.lineWidth = r * 0.20;
            
            ctx.beginPath();
            ctx.moveTo(-eyeOffset - r * 0.12, -r * 0.38);
            ctx.lineTo(-eyeOffset + r * 0.12, -r * 0.26);
            ctx.lineTo(-eyeOffset - r * 0.12, -r * 0.14);
            ctx.stroke();
            
            ctx.beginPath();
            ctx.moveTo(eyeOffset + r * 0.12, -r * 0.38);
            ctx.lineTo(eyeOffset - r * 0.12, -r * 0.26);
            ctx.lineTo(eyeOffset + r * 0.12, -r * 0.14);
            ctx.stroke();
        } else if (nauseaLevel > 0.5) {
            // Spiral dizzy alien eyes!
            drawSpiral(ctx, -eyeOffset, -r * 0.16, r * 0.20, r * 0.08);
            drawSpiral(ctx, eyeOffset, -r * 0.16, r * 0.20, r * 0.08);
        } else {
            // Slanted Area 51 alien eyes
            ctx.save();
            ctx.translate(-eyeOffset, -r * 0.16);
            ctx.rotate(-0.35); 
            ctx.beginPath();
            ctx.ellipse(0, 0, r * 0.16, r * 0.32, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();

            ctx.save();
            ctx.translate(eyeOffset, -r * 0.16);
            ctx.rotate(0.35); 
            ctx.beginPath();
            ctx.ellipse(0, 0, r * 0.16, r * 0.32, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }

        // Tiny alien smile or wavy line
        ctx.lineWidth = r * 0.08;
        if (nauseaLevel > 0.5) {
            ctx.beginPath();
            ctx.moveTo(-r * 0.20, r * 0.28);
            ctx.bezierCurveTo(-r * 0.10, r * 0.20, r * 0.10, r * 0.36, r * 0.20, r * 0.28);
            ctx.stroke();
        } else {
            ctx.beginPath();
            ctx.arc(0, r * 0.28, r * 0.10, 0, Math.PI, false);
            ctx.stroke();
        }
    }

    ctx.restore();
}

function getBallTargetColor(ball) {
    // If it's the primary ball (id === 0) and we are aiming or stationary, we should check activePowerUps toggles
    if (ball.id === 0 && !ball.moving && !ball.isSinking && state.currentPlayingMode === 'powerup') {
        if (state.activePowerUps.stickyBall) return { r: 255, g: 215, b: 0 };
        if (state.activePowerUps.multiBall) return { r: 255, g: 105, b: 180 };
        if (state.activePowerUps.linearLaser) return { r: 52, g: 211, b: 153 }; // Neon Emerald
    }
    
    // Otherwise, check flight properties
    if (ball.isStickyShot) return { r: 255, g: 215, b: 0 };
    if (ball.linearLaserActive) return { r: 52, g: 211, b: 153 }; // Neon Emerald
    if (state.gameState.multiBallActiveOnScreen && ball.moving && ball.id < MULTI_BALL_COUNT) return { r: 255, g: 105, b: 180 };
    if (ball.isInBunker) return { r: 240, g: 230, b: 140 };
    
    // Fall back to player's custom color if it is the primary ball
    if (ball.id === 0 || (state.currentPlayingMode === 'multiplayer' && ball.id === state.gameState.balls[0]?.id)) {
        const custom = hexToRgb(state.customBallColor || '#FFFFFF');
        if (custom) return custom;
    }
    
    return { r: 255, g: 255, b: 255 };
}

function getClosestPointOnSegment(px, py, ax, ay, bx, by) {
    const abx = bx - ax;
    const aby = by - ay;
    const apx = px - ax;
    const apy = py - ay;
    
    const ab2 = abx * abx + aby * aby;
    if (ab2 === 0) return { x: ax, y: ay, t: 0 };
    
    let t = (apx * abx + apy * aby) / ab2;
    t = Math.max(0, Math.min(1, t)); // clamp to line segment
    
    return {
        x: ax + t * abx,
        y: ay + t * aby,
        t: t
    };
}

export function resetBallToStartLocation() {
    if (!state.gameState.planets || state.gameState.planets.length === 0) {
        log("Cannot reset ball, no planets available.");
        state.gameState.balls = [createBallState(0, PLAY_AREA_WIDTH / 2, PLAY_AREA_HEIGHT / 2, BALL_RADIUS)];
        state.teeState.visible = false; state.teeState.flying = false;
        return;
    }
    const startPlanet = state.gameState.planets[0]; 
    let spawnAngleOnPlanet = state.gameState.levelSpawnAngle !== undefined ? state.gameState.levelSpawnAngle : Math.random() * 2 * Math.PI; 

    if (state.currentPlayingMode === 'multiplayer') {
        const slot = mpState.playerId; // 0, 1, 2, or 3
        const spread = 28 / startPlanet.r; // angle spread for ball and a half physical spacing (at least 28px center-to-center)
        const offset = (slot - 1.5) * spread; // spreads slots 0, 1, 2, 3 safely without collisions
        spawnAngleOnPlanet += offset;
    }

    state.teeState.angle = spawnAngleOnPlanet;  
    state.teeState.tipX = startPlanet.x + Math.cos(state.teeState.angle) * (startPlanet.r + state.teeState.height);
    state.teeState.tipY = startPlanet.y + Math.sin(state.teeState.angle) * (startPlanet.r + state.teeState.height);

    const ballStartX = state.teeState.tipX + Math.cos(state.teeState.angle) * BALL_RADIUS; 
    const ballStartY = state.teeState.tipY + Math.sin(state.teeState.angle) * BALL_RADIUS;
    
    state.gameState.balls = [createBallState(0, ballStartX, ballStartY, BALL_RADIUS)];
    const primaryBall = state.gameState.balls[0];
    primaryBall.rotation = spawnAngleOnPlanet + Math.PI / 2; // Face out to space relative to surface normal!
    primaryBall.r = BALL_RADIUS; 
    primaryBall.isInBunker = false; 
    primaryBall.isEscapingBunkerPlanetIdx = -1;
    primaryBall.escapingBunkerGraceSteps = 0;
    primaryBall.homingActiveThisShot = false;
    primaryBall.isStickyShot = false;
    primaryBall.spawnProtectionTimer = state.currentPlayingMode === 'multiplayer' ? 30 : 0;

    state.gameState.lastShotDistanceTraveled = 0; 
    state.bunkerHitsInCurrentHole = 0;
    state.hasBouncedOffRockInCurrentHole = false;

    state.currentHoleOptimalStartX = primaryBall.x;
    state.currentHoleOptimalStartY = primaryBall.y;
    const goalPlanet = state.gameState.planets[state.gameState.goal.planetIndex];
    const goalHoleX = goalPlanet.x + goalPlanet.r * Math.cos(state.gameState.goal.angleOnPlanet);
    const goalHoleY = goalPlanet.y + goalPlanet.r * Math.sin(state.gameState.goal.angleOnPlanet);
    state.currentHoleOptimalDistance = Math.hypot(goalHoleX - state.currentHoleOptimalStartX, goalHoleY - state.currentHoleOptimalStartY);

    if (state.shotCount === 0 && state.currentHole >= 0) { 
        state.teeState.visible = true;
        state.teeState.flying = false;
    } else {
        state.teeState.visible = false;
        state.teeState.flying = false;
    }

    if (state.currentPlayingMode === 'multiplayer' && state.multiplayerLocalFinished) {
        if (state.gameState.balls[0]) {
            state.gameState.balls[0].x = -9999;
            state.gameState.balls[0].y = -9999;
            state.gameState.balls[0].vx = 0;
            state.gameState.balls[0].vy = 0;
            state.gameState.balls[0].moving = false;
        }
        state.teeState.visible = false;
    }

    log(`Ball reset. Ready for shot ${state.shotCount+1}. Tee visible: ${state.teeState.visible}. Ball center at (${primaryBall.x.toFixed(1)}, ${primaryBall.y.toFixed(1)}). Tee tip at (${state.teeState.tipX.toFixed(1)}, ${state.teeState.tipY.toFixed(1)})`);
}

export function initGame(skipResets = false) {
    document.getElementById('startScreen').classList.remove('active');
    document.getElementById('gameOverScreen').classList.remove('active');
    document.getElementById('settingsPanel').classList.remove('active');
    document.getElementById('goalScreen').classList.remove('active');
    document.getElementById('highScoreCourseListModal')?.classList.remove('active');
    document.getElementById('highScoreDisplayModal').classList.remove('active');
    document.getElementById('powerUpShopModal').classList.remove('active');
    
    document.getElementById('gameUIGroup').style.display = 'block';
    
    if (state.currentPlayingMode === 'powerup') { 
        document.getElementById('powerUpControlsContainer').style.display = 'flex';
        document.getElementById('powerUpShopBtn').style.display = 'block';
    } else {
        document.getElementById('powerUpControlsContainer').style.display = 'none';
        document.getElementById('powerUpShopBtn').style.display = 'none';
    }

    if (state.currentPlayingMode !== 'multiplayer') {
        hideMultiplayerHUD();
    }
    
    if (!skipResets) {
        // Transition / highpass menu background music out to silent in-game play
        transitionMusicToGame();
        
        // Play robotic voice intro start sequences depending on playing mode
        if (state.currentPlayingMode === 'powerup') {
            triggerStartArcadeModeSequence(); // Arcade Special Start!
        } else {
            triggerStartArcadeSequence(); // Classic / Custom Start!
        }

        state.isArcadeRunOver = false; 
        state.lastRunTimestamp = null; // Clear latest completed run highlight on new game start
        state.currentHole = 0; 
        state.currentHoleName = "";
        state.totalScore = 0; 
        state.shotCount = 0; 
        state.totalCoursePar = 0; 
        state.totalCourseDistance = 0; 
        state.totalCourseOptimalDistance = 0;
        state.gameState.goalReached = false;
        state.gameState.balls = []; 
        state.gameState.planets = [];
        state.gameState.coins = [];
        state.gameState.previousShotTrail = [];
        state.gameState.lastShotDistanceTraveled = 0;
        state.gameState.currentHoleDistanceTraveled = 0;
        state.gameState.isAiming = false;
        state.fireworks = [];
        state.collisionParticles = [];
        state.currentClassicCourseLevelsPlayed = []; 

        state.activePowerUps = {
            trajectoryLengthener: false,
            gravityGrid: false,
            stickyBall: false,
            linearLaser: false,
            chronoRewind: false,
            multiBall: false,
            oobShield: false
        };
        state.gameState.multiBallActiveOnScreen = false;
        state.rewindState = null;

        if (state.currentPlayingMode === 'powerup') { 
            state.totalSpaceBucks = ARCADE_STARTING_BUCKS; 
            state.ownedPowerUps = {
                trajectoryLengthener: 0,
                gravityGrid: 0,
                stickyBall: 0,
                linearLaser: 0,
                chronoRewind: 0,
                multiBall: 0,
                oobShield: 0
            };
            log(`Starting Arcade Mode. Bucks and Powerups RESET. Initial Bucks: ${state.totalSpaceBucks}`);
            updatePowerUpUIDisplay(); 
            updatePowerUpActivationButtons(); 
            state.TOTAL_HOLES = Infinity; 
        } else if (state.isPlayingCustomCourse && state.currentPlayingCourseDefinition) { 
            state.TOTAL_HOLES = state.currentPlayingCourseDefinition.levels.length;
            log(`Starting custom course: "${state.currentPlayingCourseDefinition.name}" with ${state.TOTAL_HOLES} holes.`);
        } else { // Classic 9 hole
            state.TOTAL_HOLES = 9; 
            log(`Starting Classic 9 Hole course with ${state.TOTAL_HOLES} holes.`);
        }

        loadNextLevel();
    }
    
    state.isButtonZoomActive = false; 
    document.getElementById('toggleZoom').textContent = 'Show Course';
    state.isInteractingZoom = false; 
    state.lastScrollEventTime = 0; 
    
    if (!state.gameLoopRequestId) {
        state.gameLoopRequestId = requestAnimationFrame(gameLoop);
    }
}

export function loadNextLevel() {
    state.wormholeActive = true;
    state.wormholeProgress = 0;
    state.shotCount = 0; 
    state.gameState.goalReached = false;
    state.currentHoleName = "";
    state.gameState.previousShotTrail = []; 
    state.gameState.lastShotDistanceTraveled = 0;
    state.gameState.currentHoleDistanceTraveled = 0;
    state.gameState.isAiming = false;
    state.gameState.multiBallActiveOnScreen = false; 
    const cardFinalShotWarning = document.getElementById('cardFinalShotWarning');
    if (cardFinalShotWarning) cardFinalShotWarning.style.display = 'none';
    const largeFinalShotAlert = document.getElementById('largeFinalShotAlert');
    if (largeFinalShotAlert) largeFinalShotAlert.style.opacity = '0';
    
    state.activePowerUps.trajectoryLengthener = false;
    state.activePowerUps.gravityGrid = false;
    state.activePowerUps.stickyBall = false;
    state.activePowerUps.linearLaser = false;
    state.activePowerUps.chronoRewind = false;
    state.activePowerUps.multiBall = false;
    state.activePowerUps.oobShield = false;
    state.rewindState = null;
    
    if (state.gameState.balls[0]) {
        state.gameState.balls[0].homingActiveThisShot = false;
        state.gameState.balls[0].isStickyShot = false;
        state.gameState.balls[0].linearLaserActive = false;
    }
    updatePowerUpActivationButtons();

    let levelData;
    if (state.currentPlayingMode === 'multiplayer') {
        state.currentHole = state.currentHoleIndex; // Keep classic currentHole property synced for multiplayer!
        levelData = mpState.courseLevels[state.currentHoleIndex];
        if (!levelData) {
            console.error("Multiplayer error: No level data for hole", state.currentHoleIndex);
            levelData = generateLevel();
        }
        state.currentHoleName = levelData.name || `Hole ${state.currentHoleIndex + 1}`;
        log(`Loading synchronized multiplayer hole ${state.currentHoleIndex + 1}/9 ("${state.currentHoleName}").`);
    } else if (state.isPlayingCustomCourse && state.currentPlayingCourseDefinition && state.currentHole < state.currentPlayingCourseDefinition.levels.length) {
        levelData = JSON.parse(JSON.stringify(state.currentPlayingCourseDefinition.levels[state.currentHole])); 
        state.currentHoleName = levelData.name || `Hole ${state.currentHole + 1}`;
        log(`Loading hole ${state.currentHole + 1}/${state.TOTAL_HOLES} ("${state.currentHoleName}") from custom course "${state.currentPlayingCourseDefinition.name}".`);
    } else { 
        levelData = generateLevel(state.currentPlayingMode === 'powerup' ? state.totalScore : state.currentHole);
        state.currentHoleName = levelData.name; 
        if (state.currentPlayingMode === 'powerup') {
             log(`Generated Arcade hole ${state.totalScore + 1} ("${state.currentHoleName}"). Par: ${levelData.par}`);
        } else { 
             log(`Generated random hole ${state.currentHole + 1}/${state.TOTAL_HOLES} ("${state.currentHoleName}"). Par: ${levelData.par}`);
             state.currentClassicCourseLevelsPlayed.push(JSON.parse(JSON.stringify(levelData))); 
        }
    }
    
    state.gameState.planets = levelData.planets.map((p, pIndex) => {
        const mappedP = {
            ...p, 
            bunkers: p.bunkers ? p.bunkers.map(b => ({
                ...b,
                wallThickness: b.wallThickness || BUNKER_DEFAULT_WALL_THICKNESS
            })) : []
        };
        if (!mappedP.style) {
            applyPlanetStyle(mappedP, pIndex === 0);
        }
        return mappedP;
    });

    state.gameState.goal = {
        ...levelData.goal,
        wiggleAngle: 0,
        wiggleVel: 0
    };
    state.currentPar = levelData.par;
    if (!state.multiplayerLocalFinished) {
        state.totalCoursePar += state.currentPar; 
    }
    
    state.gameState.levelSpawnAngle = levelData.spawnAngle;

    // Cache static rendering layers (planets textures, bunkers, gravity grid mesh) for ultra-performance
    preRenderPlanets();
    cacheGravityGrid();
    generateCoinsForLevel();

    resetBallToStartLocation(); 
    updateInGameUIDisplay();
    
    const primaryBall = state.gameState.balls[0]; 
    if (state.shotCount === 0 && primaryBall && !state.multiplayerLocalFinished) { 
        state.initialCourseViewActive = true;
        state.initialCourseViewStartTime = performance.now();
        state.viewCurrentScale = state.MIN_PLAY_AREA_VIEW_SCALE;
        state.viewCurrentCenterX = PLAY_AREA_WIDTH / 2;
        state.viewCurrentCenterY = PLAY_AREA_HEIGHT / 2;
        state.viewTargetScale = MAX_VIEW_SCALE;
        state.viewTargetCenterX = primaryBall.x;
        state.viewTargetCenterY = primaryBall.y;
        state.zoomInteractionEndTime = Infinity; 
        log("Initial course smooth zoom-in initiated.");
    } else {
        state.initialCourseViewActive = false;
        state.zoomInteractionEndTime = 0; 
    }

    const displayedHole = state.currentPlayingMode === 'powerup' ? state.totalScore + 1 : state.currentHole + 1;
    const isMP = state.currentPlayingMode === 'multiplayer';
    triggerHoleBanner(displayedHole, state.currentPar, isMP);
}

// --- Updates and Physics Cycles ---
let lastTime = 0;

function update(currentTime) {
    if (state.currentPlayingMode === 'powerup' && state.isArcadeRunOver) {
        return;
    }
    if (!lastTime) { lastTime = currentTime; return; } 
    let deltaTime = Math.min((currentTime - lastTime) / 1000, 0.1); 
    lastTime = currentTime;

    // Track frame rate dynamically to synchronize trajectory prediction step size
    if (deltaTime > 0.001) {
        state.lastDeltaTime = deltaTime;
    }

    // Run local Bot AI tick if active
    if (state.isBotGame) {
        updateBot(deltaTime);
    }

    // Run physics step natively once per frame using actual delta time for buttery smoothness
    runPhysicsStep(deltaTime);
}

function runPhysicsStep(deltaTime) {
    const effectiveDeltaTime = deltaTime * GAME_SPEED_MULTIPLIER; 
    const now = performance.now();
    
    if (state.mpRecentlyRefocusedTimer > 0) {
        state.mpRecentlyRefocusedTimer--;
    }
    const primaryBall = state.gameState.balls[0]; 

    if (state.wormholeActive) {
        state.wormholeProgress += effectiveDeltaTime;
        if (state.wormholeProgress >= 0.8) {
            state.wormholeActive = false;
        }
    }

    if (state.collisionParticles) {
        for (let i = state.collisionParticles.length - 1; i >= 0; i--) {
            const p = state.collisionParticles[i];
            p.x += p.vx * effectiveDeltaTime;
            p.y += p.vy * effectiveDeltaTime;
            p.life -= effectiveDeltaTime;
            if (p.life <= 0) {
                state.collisionParticles.splice(i, 1);
            }
        }
    }

    // Smoothly interpolate ball colors towards their target states
    state.gameState.balls.forEach(ball => {
        const targetColor = getBallTargetColor(ball);
        const lerpFactor = 0.12; 
        ball.colorR = lerp(ball.colorR, targetColor.r, lerpFactor);
        ball.colorG = lerp(ball.colorG, targetColor.g, lerpFactor);
        ball.colorB = lerp(ball.colorB, targetColor.b, lerpFactor);
    }); 

    // Dynamic spring-dampened update of flag wiggle
    if (state.gameState.goal) {
        const goal = state.gameState.goal;
        if (goal.wiggleAngle === undefined) goal.wiggleAngle = 0;
        if (goal.wiggleVel === undefined) goal.wiggleVel = 0;
        
        const springK = 350; // stiffness
        const damping = 8;   // friction/damping
        const accel = -springK * goal.wiggleAngle - damping * goal.wiggleVel;
        
        goal.wiggleVel += accel * effectiveDeltaTime;
        goal.wiggleAngle += goal.wiggleVel * effectiveDeltaTime;
    } 

    if (state.isInteractingZoom && !state.isPinching && (now - state.lastScrollEventTime > SCROLL_GESTURE_TIMEOUT)) {
        state.isInteractingZoom = false; 
        state.zoomInteractionEndTime = now; 
        log("Scroll gesture timed out. Auto-return initiated.");
    }
    
    if (!state.isButtonZoomActive) {
        if (state.currentPlayingMode === 'multiplayer' && state.multiplayerLocalFinished) {
            // Check if we are still celebrating our finish!
            if (state.localFinishCelebrationEndTime && now < state.localFinishCelebrationEndTime) {
                // Focus on the final Hole 9 green goal
                const levelData = mpState.courseLevels[state.currentHoleIndex];
                if (levelData && levelData.planets && levelData.goal) {
                    const goalPlanet = levelData.planets[levelData.goal.planetIndex];
                    if (goalPlanet) {
                        const goalHoleX = goalPlanet.x + goalPlanet.r * Math.cos(levelData.goal.angleOnPlanet);
                        const goalHoleY = goalPlanet.y + goalPlanet.r * Math.sin(levelData.goal.angleOnPlanet);
                        state.viewTargetScale = 0.85;
                        state.viewTargetCenterX = goalHoleX;
                        state.viewTargetCenterY = goalHoleY;
                    }
                }
            } else {
                // Spectator mode: focus on the selected or leading active opponent/bot (or finished if selected)!
                let spectatePlayer = null;
                if (state.spectatingPlayerId !== null && state.spectatingPlayerId !== undefined) {
                    spectatePlayer = mpState.players.find(p => p.id === state.spectatingPlayerId);
                }
                if (!spectatePlayer || (spectatePlayer.finished && mpState.players.some(p => p.id !== mpState.playerId && !p.finished))) {
                    spectatePlayer = mpState.players.find(p => p.id !== mpState.playerId && !p.finished);
                    if (!spectatePlayer) {
                        spectatePlayer = mpState.players.find(p => p.id !== mpState.playerId); // fallback
                    }
                    if (spectatePlayer) {
                        state.spectatingPlayerId = spectatePlayer.id; // Sync active spectated ID
                        updateMPLeaderboardHUD(); // Refresh active spectated highlights
                    }
                }
                
                if (spectatePlayer) {
                    if (state.currentHoleIndex !== spectatePlayer.holeIndex) {
                        state.currentHoleIndex = spectatePlayer.holeIndex;
                        loadNextLevel();
                    }
                    
                    state.viewTargetScale = 0.75; // spectator zoom
                    
                    // Focus on the goal hole if their ball is sunk or finished/out-of-bounds placeholder
                    if (spectatePlayer.finished || !spectatePlayer.ballState || spectatePlayer.ballState.x < -1000) {
                        const levelData = mpState.courseLevels[spectatePlayer.holeIndex];
                        if (levelData && levelData.planets && levelData.goal) {
                            const goalPlanet = levelData.planets[levelData.goal.planetIndex];
                            if (goalPlanet) {
                                const goalHoleX = goalPlanet.x + goalPlanet.r * Math.cos(levelData.goal.angleOnPlanet);
                                const goalHoleY = goalPlanet.y + goalPlanet.r * Math.sin(levelData.goal.angleOnPlanet);
                                state.viewTargetCenterX = goalHoleX;
                                state.viewTargetCenterY = goalHoleY;
                            }
                        }
                    } else {
                        // Focus on active ball coordinates
                        state.viewTargetCenterX = spectatePlayer.ballState.x;
                        state.viewTargetCenterY = spectatePlayer.ballState.y;
                    }
                } else {
                // Fallback to goal hole
                const levelData = mpState.courseLevels[state.currentHoleIndex];
                if (levelData && levelData.planets && levelData.goal) {
                    const goalPlanet = levelData.planets[levelData.goal.planetIndex];
                    if (goalPlanet) {
                        const goalHoleX = goalPlanet.x + goalPlanet.r * Math.cos(levelData.goal.angleOnPlanet);
                        const goalHoleY = goalPlanet.y + goalPlanet.r * Math.sin(levelData.goal.angleOnPlanet);
                        state.viewTargetScale = 0.85;
                        state.viewTargetCenterX = goalHoleX;
                        state.viewTargetCenterY = goalHoleY;
                    }
                } else {
                    state.viewTargetScale = state.MIN_PLAY_AREA_VIEW_SCALE;
                    state.viewTargetCenterX = PLAY_AREA_WIDTH / 2;
                    state.viewTargetCenterY = PLAY_AREA_HEIGHT / 2;
                }
            }
        }
} else if (state.initialCourseViewActive) {
            const progressRatio = Math.min(1, (now - state.initialCourseViewStartTime) / INITIAL_COURSE_VIEW_DURATION_MS);
            const dynamicLerpFactor = 0.90 + Math.pow(progressRatio, 2) * 0.50; 
            
            const initialZoomLerpScaleDynamic = LERP_SPEED_SCALE * dynamicLerpFactor;
            const initialZoomLerpCenterDynamic = LERP_SPEED_CENTER_RETURN * dynamicLerpFactor;

            state.viewCurrentScale = lerp(state.viewCurrentScale, state.viewTargetScale, initialZoomLerpScaleDynamic); 
            state.viewCurrentCenterX = lerp(state.viewCurrentCenterX, state.viewTargetCenterX, initialZoomLerpCenterDynamic);
            state.viewCurrentCenterY = lerp(state.viewCurrentCenterY, state.viewTargetCenterY, initialZoomLerpCenterDynamic);

            if (now - state.initialCourseViewStartTime > INITIAL_COURSE_VIEW_DURATION_MS) {
                state.initialCourseViewActive = false;
                state.zoomInteractionEndTime = 0; 
                state.viewCurrentScale = state.viewTargetScale; state.viewCurrentCenterX = state.viewTargetCenterX; state.viewCurrentCenterY = state.viewTargetCenterY; 
                log("Initial course smooth zoom-in completed by time.");
            } else {
                if (Math.abs(state.viewCurrentScale - state.viewTargetScale) < VIEW_STABILITY_SCALE_THRESHOLD * 3.0 && 
                    Math.hypot(state.viewCurrentCenterX - state.viewTargetCenterX, state.viewCurrentCenterY - state.viewTargetCenterY) < VIEW_STABILITY_CENTER_THRESHOLD * 3.0) {
                    state.initialCourseViewActive = false;
                    state.zoomInteractionEndTime = 0;
                    state.viewCurrentScale = state.viewTargetScale; state.viewCurrentCenterX = state.viewTargetCenterX; state.viewCurrentCenterY = state.viewTargetCenterY; 
                    log("Initial course smooth zoom-in completed by reaching target (relaxed threshold).");
                }
            }
        } else if (state.gameState.isAiming && primaryBall) {
            const maxDrag = state.currentDynamicMaxDrag || 150;
            const powerRatio = clamp(state.gameState.aimPower / maxDrag, 0, 1);
            
            // Curved power ratio (1.6 power slope) so zoom is gentle at start and only expands at full power
            const curvedRatio = Math.pow(powerRatio, 1.6);
            
            // If trajectoryLengthener aim powerup is active, zoom out a bit more to accommodate the 3x aim path
            let baseMinZoom = 0.58;
            if (state.activePowerUps.trajectoryLengthener) {
                baseMinZoom = 0.45; // Zooms out a bit more (pulls back more)
            }
            const minAimZoomScale = Math.max(state.MIN_PLAY_AREA_VIEW_SCALE, baseMinZoom);
            state.viewTargetScale = MAX_VIEW_SCALE - curvedRatio * (MAX_VIEW_SCALE - minAimZoomScale);
            state.viewTargetCenterX = primaryBall.x;
            state.viewTargetCenterY = primaryBall.y;
        } else if (state.zoomInteractionEndTime === Infinity && !state.isPinching && !state.isInteractingZoom) {
            // Manual zoom active
        } else if (state.gameState.multiBallActiveOnScreen) { 
            state.viewTargetScale = state.MIN_PLAY_AREA_VIEW_SCALE;
            state.viewTargetCenterX = PLAY_AREA_WIDTH / 2;
            state.viewTargetCenterY = PLAY_AREA_HEIGHT / 2;
        } else if (state.isPinching || state.isInteractingZoom) { 
            // Manual zoom active
        } else if (state.zoomInteractionEndTime !== 0 && now - state.zoomInteractionEndTime > ZOOM_RETURN_DELAY_MS) {
            // Auto-return phase
            if (primaryBall && !primaryBall.moving && !primaryBall.isSinking) { 
                state.viewTargetScale = MAX_VIEW_SCALE;
                state.viewTargetCenterX = primaryBall.x;
                state.viewTargetCenterY = primaryBall.y;
            } else if (primaryBall && (primaryBall.moving || primaryBall.isSinking) && state.gameState.planets.length > 0 && state.gameState.goal.planetIndex < state.gameState.planets.length) { 
                const goalPlanet = state.gameState.planets[state.gameState.goal.planetIndex];
                const goalHoleX = goalPlanet.x + goalPlanet.r * Math.cos(state.gameState.goal.angleOnPlanet);
                const goalHoleY = goalPlanet.y + goalPlanet.r * Math.sin(state.gameState.goal.angleOnPlanet);

                const minX = Math.min(primaryBall.x - primaryBall.r*2, goalHoleX - state.gameState.goal.holeRadius*2);
                const maxX = Math.max(primaryBall.x + primaryBall.r*2, goalHoleX + state.gameState.goal.holeRadius*2);
                const minY = Math.min(primaryBall.y - primaryBall.r*2, goalHoleY - state.gameState.goal.holeRadius*2);
                const maxY = Math.max(primaryBall.y + primaryBall.r*2, goalHoleY + state.gameState.goal.holeRadius*2);

                const requiredWidth = (maxX - minX) * 1.8; 
                const requiredHeight = (maxY - minY) * 1.8;

                const scaleX = canvas.width / requiredWidth;
                const scaleY = canvas.height / requiredHeight;
                
                state.viewTargetScale = clamp(Math.min(scaleX, scaleY), state.MIN_PLAY_AREA_VIEW_SCALE, MAX_VIEW_SCALE);
                state.viewTargetCenterX = (minX + maxX) / 2;
                state.viewTargetCenterY = (minY + maxY) / 2;

                if (state.viewTargetScale <= state.MIN_PLAY_AREA_VIEW_SCALE * 1.1) { 
                    state.viewTargetScale = state.MIN_PLAY_AREA_VIEW_SCALE;
                    state.viewTargetCenterX = PLAY_AREA_WIDTH / 2;
                    state.viewTargetCenterY = PLAY_AREA_HEIGHT / 2;
                }
            } else { 
                if (primaryBall) {
                     state.viewTargetScale = MAX_VIEW_SCALE;
                     state.viewTargetCenterX = primaryBall.x;
                     state.viewTargetCenterY = primaryBall.y;
                } else {
                     state.viewTargetScale = MAX_VIEW_SCALE; 
                     state.viewTargetCenterX = PLAY_AREA_WIDTH / 2;
                     state.viewTargetCenterY = PLAY_AREA_HEIGHT / 2;
                }
            }
            if (Math.abs(state.viewCurrentScale - state.viewTargetScale) < VIEW_STABILITY_SCALE_THRESHOLD && 
                Math.hypot(state.viewCurrentCenterX - state.viewTargetCenterX, state.viewCurrentCenterY - state.viewTargetCenterY) < VIEW_STABILITY_CENTER_THRESHOLD) {
                state.zoomInteractionEndTime = 0; 
                log("Zoom auto-return lerp complete.");
            }
        } else if (state.zoomInteractionEndTime === 0) { // Idle auto-zoom state
            if (primaryBall && !primaryBall.moving && !primaryBall.isSinking) { 
                state.viewTargetScale = MAX_VIEW_SCALE;
                state.viewTargetCenterX = primaryBall.x;
                state.viewTargetCenterY = primaryBall.y;
            } else if (primaryBall && (primaryBall.moving || primaryBall.isSinking) && state.gameState.planets.length > 0 && state.gameState.goal.planetIndex < state.gameState.planets.length) { 
                const goalPlanet = state.gameState.planets[state.gameState.goal.planetIndex];
                const goalHoleX = goalPlanet.x + goalPlanet.r * Math.cos(state.gameState.goal.angleOnPlanet);
                const goalHoleY = goalPlanet.y + goalPlanet.r * Math.sin(state.gameState.goal.angleOnPlanet);

                const minX = Math.min(primaryBall.x - primaryBall.r*2, goalHoleX - state.gameState.goal.holeRadius*2);
                const maxX = Math.max(primaryBall.x + primaryBall.r*2, goalHoleX + state.gameState.goal.holeRadius*2);
                const minY = Math.min(primaryBall.y - primaryBall.r*2, goalHoleY - state.gameState.goal.holeRadius*2);
                const maxY = Math.max(primaryBall.y + primaryBall.r*2, goalHoleY + state.gameState.goal.holeRadius*2);
                
                const requiredWidth = (maxX - minX) * 1.8; 
                const requiredHeight = (maxY - minY) * 1.8;

                const scaleX = canvas.width / requiredWidth;
                const scaleY = canvas.height / requiredHeight;
                
                let newTargetScale = clamp(Math.min(scaleX, scaleY), state.MIN_PLAY_AREA_VIEW_SCALE, MAX_VIEW_SCALE);
                let newTargetCenterX = (minX + maxX) / 2;
                let newTargetCenterY = (minY + maxY) / 2;

                if (newTargetScale <= state.MIN_PLAY_AREA_VIEW_SCALE * 1.1) {
                    newTargetScale = state.MIN_PLAY_AREA_VIEW_SCALE;
                    newTargetCenterX = PLAY_AREA_WIDTH / 2;
                    newTargetCenterY = PLAY_AREA_HEIGHT / 2;
                }
                if (!state.gameState.isAiming) { 
                    state.viewTargetScale = newTargetScale;
                    state.viewTargetCenterX = newTargetCenterX;
                    state.viewTargetCenterY = newTargetCenterY;
                }
            } else if (!primaryBall) { 
                state.viewTargetScale = MAX_VIEW_SCALE; 
                state.viewTargetCenterX = PLAY_AREA_WIDTH / 2;
                state.viewTargetCenterY = PLAY_AREA_HEIGHT / 2;
            }
        }
        
        if (!state.initialCourseViewActive) {
            state.viewCurrentScale = lerp(state.viewCurrentScale, state.viewTargetScale, LERP_SPEED_SCALE);
            const currentCenterLerpSpeed = (state.isPinching || state.isInteractingZoom || state.zoomInteractionEndTime === Infinity || state.gameState.isAiming) ? LERP_SPEED_CENTER : LERP_SPEED_CENTER_RETURN;
            state.viewCurrentCenterX = lerp(state.viewCurrentCenterX, state.viewTargetCenterX, currentCenterLerpSpeed);
            state.viewCurrentCenterY = lerp(state.viewCurrentCenterY, state.viewTargetCenterY, currentCenterLerpSpeed);
        }
    } 

    // --- Multiplayer Networking Synchronizations & Opponent Ball Simulation ---
    if (state.currentPlayingMode === 'multiplayer') {
        const primaryBall = state.gameState.balls[0];
        // Only send coordinates if our primary ball is valid and we aren't in a grace refocus/blur sync period
        if (primaryBall && !state.mpRecentlyRefocusedTimer && !state.isTabBlurred) {
            sendPacket({
                type: 'sync_ball',
                holeIndex: state.currentHoleIndex,
                x: primaryBall.x,
                y: primaryBall.y,
                vx: primaryBall.vx,
                vy: primaryBall.vy,
                isSinking: primaryBall.isSinking,
                isStickyShot: primaryBall.isStickyShot,
                homingActiveThisShot: primaryBall.homingActiveThisShot,
                colorR: primaryBall.colorR,
                colorG: primaryBall.colorG,
                colorB: primaryBall.colorB,
                skin: state.customBallSkin,
                trailEffect: state.customBallTrail
            });
        }
        
        // Locally simulate opponent balls between network ticks for buttery smoothness
        for (const opponent of mpState.players) {
            if (opponent.id === mpState.playerId) continue; // Skip local player
            
            if (opponent.holeIndex === state.currentHoleIndex && opponent.ballState && !opponent.ballState.isSinking) {
                const oppBall = opponent.ballState;
                if (!oppBall.trail) oppBall.trail = [];
                
                // Decrement spawn protection timer if active (set by network or tee)
                if (oppBall.spawnProtectionTimer > 0) {
                    oppBall.spawnProtectionTimer--;
                }
                
                // Decrement local bump timer
                if (oppBall.recentlyBumpedTimer > 0) {
                    oppBall.recentlyBumpedTimer--;
                }

                // Unconditionally decrement impact closed-eye timer (frame-rate independent!)
                if (oppBall.impactTimer > 0) {
                    oppBall.impactTimer -= deltaTime;
                    if (oppBall.impactTimer < 0) oppBall.impactTimer = 0;
                }
                
                const oppSpeed = Math.hypot(oppBall.vx, oppBall.vy);
                if (oppSpeed > 0.05) {
                    oppBall.trail.push({ x: oppBall.x, y: oppBall.y, alpha: 1.0 });
                    
                    const gravity = calculateGravityAcceleration(oppBall.x, oppBall.y, state.gameState.planets);
                    oppBall.vx += gravity.ax * effectiveDeltaTime;
                    oppBall.vy += gravity.ay * effectiveDeltaTime;
                    oppBall.x += oppBall.vx * effectiveDeltaTime;
                    oppBall.y += oppBall.vy * effectiveDeltaTime;

                    // Visual rotation based on velocity vector and speed (Only when touching surface in vacuum!)
                    if (isBallTouchingPlanetSurface(oppBall)) {
                        const oppSpeedForRotation = Math.hypot(oppBall.vx, oppBall.vy);
                        if (oppSpeedForRotation > 0.05) {
                            if (oppBall.rotation === undefined) oppBall.rotation = 0;
                            oppBall.rotation += (oppBall.vx >= 0 ? 1 : -1) * oppSpeedForRotation * 0.03 * effectiveDeltaTime;
                            // Lock angular velocity to roll speed while touching surface
                            oppBall.angularVelocity = (oppBall.vx >= 0 ? 1 : -1) * oppSpeedForRotation * 0.03;
                        }
                    } else {
                        // Flying in zero-friction vacuum: spin at constant angular velocity!
                        if (oppBall.angularVelocity === undefined) oppBall.angularVelocity = 0;
                        if (oppBall.rotation === undefined) oppBall.rotation = 0;
                        oppBall.rotation += oppBall.angularVelocity * effectiveDeltaTime;
                    }
                    
                    let oppBallHandledForThisPlanet = false;
                    
                    for (let planetIdx = 0; planetIdx < state.gameState.planets.length; planetIdx++) {
                        const planet = state.gameState.planets[planetIdx];
                        
                        // 1. Solid planet rock penetration check:
                        let isInsideSolidRock = false;
                        const distToPlanetCenter = Math.hypot(oppBall.x - planet.x, oppBall.y - planet.y);
                        const oppRad = oppBall.r || 8;
                        
                        if (distToPlanetCenter < planet.r - 0.5) {
                            let insideAnyCrater = false;
                            if (planet.bunkers && planet.bunkers.length > 0) {
                                for (const bunker of planet.bunkers) {
                                    const bunkerWorldSurfaceX = planet.x + planet.r * Math.cos(bunker.angle);
                                    const bunkerWorldSurfaceY = planet.y + planet.r * Math.sin(bunker.angle);
                                    const dBunker = Math.hypot(oppBall.x - bunkerWorldSurfaceX, oppBall.y - bunkerWorldSurfaceY);
                                    if (dBunker < bunker.radius - 0.5) {
                                        insideAnyCrater = true;
                                        break;
                                    }
                                }
                            }
                            if (!insideAnyCrater) {
                                isInsideSolidRock = true;
                            }
                        }
                        
                        if (isInsideSolidRock) {
                            resolveCollision(oppBall, planet, planet.r);
                            oppBallHandledForThisPlanet = true;
                        }
                        
                        // 2. Lateral bunker checks (sand trap sticky/stop physics)
                        if (!oppBallHandledForThisPlanet && planet.bunkers && planet.bunkers.length > 0) {
                            for (const bunker of planet.bunkers) {
                                const bunkerWorldSurfaceX = planet.x + planet.r * Math.cos(bunker.angle);
                                const bunkerWorldSurfaceY = planet.y + planet.r * Math.sin(bunker.angle);
                                const effectiveBunkerRadiusForLateralCheck = bunker.radius + oppRad * BUNKER_LATERAL_CHECK_GRACE_FRAC;
                                
                                if (distSq(oppBall.x, oppBall.y, bunkerWorldSurfaceX, bunkerWorldSurfaceY) < (effectiveBunkerRadiusForLateralCheck * effectiveBunkerRadiusForLateralCheck)) {
                                    oppBallHandledForThisPlanet = true;
                                    
                                    const ballRadialDist = Math.hypot(oppBall.x - planet.x, oppBall.y - planet.y);
                                    const d = Math.hypot(oppBall.x - bunkerWorldSurfaceX, oppBall.y - bunkerWorldSurfaceY);
                                    if (ballRadialDist < planet.r - 2 && d >= bunker.radius - oppRad) {
                                        // Reposition exactly on the sand floor and stop
                                        const dx = oppBall.x - bunkerWorldSurfaceX;
                                        const dy = oppBall.y - bunkerWorldSurfaceY;
                                        const distance = Math.hypot(dx, dy);
                                        if (distance > 0) {
                                            const nx = dx / distance;
                                            const ny = dy / distance;
                                            oppBall.x = bunkerWorldSurfaceX + nx * (bunker.radius - oppRad);
                                            oppBall.y = bunkerWorldSurfaceY + ny * (bunker.radius - oppRad);
                                        }
                                        oppBall.vx = 0;
                                        oppBall.vy = 0;
                                        oppBall.moving = false;
                                        oppBall.isInBunker = true;
                                    }
                                    break;
                                }
                            }
                        }
                        
                        // Flagpole physics collision
                        if (!oppBallHandledForThisPlanet && planetIdx === state.gameState.goal.planetIndex && !oppBall.isSinking) {
                            const goal = state.gameState.goal;
                            const holeAngle = goal.angleOnPlanet;
                            const currentPoleAngle = holeAngle + (goal.wiggleAngle || 0);
                            
                            const poleInnerBaseRadius = planet.r - goal.holeRadius * 0.8;
                            const ax = planet.x + poleInnerBaseRadius * Math.cos(holeAngle);
                            const ay = planet.y + poleInnerBaseRadius * Math.sin(holeAngle);
                            const bx = ax + (goal.flagHeight + goal.holeRadius * 0.8) * Math.cos(currentPoleAngle);
                            const by = ay + (goal.flagHeight + goal.holeRadius * 0.8) * Math.sin(currentPoleAngle);
                            
                            const closest = getClosestPointOnSegment(oppBall.x, oppBall.y, ax, ay, bx, by);
                            const dx = oppBall.x - closest.x;
                            const dy = oppBall.y - closest.y;
                            const distance = Math.hypot(dx, dy);
                            const poleRadius = 1.5;
                            
                            if (distance < oppRad + poleRadius) {
                                const overlap = (oppRad + poleRadius) - distance;
                                if (distance > 0.001) {
                                    oppBall.x += (dx / distance) * overlap;
                                    oppBall.y += (dy / distance) * overlap;
                                } else {
                                    const perpAngle = holeAngle + Math.PI / 2;
                                    oppBall.x += Math.cos(perpAngle) * (oppRad + poleRadius);
                                    oppBall.y += Math.sin(perpAngle) * (oppRad + poleRadius);
                                }
                                
                                const preCollisionSpeed = Math.hypot(oppBall.vx, oppBall.vy);
                                const nx = distance > 0.001 ? dx / distance : Math.cos(holeAngle + Math.PI / 2);
                                const ny = distance > 0.001 ? dy / distance : Math.sin(holeAngle + Math.PI / 2);
                                
                                const val = oppBall.vx * nx + oppBall.vy * ny;
                                if (val < 0) {
                                    const restitution = 0.25;
                                    oppBall.vx -= (1 + restitution) * val * nx;
                                    oppBall.vy -= (1 + restitution) * val * ny;
                                }
                                
                                oppBall.vx *= 0.55;
                                oppBall.vy *= 0.55;
                                
                                const tx = -Math.sin(currentPoleAngle);
                                const ty = Math.cos(currentPoleAngle);
                                const vPerp = oppBall.vx * tx + oppBall.vy * ty;
                                const wiggleImpulse = -vPerp * 0.08 * closest.t;
                                goal.wiggleVel = (goal.wiggleVel || 0) + wiggleImpulse;
                                
                                const scaledVolume = Math.min(0.9, Math.max(0.08, preCollisionSpeed / 350));
                                playSound(synth, "E5", "32n", Tone.now(), scaledVolume);
                                oppBallHandledForThisPlanet = true;
                            }
                        }
                        
                        // 3. Standard planet collision
                        if (!oppBallHandledForThisPlanet) {
                            if (Math.hypot(oppBall.x - planet.x, oppBall.y - planet.y) < oppRad + planet.r) {
                                resolveCollision(oppBall, planet, planet.r);
                            }
                        }
                    }
                } else {
                    oppBall.vx = 0;
                    oppBall.vy = 0;
                }
                
                // Out of bounds check for opponent/bot ball on screen
                if (oppBall.x < -oppBall.r * 2 || oppBall.x > PLAY_AREA_WIDTH + oppBall.r * 2 || 
                    oppBall.y < -oppBall.r * 2 || oppBall.y > PLAY_AREA_HEIGHT + oppBall.r * 2) {
                    if (state.isBotGame && opponent.isBot) {
                        resetBotBallToHoleStart(opponent, false, true);
                        log(`Bot ${opponent.name} went out of bounds on screen and was reset to last resting point.`);
                    } else {
                        oppBall.vx = 0;
                        oppBall.vy = 0;
                    }
                }
                
                // Age/fade the trail points
                for (let t = 0; t < oppBall.trail.length; t++) {
                    oppBall.trail[t].alpha -= (2.0 / TRAIL_MAX_LENGTH) * GAME_SPEED_MULTIPLIER;
                    if (oppBall.trail[t].alpha < 0) oppBall.trail[t].alpha = 0;
                }
                if (oppBall.trail.length > TRAIL_MAX_LENGTH * 3) {
                    oppBall.trail = oppBall.trail.filter(p => p.alpha > 0.001 || oppBall.trail.indexOf(p) >= oppBall.trail.length - TRAIL_MAX_LENGTH * 3);
                }
            }
        }
        
        // Update live Leaderboard HUD values
        updateMPLeaderboardHUD();
    }

    let anyBallMoving = false;
    let previouslyMultiBallActiveOnScreen = state.gameState.multiBallActiveOnScreen; 

    for (let i = state.gameState.balls.length - 1; i >= 0; i--) {
        const ball = state.gameState.balls[i];
        if (!ball) { 
            state.gameState.balls.splice(i, 1);
            continue;
        }
        
        if (ball.spawnProtectionTimer > 0) {
            ball.spawnProtectionTimer--;
        }
        
        // Unconditionally decrement impact closed-eye timer (frame-rate independent!)
        if (ball.impactTimer > 0) {
            ball.impactTimer -= deltaTime;
            if (ball.impactTimer < 0) ball.impactTimer = 0;
        }
        
        ball.prev_x = ball.x; 
        ball.prev_y = ball.y;

        if (ball.escapingBunkerGraceTime > 0 && ball.isEscapingBunkerPlanetIdx !== -1) { 
            ball.escapingBunkerGraceTime -= effectiveDeltaTime;
            if (ball.escapingBunkerGraceTime <= 0) {
                ball.escapingBunkerGraceTime = 0;
                ball.isEscapingBunkerPlanetIdx = -1; 
            }
        }

        if (ball.escapingPlanetGraceTime > 0 && ball.isEscapingPlanetIdx !== -1) {
            ball.escapingPlanetGraceTime -= effectiveDeltaTime;
            
            const launchPlanet = state.gameState.planets[ball.isEscapingPlanetIdx];
            if (launchPlanet) {
                const dist = Math.hypot(ball.x - launchPlanet.x, ball.y - launchPlanet.y);
                if (dist >= launchPlanet.r + ball.r + 0.5) {
                    ball.escapingPlanetGraceTime = 0;
                    ball.isEscapingPlanetIdx = -1;
                }
            }
            
            if (ball.escapingPlanetGraceTime <= 0) {
                ball.escapingPlanetGraceTime = 0;
                ball.isEscapingPlanetIdx = -1;
            }
        }

        // --- Nausea Update & Procedural Green Vomit Sparks ---
        const skinName = ball.id === 0 ? state.customBallSkin : (ball.skin || 'neon');
        const isFaceSkin = skinName === 'rave' || skinName === 'cat' || skinName === 'alien';
        if (ball.moving && !ball.isSinking && !state.gameState.goalReached && isFaceSkin) {
            const speed = Math.hypot(ball.vx, ball.vy);
            if (speed > 4.0) { // Require high-speed flight
                const rotationDelta = Math.abs(ball.angularVelocity || 0) * effectiveDeltaTime;
                ball.nauseaLevel = (ball.nauseaLevel || 0) + rotationDelta * 0.0024; // 10x harder than original baseline (the perfect sweet spot!)
            }
            ball.nauseaLevel = Math.min(1.2, ball.nauseaLevel || 0);
            
            if (ball.nauseaLevel >= 1.0) {
                // Initialize dynamic burst state machine on-the-fly
                if (ball.vomitIsBurstActive === undefined) {
                    ball.vomitIsBurstActive = true;
                    ball.vomitCycleTimer = 0;
                    ball.vomitBurstDuration = 0.25 + Math.random() * 0.25; // 0.25s to 0.50s ON burst
                    ball.vomitRestDuration = 1.4 + Math.random() * 1.0;   // 1.4s to 2.4s OFF resting (longer, varying rest times)
                    ball.vomitCooldown = 0;
                }
                
                ball.vomitCycleTimer = (ball.vomitCycleTimer || 0) + effectiveDeltaTime;
                
                if (ball.vomitIsBurstActive) {
                    if (ball.vomitCycleTimer >= ball.vomitBurstDuration) {
                        // Switch to rest phase and randomize rest duration for next cycle
                        ball.vomitIsBurstActive = false;
                        ball.vomitCycleTimer = 0;
                        ball.vomitRestDuration = 1.4 + Math.random() * 1.0; // 1.4s to 2.4s OFF
                    } else {
                        ball.vomitCooldown = (ball.vomitCooldown || 0) - effectiveDeltaTime;
                        if (ball.vomitCooldown <= 0) {
                            ball.vomitCooldown = 0.08; // High-density rapid spew (80ms)
                            
                            // Eject in the direction the mouth is pointing, relative to the ball's current rotation!
                            const vxDir = Math.sin(ball.rotation || 0);
                            const vyDir = Math.cos(ball.rotation || 0);
                            
                            // Spawn more particles! 2 to 4 particles (richer visual puff)
                            const pCount = 2 + Math.floor(Math.random() * 3); 
                            for (let pIdx = 0; pIdx < pCount; pIdx++) {
                                const angleSpread = (Math.random() - 0.5) * 0.8;
                                const finalVx = (vxDir * Math.cos(angleSpread) - vyDir * Math.sin(angleSpread)) * (120 + Math.random() * 140) + ball.vx * 0.45;
                                const finalVy = (vxDir * Math.sin(angleSpread) + vyDir * Math.cos(angleSpread)) * (120 + Math.random() * 140) + ball.vy * 0.45;
                                
                                if (state.collisionParticles) {
                                    state.collisionParticles.push({
                                        x: ball.x,
                                        y: ball.y,
                                        vx: finalVx,
                                        vy: finalVy,
                                        size: 1.5 + Math.random() * 2.0,
                                        color: `hsla(${90 + Math.random() * 40}, 100%, 50%, 0.95)`, // neon vomit green
                                        maxLife: 0.35 + Math.random() * 0.25, // Shorter life for rapid recycle
                                        life: 0.35 + Math.random() * 0.25
                                    });
                                }
                            }
                            
                            // Trigger the dizziness/motion sickness achievement!
                            if (ball.id === 0) {
                                triggerAchievement('dizzy');
                            }
                        }
                    }
                } else {
                    if (ball.vomitCycleTimer >= ball.vomitRestDuration) {
                        // Switch back to active spewing and randomize burst duration
                        ball.vomitIsBurstActive = true;
                        ball.vomitCycleTimer = 0;
                        ball.vomitBurstDuration = 0.25 + Math.random() * 0.25; // 0.25s to 0.50s ON
                        ball.vomitCooldown = 0;
                    }
                }
            } else {
                ball.vomitIsBurstActive = undefined;
            }
        } else {
            // Slower recovery when stopped to keep the face visible for brief enjoyment
            ball.nauseaLevel = Math.max(0, (ball.nauseaLevel || 0) - effectiveDeltaTime * 0.35);
            ball.vomitIsBurstActive = undefined;
        }

        if (ball.isSinking) {
            ball.sinkProgress += effectiveDeltaTime;
            const sinkRatio = Math.min(1, ball.sinkProgress / SINK_ANIMATION_DURATION);
            ball.r = ball.initialSinkRadius * (1 - sinkRatio); 

            const goalPlanet = state.gameState.planets[state.gameState.goal.planetIndex];
            const targetX = goalPlanet.x + goalPlanet.r * Math.cos(state.gameState.goal.angleOnPlanet); 
            const targetY = goalPlanet.y + goalPlanet.r * Math.sin(state.gameState.goal.angleOnPlanet);
            
            const dxToHole = targetX - ball.x;
            const dyToHole = targetY - ball.y;
            const distToHole = Math.hypot(dxToHole, dyToHole);
            if (distToHole > 0.1) { 
                 ball.x += (dxToHole / distToHole) * Math.min(distToHole, ball.initialSinkRadius * sinkRatio * 0.5); 
                 ball.y += (dyToHole / distToHole) * Math.min(distToHole, ball.initialSinkRadius * sinkRatio * 0.5);
            }

            if (sinkRatio >= 1) {
                ball.isSinking = false;
                ball.homingActiveThisShot = false;
                ball.isStickyShot = false;
                state.gameState.goalReached = true; 
                state.gameState.currentHoleDistanceTraveled += ball.currentShotDistanceTraveled; 
                state.totalCourseDistance += state.gameState.currentHoleDistanceTraveled;
                state.totalCourseOptimalDistance += state.currentHoleOptimalDistance;
                
                if (state.currentPlayingMode === 'multiplayer') {
                    // Send sink message
                    sendPacket({ type: 'sink', holeIndex: state.currentHoleIndex, strokes: state.shotCount });
                    
                    state.totalMultiplayerStrokes += state.shotCount;
                    console.log(`Hole completed speedrun style! Strokes on hole: ${state.shotCount}, total strokes so far: ${state.totalMultiplayerStrokes}`);
                    
                    // Update local player strokes in mpState.players list
                    const localPlayer = mpState.players.find(p => p.id === mpState.playerId);
                    if (localPlayer) {
                        localPlayer.strokes = state.totalMultiplayerStrokes;
                        localPlayer.holeIndex = state.currentHoleIndex;
                    }
                    
                    // Quick next hole transition
                    if (state.currentHoleIndex < 8) {
                        state.currentHoleIndex++;
                        loadNextLevel();
                    } else {
                        // Match finished for us!
                        mpState.endTime = performance.now();
                        const totalTime = (mpState.endTime - mpState.startTime) / 1000;
                        sendPacket({ type: 'finish', totalStrokes: state.totalMultiplayerStrokes, totalTime: totalTime });
                        
                        state.multiplayerLocalFinished = true;
                        state.multiplayerLocalCompletedAll = true;
                        
                        if (localPlayer) {
                            localPlayer.finished = true;
                            localPlayer.completedAll = true;
                            localPlayer.finalStrokes = state.totalMultiplayerStrokes;
                            localPlayer.finalTime = totalTime;
                        }
                        
                        ball.moving = false;
                        ball.x = -9999; ball.y = -9999; // hide local ball
                        
                        // Set celebration duration (camera freeze locked on final cup)
                        state.localFinishCelebrationEndTime = performance.now() + 3500;
                        
                        // Dynamic Place Calculation
                        const finishedCount = mpState.players.filter(p => p.finished && p.completedAll).length;
                        let placeStr = "1st PLACE";
                        let placeSuffix = "st";
                        if (finishedCount === 2) { placeStr = "2nd PLACE"; placeSuffix = "nd"; }
                        else if (finishedCount === 3) { placeStr = "3rd PLACE"; placeSuffix = "rd"; }
                        else if (finishedCount >= 4) { placeStr = `${finishedCount}th PLACE`; placeSuffix = "th"; }
                        
                        // Render celebration overlay
                        const overlay = document.getElementById('largeFinishAnnouncement');
                        const title = document.getElementById('largeFinishTitle');
                        const subtitle = document.getElementById('largeFinishSubtitle');
                        if (overlay && title && subtitle) {
                            title.textContent = "FINISHED!";
                            subtitle.textContent = `${placeStr} (${state.totalMultiplayerStrokes} SHOTS)`;
                            overlay.style.opacity = '1';
                            setTimeout(() => {
                                overlay.style.opacity = '0';
                            }, 3500);
                        }
                        
                        // Trigger toast notification
                        showMultiplayerToast(`🏆 YOU FINISHED! (${finishedCount}${placeSuffix} Place)`, "text-green-400 border-green-500/30");
                        
                        // Sound arpeggio celebration & fireworks
                        playFinishedTriumphSound();
                        createFireworks(canvas.width / 2, canvas.height / 2);
                        
                        console.log(`Course completed in ${totalTime.toFixed(1)}s with ${state.totalMultiplayerStrokes} strokes.`);
                        checkAndResolveMPFinish();
                    }
                    return; // Avoid executing state.gameState.balls = [ball] which overwrites the new ball with the sunk one!
                } else {
                    showGoalScreen(); 
                    createFireworks(canvas.width/2, canvas.height/2); 
                    log(`Ball ${ball.id} Reached Goal & Sunk!`);
                }
                
                state.gameState.balls = [ball]; 
                state.gameState.multiBallActiveOnScreen = false; 
            }
            anyBallMoving = true; 
        } else if (ball.moving) {
            const prevX_trail = ball.x, prevY_trail = ball.y; 
            ball.trail.push({x:ball.x, y:ball.y, alpha:1.0}); 
            for(let t=0; t<ball.trail.length; t++){
                ball.trail[t].alpha -= (2.0 / TRAIL_MAX_LENGTH) * GAME_SPEED_MULTIPLIER; 
                if(ball.trail[t].alpha < 0) ball.trail[t].alpha = 0;
            }
            if (ball.trail.length > TRAIL_MAX_LENGTH * 3) {
                ball.trail = ball.trail.filter(p => p.alpha > 0.001 || ball.trail.indexOf(p) >= ball.trail.length - TRAIL_MAX_LENGTH * 3);
            }

            let {ax, ay} = calculateGravityAcceleration(ball.x, ball.y, state.gameState.planets);
            if (ball.linearLaserActive) {
                ax = 0;
                ay = 0;
            }
            ball.vx += ax * effectiveDeltaTime; 
            ball.vy += ay * effectiveDeltaTime;

            // Check for grazing achievements (Grazing close to planet surface at extreme speed and departing safely)
            if (ball.id === 0 && !state.gameState.goalReached) {
                const speed = Math.hypot(ball.vx, ball.vy);
                state.gameState.planets.forEach((p, idx) => {
                    if (idx === ball.launchPlanetIdx) return; // Skip home launch planet!
                    if (idx === ball.nonGrazablePlanetIdx) return; // Skip planet we started close to at takeoff!
                    if (ball.bouncedPlanetIndices && ball.bouncedPlanetIndices.includes(idx)) return; // Skip any planet we bounced off during this shot!
                    
                    const distToPlanet = Math.hypot(ball.x - p.x, ball.y - p.y);
                    const grazeDistance = p.r + ball.r + 4.5;
                    const minNoCollisionDistance = p.r + ball.r + 0.1;
                    
                    if (distToPlanet <= grazeDistance) {
                        // Flag as a potential graze if flying in close range at extreme speed
                        if (speed > 11.0 && distToPlanet >= minNoCollisionDistance) {
                            if (ball.potentialGrazePlanetIdx === -1) {
                                ball.potentialGrazePlanetIdx = idx;
                            }
                        }
                    } else {
                        // Trigger graze achievement ONLY on safe departure without colliding!
                        if (ball.potentialGrazePlanetIdx === idx) {
                            triggerAchievement('graze');
                            ball.potentialGrazePlanetIdx = -1; // Reset
                        }
                    }
                });
            }

            if (ball.homingActiveThisShot && state.gameState.planets.length > 0 && state.gameState.goal.planetIndex < state.gameState.planets.length) {
                const goalPlanet = state.gameState.planets[state.gameState.goal.planetIndex];
                const goalHoleX = goalPlanet.x + goalPlanet.r * Math.cos(state.gameState.goal.angleOnPlanet);
                const goalHoleY = goalPlanet.y + goalPlanet.r * Math.sin(state.gameState.goal.angleOnPlanet);
                const dxToGoal = goalHoleX - ball.x;
                const dyToGoal = goalHoleY - ball.y;
                const distToGoal = Math.hypot(dxToGoal, dyToGoal);

                if (distToGoal > 0.1) { 
                    const normalizedDx = dxToGoal / distToGoal;
                    const normalizedDy = dyToGoal / distToGoal;
                    
                    const minEffectiveDist = state.gameState.goal.holeRadius * state.gameState.goal.holeRadius; // rough range
                    let distanceFactor = 1 - clamp((distToGoal - minEffectiveDist) / (500 - minEffectiveDist), 0, 1);
                    let currentHomingForce = 80 + distanceFactor * 300;

                    let actualHomingForceX = normalizedDx * currentHomingForce;
                    let actualHomingForceY = normalizedDy * currentHomingForce;

                    for (const p of state.gameState.planets) {
                        const distToPlanetCenterSq = distSq(ball.x, ball.y, p.x, p.y);
                        if (distToPlanetCenterSq < ((p.r + ball.r * 1.2) * (p.r + ball.r * 1.2))) {
                            const distToPlanetCenter = Math.sqrt(distToPlanetCenterSq);
                            if (distToPlanetCenter > 0.01) {
                                const planetNormalX = (ball.x - p.x) / distToPlanetCenter;
                                const planetNormalY = (ball.y - p.y) / distToPlanetCenter;
                                const projection = actualHomingForceX * planetNormalX + actualHomingForceY * planetNormalY;
                                if (projection < 0) { 
                                    actualHomingForceX -= projection * planetNormalX * 0.7;
                                    actualHomingForceY -= projection * planetNormalY * 0.7;
                                }
                            }
                        }
                    }
                    ball.vx += actualHomingForceX * effectiveDeltaTime;
                    ball.vy += actualHomingForceY * effectiveDeltaTime;
                }
            }
            
            ball.x += ball.vx * effectiveDeltaTime; 
            ball.y += ball.vy * effectiveDeltaTime;
            
            // --- Coin Collection Check (Arcade Mode Only) ---
            if (state.currentPlayingMode === 'powerup' && state.gameState.coins && state.gameState.coins.length > 0) {
                state.gameState.coins.forEach(coin => {
                    if (!coin.collected) {
                        const distToCoin = Math.hypot(ball.x - coin.x, ball.y - coin.y);
                        if (distToCoin < ball.r + coin.r) {
                            coin.collected = true;
                            state.totalSpaceBucks += coin.value;
                            playCoinSound(coin.value === 10);
                            spawnCoinCollectParticles(coin.x, coin.y, coin.value === 10);
                            log(`Collected coin! Value: ${coin.value}. Total Space Bucks: ${state.totalSpaceBucks}`);
                        }
                    }
                });
            }
            


            // Visual rotation based on velocity vector and speed (Only when touching surface in vacuum!)
            if (isBallTouchingPlanetSurface(ball)) {
                const speedForRotation = Math.hypot(ball.vx, ball.vy);
                if (speedForRotation > 0.05) {
                    if (ball.rotation === undefined) ball.rotation = 0;
                    ball.rotation += (ball.vx >= 0 ? 1 : -1) * speedForRotation * 0.03 * effectiveDeltaTime;
                    // Lock angular velocity to roll speed while touching surface
                    ball.angularVelocity = (ball.vx >= 0 ? 1 : -1) * speedForRotation * 0.03;
                }
            } else {
                // Flying in zero-friction vacuum: spin at constant angular velocity!
                if (ball.angularVelocity === undefined) ball.angularVelocity = 0;
                if (ball.rotation === undefined) ball.rotation = 0;
                ball.rotation += ball.angularVelocity * effectiveDeltaTime;
            }
            
            // --- Ball-Ball Elastic Collision (Multiplayer Mode) ---
            if (state.currentPlayingMode === 'multiplayer' && !ball.isSinking) {
                for (const opponent of mpState.players) {
                    if (opponent.id === mpState.playerId) continue; // Skip self
                    
                    if (opponent.holeIndex === state.currentHoleIndex && opponent.ballState && !opponent.ballState.isSinking) {
                        const oppBall = opponent.ballState;
                        const isSpawnProtected = (ball.spawnProtectionTimer && ball.spawnProtectionTimer > 0) || (oppBall.spawnProtectionTimer && oppBall.spawnProtectionTimer > 0);
                        if (!isSpawnProtected) {
                            const distBetweenBalls = Math.hypot(ball.x - oppBall.x, ball.y - oppBall.y);
                            const oppRadius = 8;
                            
                            if (distBetweenBalls < ball.r + oppRadius) {
                                const overlap = (ball.r + oppRadius) - distBetweenBalls;
                                const nx = (ball.x - oppBall.x) / distBetweenBalls;
                                const ny = (ball.y - oppBall.y) / distBetweenBalls;
                                
                                // Reposition to resolve overlap (each shifts 50% along normal)
                                ball.x += nx * overlap * 0.5;
                                ball.y += ny * overlap * 0.5;
                                oppBall.x -= nx * overlap * 0.5;
                                oppBall.y -= ny * overlap * 0.5;
                                
                                // Calculate relative velocity
                                const rvx = ball.vx - oppBall.vx;
                                const rvy = ball.vy - oppBall.vy;
                                const velAlongNormal = rvx * nx + rvy * ny;
                                
                                if (velAlongNormal < 0) {
                                    const restitution = 0.8;
                                    const impulse = -(1 + restitution) * velAlongNormal * 0.5;
                                    
                                    ball.vx += impulse * nx;
                                    ball.vy += impulse * ny;
                                    oppBall.vx -= impulse * nx;
                                    oppBall.vy -= impulse * ny;
                                    
                                    ball.moving = true; // Ensure the local ball is set to moving so it simulates rolling!
                                    oppBall.moving = true; // Ensure the opponent ball is set to moving so it simulates rolling and aims fresh!
                                    oppBall.recentlyBumpedTimer = 60; // Ignore static peer sync ticks for 60 frames (1s) to allow local rolling
                                    
                                    const relativeSpeed = Math.hypot(rvx, rvy);
                                    const volume = Math.min(1.0, relativeSpeed / 180 + 0.15); 
                                    const pitch = relativeSpeed > 120 ? "G4" : relativeSpeed > 60 ? "D4" : "G3";
                                    playSound(thudBounceSynth, pitch, "32n", Tone.now(), volume);
                                    
                                    const contactX = (ball.x + oppBall.x) / 2;
                                    const contactY = (ball.y + oppBall.y) / 2;
                                    if (state.collisionParticles) {
                                        const particleCount = 15;
                                        const colors = ['#00FFFF', '#FF00FF', '#FFFFFF', '#FFFF00'];
                                        for (let i = 0; i < particleCount; i++) {
                                            const pAngle = Math.random() * Math.PI * 2;
                                            const pSpeed = Math.random() * 120 + 40;
                                            state.collisionParticles.push({
                                                x: contactX,
                                                y: contactY,
                                                vx: Math.cos(pAngle) * pSpeed + (ball.vx + oppBall.vx) * 0.2,
                                                vy: Math.sin(pAngle) * pSpeed + (ball.vy + oppBall.vy) * 0.2,
                                                color: colors[Math.floor(Math.random() * colors.length)],
                                                size: Math.random() * 3 + 2,
                                                life: Math.random() * 0.4 + 0.2,
                                                maxLife: 0.5
                                            });
                                        }
                                    }
                                    
                                    // Broadcast final coordinates to keep opponent completely in sync
                                    sendPacket({
                                        type: 'collision',
                                        role: mpState.role,
                                        holeIndex: state.currentHoleIndex,
                                        targetPlayerId: opponent.id,
                                        localX: oppBall.x,
                                        localY: oppBall.y,
                                        localVx: oppBall.vx,
                                        localVy: oppBall.vy
                                    });
                                }
                            }
                        }
                    }
                }
            }
            
            const distMoved = Math.hypot(ball.x - prevX_trail, ball.y - prevY_trail);
            ball.currentShotDistanceTraveled += distMoved; 
            if (primaryBall && ball.id === primaryBall.id) { 
                state.gameState.currentHoleDistanceTraveled += distMoved;
            }
            
            for (let planetIdx = 0; planetIdx < state.gameState.planets.length; planetIdx++) {
                const planet = state.gameState.planets[planetIdx];
                if (!ball.moving) break;

                let ballHandledForThisPlanet = false; 

                // 1. Lateral bunker checks (run first to handle sand traps cleanly!)
                if (!ballHandledForThisPlanet && planet.bunkers && planet.bunkers.length > 0) {
                    for (const bunker of planet.bunkers) {
                        const bunkerWorldSurfaceX = planet.x + planet.r * Math.cos(bunker.angle);
                        const bunkerWorldSurfaceY = planet.y + planet.r * Math.sin(bunker.angle);
                        const effectiveBunkerRadiusForLateralCheck = bunker.radius + ball.r * BUNKER_LATERAL_CHECK_GRACE_FRAC;
                        const ballRadialDist = Math.hypot(ball.x - planet.x, ball.y - planet.y);

                        if (ballRadialDist < planet.r && distSq(ball.x, ball.y, bunkerWorldSurfaceX, bunkerWorldSurfaceY) < (effectiveBunkerRadiusForLateralCheck * effectiveBunkerRadiusForLateralCheck)) {
                            ballHandledForThisPlanet = true;
                            
                             const d = Math.hypot(ball.x - bunkerWorldSurfaceX, ball.y - bunkerWorldSurfaceY);
                             
                             // If the ball hits the side wall or sand floor (even near the lip), it always sticks if moving towards the sand.
                             if (d >= bunker.radius - ball.r) {
                                 const dx = ball.x - bunkerWorldSurfaceX;
                                 const dy = ball.y - bunkerWorldSurfaceY;
                                 const nx = d > 0 ? dx / d : 0;
                                 const ny = d > 0 ? dy / d : 0;
                                 const velocityAlongNormal = ball.vx * nx + ball.vy * ny;

                                 if (ballRadialDist < planet.r && velocityAlongNormal > 0) {
                                     // Land and stop in the sand trap floor/walls
                                     if (d > 0) {
                                         ball.x = bunkerWorldSurfaceX + nx * (bunker.radius - ball.r);
                                         ball.y = bunkerWorldSurfaceY + ny * (bunker.radius - ball.r);
                                     }
                                     
                                     playSound(sandTrapSound, null, "0.15s", Tone.now());
                                     ball.isInBunker = true;
                                     ball.isEscapingBunkerPlanetIdx = -1;
                                     ball.escapingBunkerGraceTime = 0;
                                     handleSingleBallStopped(ball);
                                     log(`Ball ${ball.id} failed escape / hit sand: hit inner sand wall on planet ${planetIdx}.`);
                                 }
                             }
                             break;
                        }
                    }
                } 

                // 2. Solid planet rock penetration check:
                // If the ball is inside the planet (distToPlanetCenter < planet.r),
                // but is NOT within the active bunker crater (dBunker < bunker.radius + ball.r * 1.5),
                // then it is inside solid planet rock. We must force standard collision!
                let isInsideSolidRock = false;
                const distToPlanetCenter = Math.hypot(ball.x - planet.x, ball.y - planet.y);
                const isBypassedLaunchPlanet = (ball.escapingPlanetGraceTime > 0 && planetIdx === ball.isEscapingPlanetIdx);

                if (!ballHandledForThisPlanet && distToPlanetCenter < planet.r - 0.5 && !isBypassedLaunchPlanet) {
                    let insideAnyCrater = false;
                    if (planet.bunkers && planet.bunkers.length > 0) {
                        for (const bunker of planet.bunkers) {
                            const bunkerWorldSurfaceX = planet.x + planet.r * Math.cos(bunker.angle);
                            const bunkerWorldSurfaceY = planet.y + planet.r * Math.sin(bunker.angle);
                            const dBunker = Math.hypot(ball.x - bunkerWorldSurfaceX, ball.y - bunkerWorldSurfaceY);
                            // Allow a buffer to let the sand sticking check resolve before solid rock triggers
                            if (dBunker < bunker.radius + ball.r * 1.5) {
                                insideAnyCrater = true;
                                break;
                            }
                        }
                    }
                    if (!insideAnyCrater) {
                        isInsideSolidRock = true;
                    }
                }

                if (isInsideSolidRock) {
                    ball.escapingBunkerGraceTime = 0;
                    ball.isEscapingBunkerPlanetIdx = -1;
                    resolveCollision(ball, planet, planet.r);
                    ballHandledForThisPlanet = true;
                    log(`Ball ${ball.id} solid rock penetration blocked on planet ${planetIdx}.`);
                }

                if (!ball.moving && ballHandledForThisPlanet) { 
                    break; 
                }
                
                if (!ballHandledForThisPlanet && ball.moving) { 
                    // --- Flagpole physics collision ---
                    if (planetIdx === state.gameState.goal.planetIndex && !ball.isSinking) {
                        const goal = state.gameState.goal;
                        const holeAngle = goal.angleOnPlanet;
                        const currentPoleAngle = holeAngle + (goal.wiggleAngle || 0);
                        
                        const poleInnerBaseRadius = planet.r - goal.holeRadius * 0.8;
                        const ax = planet.x + poleInnerBaseRadius * Math.cos(holeAngle);
                        const ay = planet.y + poleInnerBaseRadius * Math.sin(holeAngle);
                        const bx = ax + (goal.flagHeight + goal.holeRadius * 0.8) * Math.cos(currentPoleAngle);
                        const by = ay + (goal.flagHeight + goal.holeRadius * 0.8) * Math.sin(currentPoleAngle);
                        
                        const closest = getClosestPointOnSegment(ball.x, ball.y, ax, ay, bx, by);
                        const dx = ball.x - closest.x;
                        const dy = ball.y - closest.y;
                        const distance = Math.hypot(dx, dy);
                        const poleRadius = 1.5;
                        
                        if (distance < ball.r + poleRadius) {
                            // Reposition to resolve overlap
                            const overlap = (ball.r + poleRadius) - distance;
                            if (distance > 0.001) {
                                ball.x += (dx / distance) * overlap;
                                ball.y += (dy / distance) * overlap;
                            } else {
                                const perpAngle = holeAngle + Math.PI / 2;
                                ball.x += Math.cos(perpAngle) * (ball.r + poleRadius);
                                ball.y += Math.sin(perpAngle) * (ball.r + poleRadius);
                            }
                            
                            // Calculate pre-collision speed for volume scaling
                            const preCollisionSpeed = Math.hypot(ball.vx, ball.vy);
                            
                            // Reflect normal velocity with metallic dampening
                            const nx = distance > 0.001 ? dx / distance : Math.cos(holeAngle + Math.PI / 2);
                            const ny = distance > 0.001 ? dy / distance : Math.sin(holeAngle + Math.PI / 2);
                            
                            const val = ball.vx * nx + ball.vy * ny;
                            if (val < 0) {
                                const restitution = 0.25; 
                                ball.vx -= (1 + restitution) * val * nx;
                                ball.vy -= (1 + restitution) * val * ny;
                            }
                            
                            // Metallic flagpole energy absorption
                            ball.vx *= 0.55;
                            ball.vy *= 0.55;
                            
                            // Impart wiggle velocity to the flag based on impact speed and lever arm (closest.t)
                            const tx = -Math.sin(currentPoleAngle);
                            const ty = Math.cos(currentPoleAngle);
                            const vPerp = ball.vx * tx + ball.vy * ty;
                            
                            const wiggleImpulse = -vPerp * 0.08 * closest.t;
                            goal.wiggleVel = (goal.wiggleVel || 0) + wiggleImpulse;
                            
                            // Scale flagpole sound volume with impact speed
                            const scaledVolume = Math.min(0.9, Math.max(0.08, preCollisionSpeed / 350));
                            playSound(synth, "E5", "32n", Tone.now(), scaledVolume);
                            log(`Ball ${ball.id} flagpole collision. Wiggle Angle: ${goal.wiggleAngle}, impulse: ${wiggleImpulse}`);
                        }
                    }

                    if (planetIdx === state.gameState.goal.planetIndex && checkGoal(ball, planet, state.gameState.goal)) {
                        ball.isSinking = true;
                        ball.sinkProgress = 0;
                        ball.initialSinkRadius = ball.r;
                        ball.moving = false; 
                        ball.isInBunker = false;
                        ball.homingActiveThisShot = false;
                        ball.isStickyShot = false;
                        state.gameState.lastShotDistanceTraveled = ball.currentShotDistanceTraveled;
                        playSound(goalSound, null, null, Tone.now());
                        log(`Ball ${ball.id} entering HOLE on planet ${planetIdx}.`);
                        ballHandledForThisPlanet = true; 
                    }
                }
                
                if (ball.isSinking) { 
                    break; 
                }

                if (!ballHandledForThisPlanet && ball.moving) {
                    // Check if ball is over any bunker mouth of this planet
                    let isOverBunkerMouth = false;
                    if (planet.bunkers && planet.bunkers.length > 0) {
                        for (const bunker of planet.bunkers) {
                            const bunkerWorldSurfaceX = planet.x + planet.r * Math.cos(bunker.angle);
                            const bunkerWorldSurfaceY = planet.y + planet.r * Math.sin(bunker.angle);
                            const dBunker = Math.hypot(ball.x - bunkerWorldSurfaceX, ball.y - bunkerWorldSurfaceY);
                            if (dBunker < bunker.radius) {
                                isOverBunkerMouth = true;
                                break;
                            }
                        }
                    }

                    if (!isOverBunkerMouth && !isBypassedLaunchPlanet) {
                        if (Math.hypot(ball.x - planet.x, ball.y - planet.y) < ball.r + planet.r) {
                            resolveCollision(ball, planet, planet.r); 
                            log(`Ball ${ball.id} STANDARD collision with planet ${planetIdx}. Still moving: ${ball.moving}`);
                        }
                    }
                }
                if (!ball.moving) break; 
            } 
            
            if (ball.moving){ 
                if (ball.isOOBResetting) {
                    ball.oobProgress -= 0.05 * GAME_SPEED_MULTIPLIER; // decrease opacity based on game speed
                    if (ball.oobProgress <= 0) {
                        // Actually perform OOB reset now that it faded out
                        ball.x = ball.currentShotStartX; ball.y = ball.currentShotStartY; 
                        ball.vx = 0; ball.vy = 0; 
                        ball.currentShotDistanceTraveled = 0; 
                        
                        if (primaryBall && ball.id === primaryBall.id) { 
                            state.isButtonZoomActive = false; state.isInteractingZoom = false; state.lastScrollEventTime = 0; 
                            state.zoomInteractionEndTime = performance.now(); 
                            log("Zoom reset due to primary ball out of bounds.");
                            const oobAlert = document.getElementById('largeOOBAlert');
                            if (oobAlert) { oobAlert.style.opacity = '0'; }
                        }
                        state.teeState.flying = false; 
                        if (state.shotCount === 1) { 
                             resetBallToStartLocation(); 
                             if (state.currentHole === 0 && (!primaryBall || ball.id === primaryBall.id)) {
                                 triggerAchievement('litterbug');
                             }
                        }
                        ball.isInBunker = false; ball.isEscapingBunkerPlanetIdx = -1; ball.escapingBunkerGraceTime = 0;
                        ball.isOOBResetting = false;
                        ball.oobProgress = 1.0;
                        handleSingleBallStopped(ball); 
                    }
                } else {
                    // Check if Aegis Deflector is active and handles edge deflection
                    if (state.activePowerUps.oobShield && primaryBall && ball.id === primaryBall.id) {
                        const W = PLAY_AREA_WIDTH;
                        const H = PLAY_AREA_HEIGHT;
                        const R = 40;
                        const r = ball.r;
                        let collided = false;
                        let bounceX = false;
                        let bounceY = false;
                        let nx = 0, ny = 0;
                        let px = ball.x, py = ball.y;

                        // Check corners
                        if (ball.x < R && ball.y < R) {
                            const dx = ball.x - R;
                            const dy = ball.y - R;
                            const d = Math.hypot(dx, dy);
                            if (d >= R - r) {
                                collided = true;
                                nx = -dx / d;
                                ny = -dy / d;
                                px = R + R * (dx / d);
                                py = R + R * (dy / d);
                                ball.x = R + (R - r - 2) * (dx / d);
                                ball.y = R + (R - r - 2) * (dy / d);
                            }
                        } else if (ball.x > W - R && ball.y < R) {
                            const dx = ball.x - (W - R);
                            const dy = ball.y - R;
                            const d = Math.hypot(dx, dy);
                            if (d >= R - r) {
                                collided = true;
                                nx = -dx / d;
                                ny = -dy / d;
                                px = (W - R) + R * (dx / d);
                                py = R + R * (dy / d);
                                ball.x = (W - R) + (R - r - 2) * (dx / d);
                                ball.y = R + (R - r - 2) * (dy / d);
                            }
                        } else if (ball.x < R && ball.y > H - R) {
                            const dx = ball.x - R;
                            const dy = ball.y - (H - R);
                            const d = Math.hypot(dx, dy);
                            if (d >= R - r) {
                                collided = true;
                                nx = -dx / d;
                                ny = -dy / d;
                                px = R + R * (dx / d);
                                py = (H - R) + R * (dy / d);
                                ball.x = R + (R - r - 2) * (dx / d);
                                ball.y = (H - R) + (R - r - 2) * (dy / d);
                            }
                        } else if (ball.x > W - R && ball.y > H - R) {
                            const dx = ball.x - (W - R);
                            const dy = ball.y - (H - R);
                            const d = Math.hypot(dx, dy);
                            if (d >= R - r) {
                                collided = true;
                                nx = -dx / d;
                                ny = -dy / d;
                                px = (W - R) + R * (dx / d);
                                py = (H - R) + R * (dy / d);
                                ball.x = (W - R) + (R - r - 2) * (dx / d);
                                ball.y = (H - R) + (R - r - 2) * (dy / d);
                            }
                        } else {
                            // Straight edges
                            if (ball.x <= r) {
                                collided = true;
                                bounceX = true;
                                ball.x = r + 2;
                            } else if (ball.x >= W - r) {
                                collided = true;
                                bounceX = true;
                                ball.x = W - r - 2;
                            }
                            if (ball.y <= r) {
                                collided = true;
                                bounceY = true;
                                ball.y = r + 2;
                            } else if (ball.y >= H - r) {
                                collided = true;
                                bounceY = true;
                                ball.y = H - r - 2;
                            }
                        }

                        if (collided) {
                            if (bounceX) ball.vx = -ball.vx * 0.85;
                            if (bounceY) ball.vy = -ball.vy * 0.85;
                            if (!bounceX && !bounceY) {
                                const dot = ball.vx * nx + ball.vy * ny;
                                if (dot < 0) {
                                    ball.vx = (ball.vx - 2 * dot * nx) * 0.85;
                                    ball.vy = (ball.vy - 2 * dot * ny) * 0.85;
                                }
                            }
                            
                            // Consume the Aegis Deflector shield
                            state.ownedPowerUps.oobShield = Math.max(0, state.ownedPowerUps.oobShield - 1);
                            state.activePowerUps.oobShield = false;
                            saveGameData();
                            
                            // Register boundary shield impact timestamp to trigger white ripple flash along the border
                            state.boundaryShieldImpactTime = performance.now();
                            
                            // Play epic futuristic shield deflection zap sound
                            try {
                                const now = Tone.now();
                                clickBeepSound.triggerAttackRelease("C6", "16n", now);
                                clickBeepSound.triggerAttackRelease("F#6", "32n", now + 0.05);
                                clickBeepSound.triggerAttackRelease("C7", "32n", now + 0.1);
                            } catch(e) {}
                            
                            // Spawn beautiful defensive forcefield particles at boundary impact point
                            spawnWarpParticles(px, py, '#2DD4BF');
                            
                            updatePowerUpActivationButtons();
                            log("Aegis Deflector deployed! Ball bounced back to safety.");
                        }
                    }

                    // Check standard OOB
                    if (ball.x < -ball.r * 2 || ball.x > PLAY_AREA_WIDTH + ball.r * 2 || ball.y < -ball.r * 2 || ball.y > PLAY_AREA_HEIGHT + ball.r * 2) {
                        log(`Ball ${ball.id} out of bounds.`); 
                        if (state.gameState.multiBallActiveOnScreen && state.gameState.balls.length > 1) {
                            state.gameState.balls.splice(i, 1);
                            log(`Ball ${ball.id} removed from multi-ball shot (OOB). ${state.gameState.balls.length} balls remaining.`);
                        } else { 
                            // Trigger OOB resetting phase
                            ball.isOOBResetting = true;
                            ball.oobProgress = 1.0;
                            ball.vx *= 0.15; ball.vy *= 0.15; // slow ball down instantly
                            
                            // Play cartoony downward slide sound
                            playOutOfBoundsSound();
                            
                            if (primaryBall && ball.id === primaryBall.id) {
                                const oobAlert = document.getElementById('largeOOBAlert');
                                if (oobAlert) { oobAlert.style.opacity = '1'; }
                            }
                        }
                    } else {
                        if (Math.hypot(ball.vx, ball.vy) < MIN_SPEED_TO_STOP_IN_SPACE * GAME_SPEED_MULTIPLIER) {
                        let isOverAnyBunkerOfAnyPlanet = false;
                        for (const p_check of state.gameState.planets) {
                            if (p_check.bunkers && p_check.bunkers.length > 0) {
                                for (const b_check of p_check.bunkers) {
                                    const bwsX = p_check.x + p_check.r * Math.cos(b_check.angle);
                                    const bwsY = p_check.y + p_check.r * Math.sin(b_check.angle);
                                    const effBR_lat_check = b_check.radius + ball.r * BUNKER_LATERAL_CHECK_GRACE_FRAC;
                                    if (distSq(ball.x, ball.y, bwsX, bwsY) < (effBR_lat_check * effBR_lat_check)) {
                                        isOverAnyBunkerOfAnyPlanet = true;
                                        break;
                                    }
                                }
                            }
                            if (isOverAnyBunkerOfAnyPlanet) break;
                        }

                        if (!isOverAnyBunkerOfAnyPlanet) {
                            let isSignificantlyInfluenced = false;
                            for (const pl of state.gameState.planets) {
                                if (Math.hypot(ball.x - pl.x, ball.y - pl.y) < pl.r + ball.r + (pl.r * 0.75)) {
                                    isSignificantlyInfluenced = true; break;
                                }
                            }
                            if (!isSignificantlyInfluenced) {
                                const { ax: gravAx, ay: gravAy } = calculateGravityAcceleration(ball.x, ball.y, state.gameState.planets);
                                if (Math.hypot(gravAx, gravAy) > MIN_GRAVITY_MAGNITUDE_TO_KEEP_MOVING) isSignificantlyInfluenced = true;
                            }
                            if (!isSignificantlyInfluenced) {
                                log(`Ball ${ball.id} stopped: low speed & weak grav (not over bunker).`);
                                handleSingleBallStopped(ball);
                            }
                        }
                    }
                }
            }
        }
    }
        if (ball.moving || ball.isSinking) { 
            anyBallMoving = true;
        }
    } 

    if (!anyBallMoving && previouslyMultiBallActiveOnScreen) {
        log("All multi-balls stopped. Determining closest ball.");
        if (state.gameState.balls.length > 0) {
            let closestBall = state.gameState.balls[0];
            let minDistSqToHole = Infinity;
            const goalPlanet = state.gameState.planets[state.gameState.goal.planetIndex];
            const goalHoleX = goalPlanet.x + goalPlanet.r * Math.cos(state.gameState.goal.angleOnPlanet);
            const goalHoleY = goalPlanet.y + goalPlanet.r * Math.sin(state.gameState.goal.angleOnPlanet);

            state.gameState.balls.forEach(b => {
                if (b.isSinking) { 
                    closestBall = b;
                    minDistSqToHole = 0; 
                    return; 
                }
                const dSq = distSq(b.x, b.y, goalHoleX, goalHoleY);
                if (dSq < minDistSqToHole) {
                    minDistSqToHole = dSq;
                    closestBall = b;
                }
            });

            log(`Closest ball is ${closestBall.id} at distance ${Math.sqrt(minDistSqToHole).toFixed(1)}. Sinking: ${closestBall.isSinking}`);
            state.gameState.balls = [closestBall]; 
            if (closestBall.id !== 0) { 
                 closestBall.id = 0; 
            }
        } 
        
        state.gameState.multiBallActiveOnScreen = false; 
        state.zoomInteractionEndTime = performance.now();

        if (state.currentPlayingMode === 'powerup' && !state.gameState.goalReached && !state.isArcadeRunOver) {
            if (!state.gameState.goalReached && state.shotCount >= state.currentPar) { 
                log(`Arcade Game Over (after multi-ball resolution, over par, no goal): Hole ${state.totalScore + 1}, Shots ${state.shotCount} >= Par ${state.currentPar}.`);
                triggerArcadeGameOver();
            }
        }
    }

    if (state.teeState.flying) {
        state.teeState.fLife -= effectiveDeltaTime;
        if (state.teeState.fLife <= 0) {
            state.teeState.flying = false;
            log("Flying tee expired.");
        } else {
            const { ax, ay } = calculateGravityAcceleration(state.teeState.fx, state.teeState.fy, state.gameState.planets);
            state.teeState.fvx += ax * effectiveDeltaTime; 
            state.teeState.fvy += ay * effectiveDeltaTime; 
            
            state.teeState.fx += state.teeState.fvx * effectiveDeltaTime;
            state.teeState.fy += state.teeState.fvy * effectiveDeltaTime;
            state.teeState.fAngle += state.teeState.fRotationSpeed * effectiveDeltaTime;
        }
    }

    state.fireworks.forEach((fw, fwIndex)=>{
        if(!fw.exploded){
            fw.x+=fw.vx*effectiveDeltaTime;fw.y+=fw.vy*effectiveDeltaTime;fw.vy+=200*effectiveDeltaTime;fw.life-=effectiveDeltaTime; 
            if(fw.vy>=0||fw.life<=0){ 
                fw.exploded=true;
                for(let i=0;i<50+Math.random()*50;i++){ 
                    const angle=Math.random()*Math.PI*2,speed=Math.random()*200+100;
                    fw.particles.push({x:fw.x,y:fw.y,vx:Math.cos(angle)*speed,vy:Math.sin(angle)*speed,alpha:1,life:Math.random()*1+0.5});
                }
            }
        }else{
            fw.particles.forEach((p,pIndex)=>{
                p.x+=p.vx*effectiveDeltaTime;p.y+=p.vy*effectiveDeltaTime;p.vy+=100*effectiveDeltaTime; 
                p.life-=effectiveDeltaTime;p.alpha=Math.max(0,p.life);
                if(p.life<=0)fw.particles.splice(pIndex,1); 
            });
        }
        if(fw.exploded&&fw.particles.length===0)state.fireworks.splice(fwIndex,1); 
    });
}

function getWarpedPoint(x, y, planets) {
    let wx = x;
    let wy = y;
    
    planets.forEach((p) => {
        const dx = p.x - x;
        const dy = p.y - y;
        const dist = Math.hypot(dx, dy);
        
        // Vast global reach for each planet's gravitational field
        const maxDist = p.r * (4.0 + (p.mass / 3000) * 3.5);
        
        if (dist < maxDist) {
            // Highly dramatic pulling strength that scales with planet mass
            const maxPullRatio = Math.min(0.92, 0.4 + (p.mass / 6000) * 0.5);
            const t = dist / maxDist;
            
            // Cosine bell falloff for wide mid-range sweep and perfectly smooth boundaries
            const falloff = 0.5 * (1 + Math.cos(t * Math.PI));
            const pullRatio = maxPullRatio * falloff;
            
            wx += dx * pullRatio;
            wy += dy * pullRatio;
        }
    });
    return { x: wx, y: wy };
}

export function cacheGravityGrid() {
    if (!state.gravityGridCanvas) {
        state.gravityGridCanvas = document.createElement('canvas');
    }
    state.gravityGridCanvas.width = PLAY_AREA_WIDTH;
    state.gravityGridCanvas.height = PLAY_AREA_HEIGHT;
    const gctx = state.gravityGridCanvas.getContext('2d');
    
    gctx.strokeStyle = 'rgba(34, 211, 238, 0.28)'; // cyber cyan grid color
    gctx.lineWidth = 0.85;
    
    const gridSpacing = 40; // spacing between grid lines
    const stepSize = 20; // resolution of warped grid lines
    
    // Horizontal grid lines
    for (let y = 0; y <= PLAY_AREA_HEIGHT; y += gridSpacing) {
        gctx.beginPath();
        let first = true;
        for (let x = 0; x <= PLAY_AREA_WIDTH; x += stepSize) {
            const warped = getWarpedPoint(x, y, state.gameState.planets);
            if (first) {
                gctx.moveTo(warped.x, warped.y);
                first = false;
            } else {
                gctx.lineTo(warped.x, warped.y);
            }
        }
        const warpedEnd = getWarpedPoint(PLAY_AREA_WIDTH, y, state.gameState.planets);
        gctx.lineTo(warpedEnd.x, warpedEnd.y);
        gctx.stroke();
    }
    
    // Vertical grid lines
    for (let x = 0; x <= PLAY_AREA_WIDTH; x += gridSpacing) {
        gctx.beginPath();
        let first = true;
        for (let y = 0; y <= PLAY_AREA_HEIGHT; y += stepSize) {
            const warped = getWarpedPoint(x, y, state.gameState.planets);
            if (first) {
                gctx.moveTo(warped.x, warped.y);
                first = false;
            } else {
                gctx.lineTo(warped.x, warped.y);
            }
        }
        const warpedEnd = getWarpedPoint(x, PLAY_AREA_HEIGHT, state.gameState.planets);
        gctx.lineTo(warpedEnd.x, warpedEnd.y);
        gctx.stroke();
    }
}

export function cachePlanet(p, index) {
    const cacheScale = 2.0; // High resolution rendering scale for sharp visual scaling
    
    // 1. Create planet body canvas (body + atmosphere + specularity + shadows + bunkers + goal cutout)
    const bodyCanvas = document.createElement('canvas');
    const bodyRadius = p.r * 1.5 + 5;
    const bodySize = bodyRadius * 2;
    bodyCanvas.width = bodySize * cacheScale;
    bodyCanvas.height = bodySize * cacheScale;
    const bctx = bodyCanvas.getContext('2d');
    
    bctx.scale(cacheScale, cacheScale);
    bctx.translate(bodyRadius, bodyRadius);
    
    // Draw atmospheric corona glow
    if (p.glowRGB) {
        const isAtmospheric = p.atmosphere;
        const glowRadius = p.r * (isAtmospheric ? 1.45 : 1.20);
        const peakOpacity = isAtmospheric ? 0.38 : 0.15;
        
        bctx.save();
        const glowGrad = bctx.createRadialGradient(0, 0, p.r * 0.96, 0, 0, glowRadius);
        glowGrad.addColorStop(0, `rgba(${p.glowRGB.r}, ${p.glowRGB.g}, ${p.glowRGB.b}, ${peakOpacity})`);
        glowGrad.addColorStop(0.2, `rgba(${p.glowRGB.r}, ${p.glowRGB.g}, ${p.glowRGB.b}, ${peakOpacity * 0.75})`);
        glowGrad.addColorStop(0.6, `rgba(${p.glowRGB.r}, ${p.glowRGB.g}, ${p.glowRGB.b}, ${peakOpacity * 0.25})`);
        glowGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
        bctx.fillStyle = glowGrad;
        bctx.beginPath();
        bctx.arc(0, 0, glowRadius, 0, Math.PI * 2);
        bctx.fill();
        bctx.restore();
    }

    // Draw sphere base gradient
    const planetGradient = bctx.createRadialGradient(p.r*0.35, -p.r*0.35, p.r*0.05, 0, 0, p.r);
    planetGradient.addColorStop(0, shadeColor(p.mainColor, 25));
    planetGradient.addColorStop(0.6, p.mainColor);
    planetGradient.addColorStop(1, p.secondaryColor);
    bctx.fillStyle = planetGradient;
    bctx.beginPath(); bctx.arc(0,0,p.r,0,2*Math.PI); bctx.fill();

    // Clip context and draw planet-specific visual styles
    bctx.save();
    bctx.beginPath();
    bctx.arc(0, 0, p.r, 0, Math.PI * 2);
    bctx.clip();

    if (p.style === 'cratered') {
        // Draw Maria plains first
        if (p.maria) {
            p.maria.forEach(m => {
                bctx.save();
                const u = (m.dist !== undefined) ? m.dist / p.r : 0;
                const squashFactor = Math.sqrt(Math.max(0.12, 1 - u * u));
                bctx.translate(m.cx, m.cy);
                bctx.scale(squashFactor, 1.0);
                bctx.fillStyle = m.color;
                bctx.globalAlpha = m.opacity;
                bctx.beginPath();
                bctx.arc(0, 0, m.cr, 0, Math.PI * 2);
                bctx.fill();
                bctx.restore();
            });
        }

        // Draw tectonic fissures
        if (p.fissures) {
            p.fissures.forEach(f => {
                bctx.save();
                bctx.globalAlpha = f.opacity;
                
                // Highlight rim
                bctx.strokeStyle = 'rgba(255, 255, 255, 0.16)';
                bctx.lineWidth = (f.width * 1.8) / cacheScale;
                bctx.lineCap = 'round';
                bctx.lineJoin = 'round';
                bctx.beginPath();
                f.points.forEach((pt, ptIdx) => {
                    const ptDist = Math.hypot(pt.x, pt.y);
                    const u = ptDist / p.r;
                    const squash = Math.sqrt(Math.max(0.12, 1 - u * u));
                    const angle = Math.atan2(pt.y, pt.x);
                    const hx = pt.x + 0.5 / cacheScale;
                    const hy = pt.y + 0.5 / cacheScale;
                    const sx = hx * (1 - (1 - squash) * Math.cos(angle) * Math.cos(angle));
                    const sy = hy * (1 - (1 - squash) * Math.sin(angle) * Math.sin(angle));
                    
                    if (ptIdx === 0) bctx.moveTo(sx, sy);
                    else bctx.lineTo(sx, sy);
                });
                bctx.stroke();
                
                // Dark core crack
                bctx.strokeStyle = shadeColor(p.mainColor, -30);
                bctx.lineWidth = f.width / cacheScale;
                bctx.beginPath();
                f.points.forEach((pt, ptIdx) => {
                    const ptDist = Math.hypot(pt.x, pt.y);
                    const u = ptDist / p.r;
                    const squash = Math.sqrt(Math.max(0.12, 1 - u * u));
                    const angle = Math.atan2(pt.y, pt.x);
                    const sx = pt.x * (1 - (1 - squash) * Math.cos(angle) * Math.cos(angle));
                    const sy = pt.y * (1 - (1 - squash) * Math.sin(angle) * Math.sin(angle));
                    
                    if (ptIdx === 0) bctx.moveTo(sx, sy);
                    else bctx.lineTo(sx, sy);
                });
                bctx.stroke();
                
                bctx.restore();
            });
        }

        // Draw major craters
        if (p.craters) {
            p.craters.forEach(crater => {
                bctx.save();
                const u = (crater.dist !== undefined) ? crater.dist / p.r : 0;
                const squashFactor = Math.sqrt(Math.max(0.12, 1 - u * u));
                
                bctx.translate(crater.cx, crater.cy);
                if (crater.angle !== undefined) {
                    bctx.rotate(crater.angle);
                }
                bctx.scale(squashFactor, 1.0);
                
                // Ejecta rays
                bctx.strokeStyle = `rgba(255, 255, 255, ${crater.opacity * 0.18})`;
                bctx.lineWidth = Math.max(0.3, 0.5 / cacheScale);
                const rayCount = 4;
                for (let r = 0; r < rayCount; r++) {
                    const rayAngle = (r / rayCount) * Math.PI * 2;
                    bctx.beginPath();
                    bctx.moveTo(Math.cos(rayAngle) * crater.cr, Math.sin(rayAngle) * crater.cr);
                    bctx.lineTo(Math.cos(rayAngle) * (crater.cr * 2.1), Math.sin(rayAngle) * (crater.cr * 2.1));
                    bctx.stroke();
                }

                // Radial depth
                const bowlGrad = bctx.createRadialGradient(
                    -crater.cr * 0.22, crater.cr * 0.22, crater.cr * 0.05,
                    0, 0, crater.cr
                );
                bowlGrad.addColorStop(0, shadeColor(p.mainColor, -24));
                bowlGrad.addColorStop(0.68, shadeColor(p.mainColor, -8));
                bowlGrad.addColorStop(1, shadeColor(p.mainColor, 6));
                bctx.fillStyle = bowlGrad;
                bctx.beginPath();
                bctx.arc(0, 0, crater.cr, 0, Math.PI * 2);
                bctx.fill();
                
                // Inner concentric ridge rim
                bctx.strokeStyle = shadeColor(p.mainColor, -20);
                bctx.lineWidth = Math.max(0.35, 0.6 / cacheScale);
                bctx.beginPath();
                bctx.arc(0, 0, crater.cr * 0.62, 0, Math.PI * 2);
                bctx.stroke();
                
                // Central uplift peak
                const peakGrad = bctx.createRadialGradient(
                    crater.cr * 0.05, -crater.cr * 0.05, 0,
                    0, 0, crater.cr * 0.15
                );
                peakGrad.addColorStop(0, '#ffffff');
                peakGrad.addColorStop(0.3, shadeColor(p.mainColor, 20));
                peakGrad.addColorStop(1, shadeColor(p.mainColor, -18));
                bctx.fillStyle = peakGrad;
                bctx.beginPath();
                bctx.arc(0, 0, crater.cr * 0.15, 0, Math.PI * 2);
                bctx.fill();
                
                // Soft inner crescent shadow
                bctx.lineWidth = Math.max(0.4, 0.9 / cacheScale);
                bctx.strokeStyle = `rgba(0, 0, 0, ${crater.opacity * 0.7})`;
                bctx.beginPath();
                bctx.arc(0, 0, crater.cr, 1.4 * Math.PI, 2.2 * Math.PI);
                bctx.stroke();
                
                // Soft outer highlight
                bctx.strokeStyle = `rgba(255, 255, 255, ${crater.opacity * 0.55})`;
                bctx.beginPath();
                bctx.arc(0, 0, crater.cr, 0.4 * Math.PI, 1.2 * Math.PI);
                bctx.stroke();
                
                bctx.restore();
            });
        }

        // Draw micro-craters
        if (p.microCraters) {
            p.microCraters.forEach(mc => {
                bctx.save();
                const u = (mc.dist !== undefined) ? mc.dist / p.r : 0;
                const squashFactor = Math.sqrt(Math.max(0.12, 1 - u * u));
                bctx.translate(mc.cx, mc.cy);
                bctx.scale(squashFactor, 1.0);
                
                bctx.fillStyle = `rgba(0, 0, 0, ${mc.opacity * 0.55})`;
                bctx.beginPath();
                bctx.arc(-0.4 / cacheScale, 0.4 / cacheScale, mc.cr, 0, Math.PI * 2);
                bctx.fill();
                
                bctx.fillStyle = `rgba(255, 255, 255, ${mc.opacity * 0.4})`;
                bctx.beginPath();
                bctx.arc(0.4 / cacheScale, -0.4 / cacheScale, mc.cr, 0, Math.PI * 2);
                bctx.fill();
                
                bctx.restore();
            });
        }
    } else if (p.style === 'gas_giant' && p.bands) {
        bctx.save();
        bctx.rotate(p.tiltAngle || 0);

        p.bands.forEach(band => {
            bctx.fillStyle = band.color;
            bctx.fillRect(-p.r * 1.5, band.y, p.r * 3, band.h);
            
            const stripeCount = 3;
            for (let s = 0; s < stripeCount; s++) {
                const stripeY = band.y + (band.h * (s + 0.5)) / stripeCount;
                const stripeH = 0.8 / cacheScale;
                bctx.fillStyle = s % 2 === 0 ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.06)';
                bctx.fillRect(-p.r * 1.5, stripeY, p.r * 3, stripeH);
            }

            bctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
            bctx.lineWidth = Math.max(0.5, 1 / cacheScale);
            bctx.beginPath();
            bctx.moveTo(-p.r * 1.5, band.y);
            bctx.lineTo(p.r * 1.5, band.y);
            bctx.stroke();
        });

        if (p.hasPolarCaps) {
            const polarGradN = bctx.createLinearGradient(0, -p.r, 0, -p.r * 0.7);
            polarGradN.addColorStop(0, 'rgba(0, 0, 0, 0.32)');
            polarGradN.addColorStop(1, 'rgba(0, 0, 0, 0)');
            bctx.fillStyle = polarGradN;
            bctx.beginPath();
            bctx.ellipse(0, -p.r, p.r, p.r * 0.35, 0, 0, Math.PI * 2);
            bctx.fill();

            const polarGradS = bctx.createLinearGradient(0, p.r, 0, p.r * 0.7);
            polarGradS.addColorStop(0, 'rgba(0, 0, 0, 0.32)');
            polarGradS.addColorStop(1, 'rgba(0, 0, 0, 0)');
            bctx.fillStyle = polarGradS;
            bctx.beginPath();
            bctx.ellipse(0, p.r, p.r, p.r * 0.35, 0, 0, Math.PI * 2);
            bctx.fill();
        }

        if (p.storms) {
            p.storms.forEach(storm => {
                bctx.save();
                const u = (storm.dist !== undefined) ? storm.dist / p.r : 0;
                const squashFactor = Math.sqrt(Math.max(0.12, 1 - u * u));
                
                bctx.translate(storm.cx, storm.cy);
                bctx.rotate(storm.angle);
                bctx.scale(squashFactor, 1.0);
                
                if (storm.eddies) {
                    storm.eddies.forEach(ed => {
                        bctx.save();
                        bctx.translate(ed.offsetX, ed.offsetY);
                        bctx.fillStyle = storm.color;
                        bctx.globalAlpha = ed.opacity;
                        bctx.beginPath();
                        bctx.ellipse(0, 0, ed.crX, ed.crY, 0, 0, Math.PI * 2);
                        bctx.fill();
                        bctx.restore();
                    });
                }

                const swirlCount = 3;
                for (let sw = 0; sw < swirlCount; sw++) {
                    const factor = 1 - sw * 0.25;
                    bctx.fillStyle = sw % 2 === 0 ? storm.color : shadeColor(storm.color, sw * 8);
                    bctx.globalAlpha = storm.opacity * (0.85 + sw * 0.05);
                    bctx.beginPath();
                    bctx.ellipse(0, 0, storm.crX * factor, storm.crY * factor, 0, 0, Math.PI * 2);
                    bctx.fill();
                }
                
                bctx.fillStyle = '#ffffff';
                bctx.globalAlpha = 0.22;
                bctx.beginPath();
                bctx.ellipse(-storm.crX * 0.15, -storm.crY * 0.15, storm.crX * 0.3, storm.crY * 0.3, 0, 0, Math.PI * 2);
                bctx.fill();
                
                bctx.restore();
            });
        }
        bctx.restore();
    } else if (p.style === 'ringed') {
        if (p.bands) {
            p.bands.forEach(band => {
                bctx.fillStyle = band.color;
                bctx.fillRect(-p.r, band.y, p.r * 2, band.h);
            });
        }

        if (p.shimmerPatches) {
            p.shimmerPatches.forEach(pat => {
                bctx.save();
                const u = (pat.dist !== undefined) ? pat.dist / p.r : 0;
                const squashFactor = Math.sqrt(Math.max(0.12, 1 - u * u));
                bctx.translate(pat.cx, pat.cy);
                bctx.scale(squashFactor, 1.0);
                
                const patchGrad = bctx.createRadialGradient(0, 0, 0, 0, 0, pat.cr);
                const glowCol = p.glowRGB ? `${p.glowRGB.r}, ${p.glowRGB.g}, ${p.glowRGB.b}` : '255, 255, 255';
                patchGrad.addColorStop(0, `rgba(${glowCol}, ${pat.opacity * 1.5})`);
                patchGrad.addColorStop(1, `rgba(${glowCol}, 0)`);
                bctx.fillStyle = patchGrad;
                bctx.beginPath();
                bctx.arc(0, 0, pat.cr, 0, Math.PI * 2);
                bctx.fill();
                
                bctx.restore();
            });
        }

        if (p.fractures) {
            p.fractures.forEach(frac => {
                bctx.save();
                bctx.globalAlpha = frac.opacity;
                
                bctx.strokeStyle = 'rgba(56, 189, 248, 0.4)';
                bctx.lineWidth = 1.6 / cacheScale;
                bctx.lineCap = 'round';
                bctx.lineJoin = 'round';
                bctx.beginPath();
                frac.points.forEach((pt, ptIdx) => {
                    const ptDist = Math.hypot(pt.x, pt.y);
                    const u = ptDist / p.r;
                    const squash = Math.sqrt(Math.max(0.12, 1 - u * u));
                    const angle = Math.atan2(pt.y, pt.x);
                    const hx = pt.x + 0.4 / cacheScale;
                    const hy = pt.y + 0.4 / cacheScale;
                    const sx = hx * (1 - (1 - squash) * Math.cos(angle) * Math.cos(angle));
                    const sy = hy * (1 - (1 - squash) * Math.sin(angle) * Math.sin(angle));
                    
                    if (ptIdx === 0) bctx.moveTo(sx, sy);
                    else bctx.lineTo(sx, sy);
                });
                bctx.stroke();
                
                bctx.strokeStyle = shadeColor(p.mainColor, -25);
                bctx.lineWidth = 0.8 / cacheScale;
                bctx.beginPath();
                frac.points.forEach((pt, ptIdx) => {
                    const ptDist = Math.hypot(pt.x, pt.y);
                    const u = ptDist / p.r;
                    const squash = Math.sqrt(Math.max(0.12, 1 - u * u));
                    const angle = Math.atan2(pt.y, pt.x);
                    const sx = pt.x * (1 - (1 - squash) * Math.cos(angle) * Math.cos(angle));
                    const sy = pt.y * (1 - (1 - squash) * Math.sin(angle) * Math.sin(angle));
                    
                    if (ptIdx === 0) bctx.moveTo(sx, sy);
                    else bctx.lineTo(sx, sy);
                });
                bctx.stroke();
                
                bctx.restore();
            });
        }
    } else if (p.style === 'earthlike' && p.continents) {
        p.continents.forEach(continent => {
            bctx.save();
            const u = (continent.dist !== undefined) ? continent.dist / p.r : 0;
            const squashFactor = Math.sqrt(Math.max(0.12, 1 - u * u));
            bctx.translate(continent.cx, continent.cy);
            bctx.scale(squashFactor, 1.0);
            bctx.fillStyle = 'rgba(56, 189, 248, 0.38)';
            bctx.beginPath();
            bctx.arc(0, 0, continent.cr * 1.18, 0, Math.PI * 2);
            bctx.fill();
            bctx.restore();
        });

        p.continents.forEach(continent => {
            bctx.save();
            const u = (continent.dist !== undefined) ? continent.dist / p.r : 0;
            const squashFactor = Math.sqrt(Math.max(0.12, 1 - u * u));
            bctx.translate(continent.cx, continent.cy);
            bctx.scale(squashFactor, 1.0);
            bctx.fillStyle = '#E5C494';
            bctx.beginPath();
            bctx.arc(0, 0, continent.cr * 1.05, 0, Math.PI * 2);
            bctx.fill();
            bctx.restore();
        });

        p.continents.forEach(continent => {
            bctx.save();
            const u = (continent.dist !== undefined) ? continent.dist / p.r : 0;
            const squashFactor = Math.sqrt(Math.max(0.12, 1 - u * u));
            bctx.translate(continent.cx, continent.cy);
            bctx.scale(squashFactor, 1.0);
            bctx.fillStyle = continent.color;
            bctx.beginPath();
            bctx.arc(0, 0, continent.cr, 0, Math.PI * 2);
            bctx.fill();
            bctx.restore();
        });

        p.continents.forEach(continent => {
            bctx.save();
            const u = (continent.dist !== undefined) ? continent.dist / p.r : 0;
            const squashFactor = Math.sqrt(Math.max(0.12, 1 - u * u));
            bctx.translate(continent.cx, continent.cy);
            bctx.scale(squashFactor, 1.0);
            bctx.fillStyle = shadeColor(continent.color, -15);
            bctx.beginPath();
            bctx.arc(-continent.cr * 0.15, -continent.cr * 0.1, continent.cr * 0.6, 0, Math.PI * 2);
            bctx.fill();
            bctx.restore();
        });

        p.continents.forEach(continent => {
            bctx.save();
            const u = (continent.dist !== undefined) ? continent.dist / p.r : 0;
            const squashFactor = Math.sqrt(Math.max(0.12, 1 - u * u));
            bctx.translate(continent.cx, continent.cy);
            bctx.scale(squashFactor, 1.0);
            bctx.fillStyle = '#8B6B4F';
            bctx.beginPath();
            bctx.arc(continent.cr * 0.18, continent.cr * 0.18, continent.cr * 0.35, 0, Math.PI * 2);
            bctx.fill();
            
            bctx.fillStyle = '#FFFFFF';
            bctx.beginPath();
            bctx.arc(continent.cr * 0.22, continent.cr * 0.22, continent.cr * 0.14, 0, Math.PI * 2);
            bctx.fill();
            bctx.restore();
        });

        if (p.rivers) {
            p.rivers.forEach(river => {
                bctx.save();
                bctx.strokeStyle = '#38BDF8';
                bctx.lineWidth = 0.9 / cacheScale;
                bctx.lineCap = 'round';
                bctx.lineJoin = 'round';
                bctx.beginPath();
                river.points.forEach((pt, ptIdx) => {
                    const ptDist = Math.hypot(pt.x, pt.y);
                    const u = ptDist / p.r;
                    const squash = Math.sqrt(Math.max(0.12, 1 - u * u));
                    const angle = Math.atan2(pt.y, pt.x);
                    const sx = pt.x * (1 - (1 - squash) * Math.cos(angle) * Math.cos(angle));
                    const sy = pt.y * (1 - (1 - squash) * Math.sin(angle) * Math.sin(angle));
                    
                    if (ptIdx === 0) bctx.moveTo(sx, sy);
                    else bctx.lineTo(sx, sy);
                });
                bctx.stroke();
                bctx.restore();
            });
        }

        if (p.hasPolarCaps) {
            bctx.save();
            bctx.rotate(p.tiltAngle || 0);
            bctx.fillStyle = '#FFFFFF';
            bctx.beginPath();
            bctx.ellipse(0, -p.r, p.r * 0.5, p.r * 0.15, 0, 0, Math.PI * 2);
            bctx.fill();
            bctx.beginPath();
            bctx.ellipse(0, p.r, p.r * 0.5, p.r * 0.15, 0, 0, Math.PI * 2);
            bctx.fill();
            bctx.restore();
        }

        if (p.cities) {
            p.cities.forEach(city => {
                const lx = p.r * 0.35;
                const ly = -p.r * 0.35;
                const lightFactor = (city.cx * lx + city.cy * ly) / (p.r * p.r);
                
                if (lightFactor < 0.25) {
                    bctx.save();
                    const u = Math.hypot(city.cx, city.cy) / p.r;
                    const squashFactor = Math.sqrt(Math.max(0.12, 1 - u * u));
                    bctx.translate(city.cx, city.cy);
                    bctx.scale(squashFactor, 1.0);
                    
                    const darkFactor = Math.max(0, Math.min(1, (0.25 - lightFactor) / 1.25));
                    bctx.globalAlpha = city.opacity * darkFactor * 0.95;
                    bctx.fillStyle = '#FBBF24';
                    bctx.beginPath();
                    bctx.arc(0, 0, city.size, 0, Math.PI * 2);
                    bctx.fill();
                    bctx.restore();
                }
            });
        }

        if (p.clouds) {
            p.clouds.forEach(cloud => {
                bctx.save();
                const u = (cloud.dist !== undefined) ? cloud.dist / p.r : 0;
                const squashFactor = Math.sqrt(Math.max(0.12, 1 - u * u));
                bctx.translate(cloud.cx, cloud.cy);
                if (cloud.angle !== undefined) {
                    bctx.rotate(cloud.angle);
                }
                bctx.scale(squashFactor, 1.0);
                bctx.rotate(cloud.rot);
                bctx.fillStyle = 'rgba(255, 255, 255, 0.32)';
                bctx.beginPath();
                bctx.ellipse(0, 0, cloud.crX, cloud.crY, 0, 0, Math.PI * 2);
                bctx.fill();
                bctx.restore();
            });
        }
    }

    // Glass specularity
    const specularGrad = bctx.createLinearGradient(p.r, -p.r, -p.r * 0.3, p.r * 0.3);
    specularGrad.addColorStop(0, 'rgba(255, 255, 255, 0.32)');
    specularGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.04)');
    specularGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
    bctx.fillStyle = specularGrad;
    bctx.beginPath();
    bctx.arc(0, 0, p.r, 0, Math.PI * 2);
    bctx.fill();

    // 3D Shadow depth overlay
    const shadowGrad = bctx.createRadialGradient(p.r*0.35, -p.r*0.35, p.r*0.5, p.r*0.35, -p.r*0.35, p.r*1.15);
    shadowGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
    shadowGrad.addColorStop(0.7, 'rgba(0, 0, 0, 0.15)');
    shadowGrad.addColorStop(1, 'rgba(0, 0, 0, 0.55)');
    bctx.fillStyle = shadowGrad;
    bctx.beginPath();
    bctx.arc(0, 0, p.r, 0, Math.PI * 2);
    bctx.fill();

    bctx.restore(); // Restore from planet body sphere clipping

    // Draw bunkers onto bodyCanvas
    if (p.bunkers && p.bunkers.length > 0) {
        p.bunkers.forEach(bunker => {
            bctx.save();
            bctx.rotate(bunker.angle);

            // Cut out bunker shape from the planet sphere
            bctx.save();
            bctx.beginPath();
            bctx.arc(0, 0, p.r, 0, Math.PI * 2);
            bctx.clip();

            bctx.globalCompositeOperation = 'destination-out';
            bctx.beginPath();
            bctx.arc(p.r, 0, bunker.radius, 0, Math.PI * 2);
            bctx.fillStyle = 'rgba(0,0,0,1)';
            bctx.fill();
            bctx.globalCompositeOperation = 'source-over';

            // Fill sand trap color
            bctx.beginPath();
            bctx.arc(p.r, 0, bunker.radius, 0, Math.PI * 2);
            bctx.fillStyle = shadeColor(p.mainColor, -65);
            bctx.fill();

            bctx.restore(); // remove clip

            // Draw sand wall border
            bctx.save();
            bctx.beginPath();
            bctx.arc(0, 0, p.r, 0, Math.PI * 2);
            bctx.clip();

            const scaledBorderWidth = Math.max(0.5, (bunker.wallThickness || BUNKER_DEFAULT_WALL_THICKNESS) / cacheScale);
            bctx.lineWidth = scaledBorderWidth;
            bctx.strokeStyle = BUNKER_COLOR_MAIN;
            bctx.beginPath();
            bctx.arc(p.r, 0, bunker.radius - (scaledBorderWidth / 2), 0, Math.PI * 2);
            bctx.stroke();

            bctx.restore();
            bctx.restore();
        });
    }

    // Cut out goal hole if this is the goal planet
    if (index === state.gameState.goal.planetIndex) {
        const goalData = state.gameState.goal;
        const holeAngle = goalData.angleOnPlanet;
        const holeRadius = goalData.holeRadius;
        const holeSurfaceX = p.r * Math.cos(holeAngle);
        const holeSurfaceY = p.r * Math.sin(holeAngle);

        bctx.save();
        bctx.beginPath();
        bctx.arc(0, 0, p.r + 1, 0, 2 * Math.PI);
        bctx.clip();

        bctx.beginPath();
        bctx.arc(holeSurfaceX, holeSurfaceY, holeRadius + 1, 0, 2 * Math.PI);
        bctx.fillStyle = 'rgba(0,0,0,0.85)';
        bctx.fill();

        bctx.globalCompositeOperation = 'destination-out';
        bctx.beginPath();
        bctx.arc(holeSurfaceX, holeSurfaceY, holeRadius, 0, 2 * Math.PI);
        bctx.fillStyle = 'black';
        bctx.fill();
        bctx.globalCompositeOperation = 'source-over';
        
        bctx.restore();
    }

    p.bodyCanvas = bodyCanvas;

    // 2. Pre-render rings if ringed planet
    if (p.style === 'ringed') {
        const ringColorRGB = p.glowRGB ? `${p.glowRGB.r}, ${p.glowRGB.g}, ${p.glowRGB.b}` : "255, 255, 255";
        const ringsToDraw = p.rings || [
            { rScale: 1.35, width: 0.08, color: `rgba(${ringColorRGB}, 0.25)` },
            { rScale: 1.50, width: 0.12, color: `rgba(${ringColorRGB}, 0.65)` },
            { rScale: 1.70, width: 0.02, color: `rgba(${ringColorRGB}, 0.10)` },
            { rScale: 1.88, width: 0.15, color: `rgba(${ringColorRGB}, 0.55)` },
            { rScale: 2.12, width: 0.05, color: `rgba(${ringColorRGB}, 0.25)` }
        ];

        const ringsWidth = p.r * 5 + 10;
        const ringsSize = ringsWidth * 2;

        // Back Rings Canvas
        const backRingsCanvas = document.createElement('canvas');
        backRingsCanvas.width = ringsSize * cacheScale;
        backRingsCanvas.height = ringsSize * cacheScale;
        const brctx = backRingsCanvas.getContext('2d');
        brctx.scale(cacheScale, cacheScale);
        brctx.translate(ringsWidth, ringsWidth);

        ringsToDraw.forEach(sr => {
            brctx.strokeStyle = sr.color;
            brctx.lineWidth = p.r * sr.width;
            brctx.lineCap = 'round';
            brctx.beginPath();
            brctx.ellipse(0, 0, p.r * sr.rScale, p.r * sr.rScale * 0.2, 0, Math.PI, 2 * Math.PI);
            brctx.stroke();
        });
        p.backRingsCanvas = backRingsCanvas;

        // Front Rings Canvas
        const frontRingsCanvas = document.createElement('canvas');
        frontRingsCanvas.width = ringsSize * cacheScale;
        frontRingsCanvas.height = ringsSize * cacheScale;
        const frctx = frontRingsCanvas.getContext('2d');
        frctx.scale(cacheScale, cacheScale);
        frctx.translate(ringsWidth, ringsWidth);

        ringsToDraw.forEach(sr => {
            frctx.strokeStyle = sr.color;
            frctx.lineWidth = p.r * sr.width;
            frctx.lineCap = 'round';
            frctx.beginPath();
            frctx.ellipse(0, 0, p.r * sr.rScale, p.r * sr.rScale * 0.2, 0, 0, Math.PI);
            frctx.stroke();
        });
        p.frontRingsCanvas = frontRingsCanvas;
    }
}

export function preRenderPlanets() {
    if (state.gameState.planets) {
        state.gameState.planets.forEach((p, index) => {
            cachePlanet(p, index);
        });
    }
}

function render() {
    if(!state.gameLoopRequestId && !document.getElementById('startScreen').classList.contains('active')) return;
    
    updateInGameUIDisplay(); 

    ctx.fillStyle = '#05071A';
    ctx.fillRect(0,0,canvas.width,canvas.height);
    
    const offsetX = canvas.width / 2 - state.viewCurrentCenterX * state.viewCurrentScale;
    const offsetY = canvas.height / 2 - state.viewCurrentCenterY * state.viewCurrentScale;

    ctx.save();
    ctx.translate(offsetX, offsetY);
    ctx.scale(state.viewCurrentScale, state.viewCurrentScale);
    
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.beginPath();
    state.stars.forEach(s => {
        const starParallaxX = s.initialX - (state.viewCurrentCenterX - PLAY_AREA_WIDTH / 2) * STAR_PARALLAX_FACTOR * (1/state.viewCurrentScale);
        const starParallaxY = s.initialY - (state.viewCurrentCenterY - PLAY_AREA_HEIGHT / 2) * STAR_PARALLAX_FACTOR * (1/state.viewCurrentScale);
        const genAreaWidth = PLAY_AREA_WIDTH * STAR_GENERATION_AREA_MULTIPLIER;
        const genAreaHeight = PLAY_AREA_HEIGHT * STAR_GENERATION_AREA_MULTIPLIER;
        const genOffsetX = (PLAY_AREA_WIDTH - genAreaWidth) / 2;
        const genOffsetY = (PLAY_AREA_HEIGHT - genAreaHeight) / 2;
        const wrappedX = ((starParallaxX - genOffsetX) % genAreaWidth + genAreaWidth) % genAreaWidth + genOffsetX;
        const wrappedY = ((starParallaxY - genOffsetY) % genAreaHeight + genAreaHeight) % genAreaHeight + genOffsetY;
        ctx.moveTo(wrappedX + s.s, wrappedY);
        ctx.arc(wrappedX, wrappedY, s.s, 0, 2*Math.PI);
    });
    ctx.fill();

    ctx.save();

    // Calculate minimum distance of any active ball to the boundary
    let minDistToBoundary = Infinity;
    if (state.gameState && state.gameState.balls) {
        state.gameState.balls.forEach(ball => {
            if (ball && !ball.sunk) {
                const dist = Math.min(ball.x, PLAY_AREA_WIDTH - ball.x, ball.y, PLAY_AREA_HEIGHT - ball.y);
                if (dist < minDistToBoundary) {
                    minDistToBoundary = dist;
                }
            }
        });
    }

    const warningThreshold = 250;
    let factor = 0;
    if (minDistToBoundary < warningThreshold) {
        factor = 1 - (Math.max(0, minDistToBoundary) / warningThreshold);
    }

    // Interpolate colors: default is cool light cyan-blue, warning is orange
    const r = Math.round(160 + (255 - 160) * factor);
    const g = Math.round(210 + (120 - 210) * factor);
    const b = Math.round(255 + (0 - 255) * factor);
    const a = 0.45 + (0.95 - 0.45) * factor;

    ctx.beginPath();
    ctx.roundRect(0, 0, PLAY_AREA_WIDTH, PLAY_AREA_HEIGHT, 40);

    if (state.activePowerUps.oobShield) {
        // High-tech solid glowing neon-teal containment field!
        const pulse = 0.55 + Math.sin(performance.now() * 0.004) * 0.2;
        ctx.strokeStyle = `rgba(45, 212, 191, ${pulse})`; 
        ctx.setLineDash([]); // solid line!
        ctx.lineWidth = 4.5 / state.viewCurrentScale;
        ctx.shadowColor = '#2dd4bf';
        ctx.shadowBlur = 18 / state.viewCurrentScale;
    } else {
        // Standard dashed white/orange line
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${a})`;
        ctx.setLineDash([10, 10]);
        ctx.lineWidth = (2 + factor * 2.5) / state.viewCurrentScale;
        if (factor > 0) {
            ctx.shadowColor = `rgba(255, 120, 0, ${factor * 0.9})`;
            ctx.shadowBlur = 15 * factor;
        }
    }
    ctx.stroke();

    // --- Render boundary forcefield impact ripple if oobShield is active ---
    if (state.activePowerUps.oobShield) {
        ctx.save();
        
        // Draw bright forcefield contact ripple if we recently bounced
        let impactIntensity = 0;
        if (state.boundaryShieldImpactTime) {
            const elapsed = performance.now() - state.boundaryShieldImpactTime;
            if (elapsed < 1000) {
                impactIntensity = 1.0 - elapsed / 1000;
            } else {
                state.boundaryShieldImpactTime = 0;
            }
        }
        
        if (impactIntensity > 0) {
            ctx.beginPath();
            ctx.roundRect(0, 0, PLAY_AREA_WIDTH, PLAY_AREA_HEIGHT, 40);
            ctx.strokeStyle = `rgba(255, 255, 255, ${impactIntensity * 0.95})`; // bright white ripple
            ctx.lineWidth = (4.5 + impactIntensity * 10) / state.viewCurrentScale;
            ctx.shadowColor = '#2dd4bf';
            ctx.shadowBlur = (20 + impactIntensity * 30) / state.viewCurrentScale;
            ctx.stroke();
        }
        
        ctx.restore();
    }

    ctx.restore();
    
    // --- Render Distorted Gravity Well Mesh (Einstein-Rosen Curvature) ---
    if (state.activePowerUps.gravityGrid && state.gravityGridCanvas) {
        ctx.save();
        ctx.drawImage(state.gravityGridCanvas, 0, 0);
        ctx.restore();
    }
    
    state.gameState.planets.forEach((p, index) => {
        ctx.save();
        ctx.translate(p.x, p.y);

        // 1. Draw back rings if ringed planet from cache
        if (p.style === 'ringed' && p.ringAngle !== undefined && p.backRingsCanvas) {
            ctx.save();
            ctx.rotate(p.ringAngle);
            const w = (p.r * 5 + 10) * 2;
            ctx.drawImage(p.backRingsCanvas, -w/2, -w/2, w, w);
            ctx.restore();
        }

        // 2. Draw cached planet body (includes atmosphere, textures, specular, shadow, bunkers and goal cutout)
        if (p.bodyCanvas) {
            const w = p.r * 3 + 10;
            ctx.drawImage(p.bodyCanvas, -w/2, -w/2, w, w);
        }

        if (index === state.gameState.goal.planetIndex) {
            const goalData = state.gameState.goal; const holeAngle = goalData.angleOnPlanet;
            const holeRadius = goalData.holeRadius; const flagHeight = goalData.flagHeight;
            const holeSurfaceX = p.r * Math.cos(holeAngle); const holeSurfaceY = p.r * Math.sin(holeAngle);

            // Clip the goal rings to the planet body to prevent them from spilling into space
            ctx.save();
            ctx.beginPath();
            ctx.arc(0, 0, p.r, 0, Math.PI * 2);
            ctx.clip();

            // Glowing goal rings of solid green emerald color (as requested)
            const ringCount = 3;
            for (let rIdx = 0; rIdx < ringCount; rIdx++) {
                ctx.beginPath();
                const currentRadius = holeRadius + (2 + rIdx * 2.5) / state.viewCurrentScale;
                ctx.arc(holeSurfaceX, holeSurfaceY, currentRadius, 0, Math.PI * 2);
                ctx.strokeStyle = `rgba(16, 185, 129, ${0.7 - rIdx * 0.25})`; // Emerald green
                ctx.lineWidth = Math.max(1, (2.5 - rIdx * 0.6) / state.viewCurrentScale);
                ctx.shadowBlur = (12 - rIdx * 3) / state.viewCurrentScale;
                ctx.shadowColor = '#10B981';
                ctx.stroke();
            }
            ctx.restore();
            
            const poleInnerBaseRadius = p.r - holeRadius * 0.8; 
            const poleBaseX = poleInnerBaseRadius * Math.cos(holeAngle); const poleBaseY = poleInnerBaseRadius * Math.sin(holeAngle);
            const currentPoleAngle = holeAngle + (goalData.wiggleAngle || 0);
            const poleTopX = poleBaseX + (flagHeight + holeRadius * 0.8) * Math.cos(currentPoleAngle); const poleTopY = poleBaseY + (flagHeight + holeRadius * 0.8) * Math.sin(currentPoleAngle);
            ctx.beginPath(); ctx.moveTo(poleBaseX, poleBaseY); ctx.lineTo(poleTopX, poleTopY);
            ctx.strokeStyle = '#B0B0B0'; ctx.lineWidth = Math.max(0.75, 2 / state.viewCurrentScale); ctx.stroke();
            
            // Draw waving solar wind flag
            const flagWidth = flagHeight * 0.65;
            const flagHeightOnPole = flagHeight * 0.32;
            const t = performance.now() * 0.007; // wave animation speed
            
            ctx.save();
            ctx.beginPath();
            
            // Start at top attach point on the pole
            ctx.moveTo(poleTopX, poleTopY);
            
            const flagSteps = 10;
            const perpAngle = currentPoleAngle + Math.PI / 2; // direction the flag points
            
            // Waving path
            for (let s = 1; s <= flagSteps; s++) {
                const frac = s / flagSteps;
                const distAlong = frac * flagWidth;
                const wave = Math.sin(frac * 3 - t) * 3.0; // stable world-space waving!
                
                const px = poleTopX + distAlong * Math.cos(perpAngle) + wave * Math.cos(currentPoleAngle);
                const py = poleTopY + distAlong * Math.sin(perpAngle) + wave * Math.sin(currentPoleAngle);
                ctx.lineTo(px, py);
            }
            
            // Connect to bottom attach point on the pole
            const poleBottomAttachX = poleTopX - flagHeightOnPole * Math.cos(currentPoleAngle);
            const poleBottomAttachY = poleTopY - flagHeightOnPole * Math.sin(currentPoleAngle);
            
            for (let s = flagSteps; s >= 0; s--) {
                const frac = s / flagSteps;
                const distAlong = frac * flagWidth;
                const wave = Math.sin(frac * 3 - t) * 3.0; // stable world-space waving!
                
                const poleAttachFracX = poleTopX - (1 - frac) * flagHeightOnPole * Math.cos(currentPoleAngle);
                const poleAttachFracY = poleTopY - (1 - frac) * flagHeightOnPole * Math.sin(currentPoleAngle);
                
                const px = poleBottomAttachX + distAlong * Math.cos(perpAngle) + wave * Math.cos(currentPoleAngle);
                const py = poleBottomAttachY + distAlong * Math.sin(perpAngle) + wave * Math.sin(currentPoleAngle);
                ctx.lineTo(px, py);
            }
            
            ctx.closePath();
            
            // Vibrant standard red flag (as requested)
            ctx.fillStyle = '#EF4444';
            ctx.shadowBlur = 3; // stable world-space glow!
            ctx.shadowColor = '#EF4444';
            ctx.fill();
            
            // Sleek glowing flag border
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 1.0; // scales down beautifully!
            ctx.stroke();
            
            ctx.restore();
        }
        ctx.restore(); 
    });

    // --- Render Coins (Arcade Mode Only) ---
    if (state.currentPlayingMode === 'powerup' && state.gameState.coins && state.gameState.coins.length > 0) {
        state.gameState.coins.forEach(coin => {
            if (coin.collected) return;
            
            ctx.save();
            ctx.translate(coin.x, coin.y);
            
            // Floating up and down using a sine wave
            const floatOffset = Math.sin(performance.now() * 0.005 + coin.pulseOffset) * 3.5;
            ctx.translate(0, floatOffset);
            
            // Slow horizontal 3D rotation scale
            const spinScale = Math.sin(performance.now() * 0.004 + coin.pulseOffset);
            ctx.scale(spinScale, 1.0);
            
            if (coin.value === 10) {
                // Draw majestic purple Mega Coin!
                ctx.beginPath();
                ctx.arc(0, 0, coin.r, 0, Math.PI * 2);
                ctx.fillStyle = '#C084FC'; // Purple 400
                ctx.fill();
                
                // Outer glowing neon border
                ctx.beginPath();
                ctx.arc(0, 0, coin.r * 0.85, 0, Math.PI * 2);
                ctx.strokeStyle = '#F472B6'; // Pink 400
                ctx.lineWidth = 1.8;
                ctx.stroke();
                
                // Gold Core
                ctx.beginPath();
                ctx.arc(0, 0, coin.r * 0.5, 0, Math.PI * 2);
                ctx.fillStyle = '#F59E0B'; // Amber Gold core
                ctx.fill();
                
                // "10" text in center
                ctx.fillStyle = '#FFFFFF';
                ctx.font = `900 ${Math.floor(coin.r * 0.82)}px 'Orbitron', sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText('10', 0, -0.5);
            } else {
                // Draw normal shiny gold coin!
                ctx.beginPath();
                ctx.arc(0, 0, coin.r, 0, Math.PI * 2);
                ctx.fillStyle = '#F59E0B'; // Amber 500 Gold
                ctx.fill();
                
                // Inner border highlight
                ctx.beginPath();
                ctx.arc(0, 0, coin.r * 0.75, 0, Math.PI * 2);
                ctx.strokeStyle = '#FBBF24'; // Yellow 400
                ctx.lineWidth = 1.2;
                ctx.stroke();
                
                // "1" digit in center
                ctx.fillStyle = '#FFFFFF';
                ctx.font = `bold ${Math.floor(coin.r * 1.1)}px 'Orbitron', sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText('1', 0, 0);
            }
            
            ctx.restore();
        });
    }

    function drawImprovedTee(ctx, tipX, tipY, angle, width, height, alpha = 1.0) {
        ctx.save();
        const ux = Math.cos(angle);
        const uy = Math.sin(angle);
        const tx = -Math.sin(angle);
        const ty = Math.cos(angle);
        const teeBaseX = tipX - ux * height;
        const teeBaseY = tipY - uy * height;
        
        ctx.fillStyle = `rgba(248, 250, 252, ${alpha})`; // slate-50 white
        ctx.strokeStyle = `rgba(15, 23, 42, ${alpha})`; // slate-900 outline
        ctx.lineWidth = Math.max(0.75, 1.25 / state.viewCurrentScale);
        
        ctx.beginPath();
        ctx.moveTo(teeBaseX, teeBaseY);
        
        const shaftLeftBaseX = teeBaseX + tx * (width * 0.15) + ux * (height * 0.15);
        const shaftLeftBaseY = teeBaseY + ty * (width * 0.15) + uy * (height * 0.15);
        ctx.lineTo(shaftLeftBaseX, shaftLeftBaseY);
        
        const shaftLeftTopX = tipX + tx * (width * 0.18) - ux * (height * 0.25);
        const shaftLeftTopY = tipY + ty * (width * 0.18) - uy * (height * 0.25);
        ctx.lineTo(shaftLeftTopX, shaftLeftTopY);
        
        const cupLeftX = tipX + tx * (width * 0.5) - ux * (height * 0.1);
        const cupLeftY = tipY + ty * (width * 0.5) - uy * (height * 0.1);
        ctx.lineTo(cupLeftX, cupLeftY);
        
        const cupMidX = tipX - ux * (height * 0.18);
        const cupMidY = tipY - uy * (height * 0.18);
        ctx.quadraticCurveTo(cupMidX, cupMidY, tipX - tx * (width * 0.5) - ux * (height * 0.1), tipY - ty * (width * 0.5) - uy * (height * 0.1));
        
        const shaftRightTopX = tipX - tx * (width * 0.18) - ux * (height * 0.25);
        const shaftRightTopY = tipY - ty * (width * 0.18) - uy * (height * 0.25);
        ctx.lineTo(shaftRightTopX, shaftRightTopY);
        
        const shaftRightBaseX = teeBaseX - tx * (width * 0.15) + ux * (height * 0.15);
        const shaftRightBaseY = teeBaseY - ty * (width * 0.15) + uy * (height * 0.15);
        ctx.lineTo(shaftRightBaseX, shaftRightBaseY);
        
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();
    }

    if (state.teeState.visible && !state.teeState.flying) { 
        drawImprovedTee(ctx, state.teeState.tipX, state.teeState.tipY, state.teeState.angle, state.teeState.width, state.teeState.height, 1.0);
    } else if (state.teeState.flying) { 
        const alpha = Math.max(0, state.teeState.fLife / 2.0); 
        drawImprovedTee(ctx, state.teeState.fx, state.teeState.fy, state.teeState.fAngle, state.teeState.width, state.teeState.height, alpha);
    }

    if (state.currentPlayingMode === 'powerup' && state.activePowerUps.trajectoryGhost && state.gameState.previousShotTrail.length > 1) {
        ctx.lineCap = "round";
        ctx.lineWidth = Math.max(1, 2 / state.viewCurrentScale);
        const ghostBaseAlpha = 0.35; 
        for (let i = 0; i < state.gameState.previousShotTrail.length - 1; i++) {
            const p1 = state.gameState.previousShotTrail[i];
            const p2 = state.gameState.previousShotTrail[i + 1];
            if (!p1 || !p2) continue;
            
            const ageRatio = i / (state.gameState.previousShotTrail.length - 1); 
            const alpha = ghostBaseAlpha * (1 - ageRatio * 0.85); 

            if (alpha < 0.01 && (i < state.gameState.previousShotTrail.length - 2) ) continue; 

            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.strokeStyle = `rgba(100, 150, 255, ${Math.max(0,alpha)})`; 
            if (i < state.gameState.previousShotTrail.length - 2) {
                 const p3 = state.gameState.previousShotTrail[i+2]; 
                 if(p3){
                    const xc = (p2.x + p3.x) / 2; 
                    const yc = (p2.y + p3.y) / 2;
                    ctx.quadraticCurveTo(p2.x, p2.y, xc, yc); 
                 } else { ctx.lineTo(p2.x, p2.y); }
            } else { ctx.lineTo(p2.x, p2.y); }
            ctx.stroke();
        }
    }
    
    // --- DRAW TARGET INDICATOR BADGE (in screen space, so active ball and trajectory draw OVER it!) ---
    if (state.gameState.planets.length > 0 && state.gameState.goal.planetIndex < state.gameState.planets.length && state.gameState.planets[state.gameState.goal.planetIndex]) {
        const goalPlanetForIndicator = state.gameState.planets[state.gameState.goal.planetIndex];
        if (goalPlanetForIndicator && !state.gameState.multiBallActiveOnScreen && !(state.isButtonZoomActive && state.viewTargetScale === state.MIN_PLAY_AREA_VIEW_SCALE && Math.abs(state.viewCurrentScale - state.MIN_PLAY_AREA_VIEW_SCALE) < 0.01) ) {
            const goalHoleWorldX = goalPlanetForIndicator.x + goalPlanetForIndicator.r * Math.cos(state.gameState.goal.angleOnPlanet);
            const goalHoleWorldY = goalPlanetForIndicator.y + goalPlanetForIndicator.r * Math.sin(state.gameState.goal.angleOnPlanet);
            const goalHoleWorldRadius = state.gameState.goal.holeRadius;

            // Calculate distance of goal center from all screen edges
            const goalCenterX = goalHoleWorldX * state.viewCurrentScale + offsetX;
            const goalCenterY = goalHoleWorldY * state.viewCurrentScale + offsetY;
            const distToEdgeX = Math.min(goalCenterX, canvas.width - goalCenterX);
            const distToEdgeY = Math.min(goalCenterY, canvas.height - goalCenterY);
            const minDistToEdge = Math.min(distToEdgeX, distToEdgeY);
            
            // Safe earlier fadeout threshold: Starts fading when within 55px of screen edge, completely hidden when > 55px on screen
            const alpha = minDistToEdge < 0 ? 1.0 : clamp(1.0 - (minDistToEdge / 55), 0, 1);
            
            if (alpha > 0) {
                let originX = state.viewCurrentCenterX;
                let originY = state.viewCurrentCenterY;
                
                // If in spectator mode, use the spectated bot's ball as the origin
                let spectatePlayerForIndicator = null;
                if (state.currentPlayingMode === 'multiplayer' && state.multiplayerLocalFinished) {
                    if (state.spectatingPlayerId !== null && state.spectatingPlayerId !== undefined) {
                        spectatePlayerForIndicator = mpState.players.find(p => p.id === state.spectatingPlayerId);
                    }
                }
                
                if (spectatePlayerForIndicator && spectatePlayerForIndicator.ballState && !spectatePlayerForIndicator.finished) {
                    originX = spectatePlayerForIndicator.ballState.x;
                    originY = spectatePlayerForIndicator.ballState.y;
                } else {
                    const primaryBallForIndicator = state.gameState.balls[0];
                    if (primaryBallForIndicator) {
                        originX = primaryBallForIndicator.x;
                        originY = primaryBallForIndicator.y;
                    }
                }

                // 1. Calculate Ball-to-Goal metrics (for inner arrow heading and distance value)
                const dxBall = goalHoleWorldX - originX;
                const dyBall = goalHoleWorldY - originY;
                const angleBallToGoal = Math.atan2(dyBall, dxBall);
                const distG = Math.hypot(dxBall, dyBall);
                
                // 2. Calculate Camera-to-Goal metrics (for stable, non-wobbly screen edge positioning)
                const dxCam = goalHoleWorldX - state.viewCurrentCenterX;
                const dyCam = goalHoleWorldY - state.viewCurrentCenterY;
                const angleCamToGoal = Math.atan2(dyCam, dxCam);

                // Safe padding boundaries for the compact indicator capsule (width 82, height 36)
                const isMobile = window.innerWidth <= 768;
                const arrowMarginX = isMobile ? 85 : 220; // 220px on desktop to fully clear left stats, right leaderboard, and bottom left/right corner buttons
                let arrowMarginY = 75;
                let bottomMargin = 95; // Increased to 95 to ensure it never underlaps Bottom Controls (Show Course / Settings)
                if (state.currentPlayingMode === 'powerup') {
                    if (isMobile) {
                        bottomMargin = 175; // Safety from mobile bottom shop bar
                    } else {
                        bottomMargin = 125; // Safety from desktop bottom shop bar
                    }
                } else {
                    if (isMobile) {
                        bottomMargin = 95; // Safety from bottom mobile settings buttons
                    } else {
                        bottomMargin = 95; // Safety from bottom desktop buttons
                    }
                }
                
                // Ray-box intersection centered around the VIEWER screen center for ultra-stable positioning
                const cx = canvas.width / 2;
                const cy = canvas.height / 2;
                
                const minX = arrowMarginX;
                const maxX = canvas.width - arrowMarginX;
                const minY = arrowMarginY;
                const maxY = canvas.height - bottomMargin;
                
                const cosCam = Math.cos(angleCamToGoal);
                const sinCam = Math.sin(angleCamToGoal);
                
                // Ray-box intersection from camera screen center
                let tMinX = Infinity, tMaxX = Infinity, tMinY = Infinity, tMaxY = Infinity;
                if (cosCam > 0) tMaxX = (maxX - cx) / cosCam;
                if (cosCam < 0) tMinX = (minX - cx) / cosCam;
                if (sinCam > 0) tMaxY = (maxY - cy) / sinCam;
                if (sinCam < 0) tMinY = (minY - cy) / sinCam;
                
                const tIntersection = Math.min(tMinX, tMaxX, tMinY, tMaxY);
                let arrX_raw = cx + cosCam * tIntersection;
                let arrY_raw = cy + sinCam * tIntersection;
                
                arrX_raw = clamp(arrX_raw, arrowMarginX, canvas.width - arrowMarginX);
                arrY_raw = clamp(arrY_raw, arrowMarginY, canvas.height - bottomMargin);

                // Temporarily exit camera world transform to draw in screen space
                ctx.restore(); 
                
                // Draw gorgeous, sleek, stable capsule
                const w = 82;
                const h = 36;
                const rx = arrX_raw - w / 2;
                const ry = arrY_raw - h / 2;
                
                ctx.save();
                ctx.globalAlpha = alpha; // Smooth fade transition!
                
                // 1. Sleek Drop Shadow
                ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
                ctx.beginPath();
                if (ctx.roundRect) {
                    ctx.roundRect(rx + 3, ry + 3, w, h, 8);
                } else {
                    ctx.rect(rx + 3, ry + 3, w, h);
                }
                ctx.fill();
                
                // 2. High-Contrast Deep Slate Background with Sleek glowing Neon Gold border
                ctx.fillStyle = '#0F172A';
                ctx.strokeStyle = '#FBBF24';
                ctx.lineWidth = 1.5; // Thinner border!
                ctx.beginPath();
                if (ctx.roundRect) {
                    ctx.roundRect(rx, ry, w, h, 8);
                } else {
                    ctx.rect(rx, ry, w, h);
                }
                ctx.fill();
                ctx.stroke();
                
                // 3. Rotating Arrow Head on the left inside the capsule (pointing from ball to goal)
                ctx.save();
                ctx.translate(rx + 18, arrY_raw);
                ctx.rotate(angleBallToGoal);
                ctx.beginPath();
                ctx.moveTo(6, 0);
                ctx.lineTo(-6, -5);
                ctx.lineTo(-3, 0);
                ctx.lineTo(-6, 5);
                ctx.closePath();
                ctx.fillStyle = '#FBBF24';
                ctx.fill();
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 1;
                ctx.stroke();
                ctx.restore();
                
                // 4. "TARGET" Small Heading Label
                ctx.fillStyle = '#94A3B8';
                ctx.font = `900 8px 'Orbitron', sans-serif`;
                ctx.textAlign = 'left';
                ctx.textBaseline = 'top';
                ctx.fillText('TARGET', rx + 34, ry + 6);
                
                // 5. Distance Value
                ctx.fillStyle = '#FFFFFF';
                ctx.font = `900 12px 'Orbitron', sans-serif`;
                ctx.fillText(`${distG.toFixed(0)}`, rx + 34, ry + 16);
                
                ctx.restore();
                
                // Re-open camera world transform to draw the rest of the elements (trails, balls, prediction) on top!
                ctx.save();
                ctx.translate(offsetX, offsetY);
                ctx.scale(state.viewCurrentScale, state.viewCurrentScale);
            }
        }
    }
    state.gameState.balls.forEach(ball => {
        let visualTrailPoints = ball.trail.slice(-TRAIL_MAX_LENGTH);
        if (ball.moving || ball.isSinking) { 
            visualTrailPoints.push({ x: ball.x, y: ball.y, alpha: 1.0 });
        }

        if (visualTrailPoints.length > 1) {
            ctx.lineCap = "round";
            
            const isPrimary = ball.id === 0 || (state.currentPlayingMode === 'multiplayer' && ball.id === state.gameState.balls[0]?.id);
            
            if (isPrimary && state.customBallTrail === 'rainbow') {
                // Draw a gorgeous 6-band parallel pixel rainbow ribbon segment-by-segment for smooth fade out!
                const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a855f7']; // Red, Orange, Yellow, Green, Blue, Purple
                const bandWidth = Math.max(0.6, 1.2 / state.viewCurrentScale);
                
                for (let i = 0; i < visualTrailPoints.length - 1; i++) {
                    const p1 = visualTrailPoints[i];
                    const p2 = visualTrailPoints[i + 1];
                    if (!p1 || !p2 || (p1.alpha < 0.01 && p2.alpha < 0.01)) continue;

                    // Calculate perpendicular offset angle for segment normal
                    const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x) + Math.PI / 2;
                    const segmentAlpha = Math.min(p1.alpha, 0.75);

                    colors.forEach((col, colorIdx) => {
                        ctx.save();
                        ctx.beginPath();
                        const offset = (colorIdx - 2.5) * bandWidth;
                        const dx = Math.cos(angle) * offset;
                        const dy = Math.sin(angle) * offset;

                        ctx.moveTo(p1.x + dx, p1.y + dy);
                        ctx.lineTo(p2.x + dx, p2.y + dy);

                        ctx.strokeStyle = col;
                        ctx.globalAlpha = segmentAlpha;
                        ctx.lineWidth = bandWidth;
                        ctx.stroke();
                        ctx.restore();
                    });
                }
            } else if (isPrimary && state.customBallTrail === 'stardust') {
                ctx.save();
                visualTrailPoints.forEach((pt, pIdx) => {
                    if (pt.alpha < 0.05) return;
                    const size = Math.max(1, 3.2 * pt.alpha / state.viewCurrentScale);
                    
                    // Celestial hues: Gold (45), Cyan (185), Magenta (300), plus flickering Pure White
                    const starHues = [45, 185, 300];
                    const starHue = starHues[(pIdx + Math.floor(performance.now() * 0.003)) % starHues.length];
                    
                    // Outer glow (drawing a semi-transparent larger circle is 50x faster than canvas shadowBlur!)
                    if (pIdx % 4 === 0) {
                        ctx.fillStyle = `rgba(255, 255, 255, ${pt.alpha * 0.22})`;
                    } else {
                        ctx.fillStyle = `hsla(${starHue}, 100%, 75%, ${pt.alpha * 0.20})`;
                    }
                    ctx.beginPath();
                    ctx.arc(pt.x, pt.y, size * 2.5, 0, Math.PI * 2);
                    ctx.fill();

                    // Inner core
                    if (pIdx % 4 === 0) {
                        ctx.fillStyle = `rgba(255, 255, 255, ${pt.alpha * 0.85})`;
                    } else {
                        ctx.fillStyle = `hsla(${starHue}, 100%, 75%, ${pt.alpha * 0.75})`;
                    }
                    ctx.beginPath();
                    ctx.arc(pt.x, pt.y, size, 0, Math.PI * 2);
                    ctx.fill();
                    
                    // Little glittering crosshairs
                    if (pIdx % 4 === 0) {
                        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
                        ctx.lineWidth = 0.8 / state.viewCurrentScale;
                        ctx.beginPath();
                        ctx.moveTo(pt.x - size * 1.5, pt.y); ctx.lineTo(pt.x + size * 1.5, pt.y);
                        ctx.moveTo(pt.x, pt.y - size * 1.5); ctx.lineTo(pt.x, pt.y + size * 1.5);
                        ctx.stroke();
                    }
                });
                ctx.restore();
            } else {
                // Classic standard glowing custom color matched trail
                ctx.lineWidth = Math.max(1.5, 3 / state.viewCurrentScale); 
                for (let i = 0; i < visualTrailPoints.length - 1; i++) {
                    const p1 = visualTrailPoints[i];
                    const p2 = visualTrailPoints[i + 1];
                    if (!p1 || !p2 || (p1.alpha < 0.01 && p2.alpha < 0.01)) continue; 
                    ctx.beginPath();
                    ctx.moveTo(p1.x, p1.y);
                    const alpha = Math.min(p1.alpha, 0.6) * 0.8; 
                    const r = Math.round(ball.colorR);
                    const g = Math.round(ball.colorG);
                    const b = Math.round(ball.colorB);
                    ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`; 
                    if (i < visualTrailPoints.length - 2) {
                         const p3 = visualTrailPoints[i+2]; 
                         if(p3){
                            const xc = (p2.x + p3.x) / 2; 
                            const yc = (p2.y + p3.y) / 2;
                            ctx.quadraticCurveTo(p2.x, p2.y, xc, yc); 
                         } else { ctx.lineTo(p2.x, p2.y); }
                    } else { ctx.lineTo(p2.x, p2.y); }
                    ctx.stroke();
                }
            }
        }
        
        if (!ball.isSinking || ball.r > 0.1) { 
            ctx.save();
            if (ball.isOOBResetting) {
                ctx.globalAlpha = Math.max(0, ball.oobProgress);
            } else if (ball.spawnProtectionTimer > 0 && Math.floor(performance.now() / 80) % 2 === 0) {
                ctx.globalAlpha = 0.25;
            }

            // Draw active power-up glowing auras
            if (!ball.isSinking && state.currentPlayingMode === 'powerup') {
                const time = performance.now();
                
                // 1. Sticky Ball Aura (Amber-gold dripping honey ring)
                if (state.activePowerUps.stickyBall && ball.id === state.gameState.balls[0]?.id) {
                    ctx.save();
                    const bounceRad = ball.r * (1.35 + Math.sin(time * 0.01) * 0.12);
                    ctx.beginPath();
                    ctx.arc(ball.x, ball.y, bounceRad, 0, Math.PI * 2);
                    ctx.strokeStyle = '#eab308'; // Tailwind yellow-500
                    ctx.lineWidth = 2.5 / state.viewCurrentScale;
                    ctx.shadowBlur = 12 / state.viewCurrentScale;
                    ctx.shadowColor = '#eab308';
                    ctx.stroke();
                    
                    // Draw little dripping droplets
                    for (let i = 0; i < 3; i++) {
                        const dripAngle = time * 0.002 + i * (Math.PI * 2 / 3);
                        const dx = ball.x + Math.cos(dripAngle) * bounceRad;
                        const dy = ball.y + Math.sin(dripAngle) * bounceRad + Math.sin(time * 0.01 + i) * 2;
                        ctx.beginPath();
                        ctx.arc(dx, dy, 2 / state.viewCurrentScale, 0, Math.PI * 2);
                        ctx.fillStyle = '#fef08a'; // light yellow
                        ctx.fill();
                    }
                    ctx.restore();
                }
                
                // 2. Homing Ball Aura (Red-orange rotating targeting reticle)
                if (state.activePowerUps.homingBall && ball.id === state.gameState.balls[0]?.id) {
                    ctx.save();
                    ctx.translate(ball.x, ball.y);
                    ctx.rotate(time * 0.002); // rotate target sweep
                    const pulseRad = ball.r * 1.5;
                    
                    ctx.strokeStyle = '#ef4444'; // Tailwind red-500
                    ctx.lineWidth = 1.2 / state.viewCurrentScale;
                    ctx.shadowBlur = 8 / state.viewCurrentScale;
                    ctx.shadowColor = '#ef4444';
                    
                    // Reticle outer bounds
                    ctx.beginPath();
                    ctx.arc(0, 0, pulseRad, 0, Math.PI * 2);
                    ctx.stroke();
                    
                    // Crosshair sweeps
                    ctx.beginPath();
                    ctx.moveTo(0, -pulseRad * 1.3); ctx.lineTo(0, -pulseRad * 0.8);
                    ctx.moveTo(0, pulseRad * 1.3); ctx.lineTo(0, pulseRad * 0.8);
                    ctx.moveTo(-pulseRad * 1.3, 0); ctx.lineTo(-pulseRad * 0.8, 0);
                    ctx.moveTo(pulseRad * 1.3, 0); ctx.lineTo(pulseRad * 0.8, 0);
                    ctx.stroke();
                    ctx.restore();
                }
                
                // 3. Trajectory Ghost Aura (Cyan phantasmal aura)
                if (state.activePowerUps.trajectoryGhost && ball.id === state.gameState.balls[0]?.id) {
                    ctx.save();
                    ctx.beginPath();
                    ctx.arc(ball.x, ball.y, ball.r * (1.25 + Math.sin(time * 0.015) * 0.08), 0, Math.PI * 2);
                    ctx.strokeStyle = '#06b6d4'; // Tailwind cyan-500
                    ctx.lineWidth = 1.5 / state.viewCurrentScale;
                    ctx.shadowBlur = 10 / state.viewCurrentScale;
                    ctx.shadowColor = '#06b6d4';
                    ctx.stroke();
                    ctx.restore();
                }
                
                // 4. Multi-Ball Aura (High energy sparkling white/indigo aura)
                if (state.activePowerUps.multiBall && ball.id === state.gameState.balls[0]?.id) {
                    ctx.save();
                    ctx.beginPath();
                    ctx.arc(ball.x, ball.y, ball.r * 1.28, 0, Math.PI * 2);
                    ctx.strokeStyle = '#ffffff';
                    ctx.lineWidth = 2.0 / state.viewCurrentScale;
                    ctx.shadowBlur = 14 / state.viewCurrentScale;
                    ctx.shadowColor = '#6366f1'; // Indigo glow
                    ctx.stroke();
                    
                    // Plasma sparks ticks
                    ctx.strokeStyle = '#818cf8';
                    ctx.lineWidth = 1.0 / state.viewCurrentScale;
                    ctx.beginPath();
                    for (let i = 0; i < 4; i++) {
                        const sparkAngle = time * 0.005 + i * (Math.PI / 2);
                        const s1 = ball.r * 1.28;
                        const s2 = ball.r * 1.52;
                        ctx.moveTo(ball.x + Math.cos(sparkAngle) * s1, ball.y + Math.sin(sparkAngle) * s1);
                        ctx.lineTo(ball.x + Math.cos(sparkAngle) * s2, ball.y + Math.sin(sparkAngle) * s2);
                    }
                    ctx.stroke();
                    ctx.restore();
                }
            }

            const skinName = ball.id === 0 ? state.customBallSkin : 'neon';
            drawBallSkin(ctx, ball.x, ball.y, ball.r, ball.rotation, skinName, ball.colorR, ball.colorG, ball.colorB, ball.impactTimer || 0, ball.nauseaLevel || 0);
            
            // If multiplayer mode, draw the local player's name above their primary ball
            if (state.currentPlayingMode === 'multiplayer' && ball.id === state.gameState.balls[0]?.id) {
                ctx.save();
                ctx.fillStyle = `rgba(${Math.round(ball.colorR)}, ${Math.round(ball.colorG)}, ${Math.round(ball.colorB)}, 0.85)`;
                ctx.font = 'bold 9px Orbitron';
                ctx.textAlign = 'center';
                const displayName = state.currentPlayerName ? (state.currentPlayerName.length > 8 ? state.currentPlayerName.substring(0, 8) + ".." : state.currentPlayerName) : "YOU";
                ctx.fillText(displayName.toUpperCase(), ball.x, ball.y - 12);
                ctx.restore();
            }

            ctx.restore();
        }
    });
    
    // --- Draw Opponent Ball (Multiplayer Mode) ---
    if (state.currentPlayingMode === 'multiplayer') {
        for (const opponent of mpState.players) {
            if (opponent.id === mpState.playerId) continue; // Skip self
            
            if (opponent.holeIndex === state.currentHoleIndex && opponent.ballState) {
                const oppBall = opponent.ballState;
                const r = oppBall.colorR !== undefined ? oppBall.colorR : 255;
                const g = oppBall.colorG !== undefined ? oppBall.colorG : 255;
                const b = oppBall.colorB !== undefined ? oppBall.colorB : 255;
                
                // Draw opponent trail
                if (oppBall.trail && oppBall.trail.length > 1) {
                    const visualTrailPoints = oppBall.trail;
                    const oppTrailEffect = oppBall.trailEffect || 'neon';
                    
                    if (oppTrailEffect === 'rainbow') {
                        // Draw a gorgeous 6-band parallel pixel rainbow ribbon segment-by-segment for smooth fade out!
                        const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a855f7']; // Red, Orange, Yellow, Green, Blue, Purple
                        const bandWidth = Math.max(0.6, 1.2 / state.viewCurrentScale);
                        
                        for (let i = 0; i < visualTrailPoints.length - 1; i++) {
                            const p1 = visualTrailPoints[i];
                            const p2 = visualTrailPoints[i + 1];
                            if (!p1 || !p2 || (p1.alpha < 0.01 && p2.alpha < 0.01)) continue;

                            // Calculate perpendicular offset angle for segment normal
                            const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x) + Math.PI / 2;
                            const segmentAlpha = Math.min(p1.alpha, 0.45);

                            colors.forEach((col, colorIdx) => {
                                ctx.save();
                                ctx.beginPath();
                                const offset = (colorIdx - 2.5) * bandWidth;
                                const dx = Math.cos(angle) * offset;
                                const dy = Math.sin(angle) * offset;

                                ctx.moveTo(p1.x + dx, p1.y + dy);
                                ctx.lineTo(p2.x + dx, p2.y + dy);

                                ctx.strokeStyle = col;
                                ctx.globalAlpha = segmentAlpha;
                                ctx.lineWidth = bandWidth;
                                ctx.stroke();
                                ctx.restore();
                            });
                        }
                    } else if (oppTrailEffect === 'stardust') {
                        ctx.save();
                        visualTrailPoints.forEach((pt, pIdx) => {
                            if (pt.alpha < 0.05) return;
                            const size = Math.max(1, 3.2 * pt.alpha / state.viewCurrentScale);
                            
                            // Celestial hues: Gold (45), Cyan (185), Magenta (300), plus flickering Pure White
                            const starHues = [45, 185, 300];
                            const starHue = starHues[(pIdx + Math.floor(performance.now() * 0.003)) % starHues.length];
                            
                            // Outer glow (drawing a semi-transparent larger circle is 50x faster than canvas shadowBlur!)
                            if (pIdx % 4 === 0) {
                                ctx.fillStyle = `rgba(255, 255, 255, ${pt.alpha * 0.15})`;
                            } else {
                                ctx.fillStyle = `hsla(${starHue}, 100%, 75%, ${pt.alpha * 0.12})`;
                            }
                            ctx.beginPath();
                            ctx.arc(pt.x, pt.y, size * 2.5, 0, Math.PI * 2);
                            ctx.fill();

                            // Inner core
                            if (pIdx % 4 === 0) {
                                ctx.fillStyle = `rgba(255, 255, 255, ${pt.alpha * 0.55})`;
                            } else {
                                ctx.fillStyle = `hsla(${starHue}, 100%, 75%, ${pt.alpha * 0.45})`;
                            }
                            ctx.beginPath();
                            ctx.arc(pt.x, pt.y, size, 0, Math.PI * 2);
                            ctx.fill();
                            
                            // Little glittering crosshairs
                            if (pIdx % 4 === 0) {
                                ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
                                ctx.lineWidth = 0.8 / state.viewCurrentScale;
                                ctx.beginPath();
                                ctx.moveTo(pt.x - size * 1.5, pt.y); ctx.lineTo(pt.x + size * 1.5, pt.y);
                                ctx.moveTo(pt.x, pt.y - size * 1.5); ctx.lineTo(pt.x, pt.y + size * 1.5);
                                ctx.stroke();
                            }
                        });
                        ctx.restore();
                    } else {
                        // Standard classic custom color matched trail
                        ctx.save();
                        ctx.lineCap = "round";
                        ctx.lineWidth = Math.max(1.0, 2 / state.viewCurrentScale);
                        for (let i = 0; i < oppBall.trail.length - 1; i++) {
                            const p1 = oppBall.trail[i];
                            const p2 = oppBall.trail[i + 1];
                            if (!p1 || !p2 || (p1.alpha < 0.01 && p2.alpha < 0.01)) continue;
                            ctx.beginPath();
                            ctx.moveTo(p1.x, p1.y);
                            const alpha = Math.min(p1.alpha, 0.4) * 0.45; 
                            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`; 
                            if (i < oppBall.trail.length - 2) {
                                const p3 = oppBall.trail[i+2];
                                if (p3) {
                                    const xc = (p2.x + p3.x) / 2;
                                    const yc = (p2.y + p3.y) / 2;
                                    ctx.quadraticCurveTo(p2.x, p2.y, xc, yc);
                                } else { ctx.lineTo(p2.x, p2.y); }
                            } else { ctx.lineTo(p2.x, p2.y); }
                            ctx.stroke();
                        }
                        ctx.restore();
                    }
                }

                if (!oppBall.isSinking) {
                    ctx.save();
                    if (oppBall.spawnProtectionTimer > 0 && Math.floor(performance.now() / 80) % 2 === 0) {
                        ctx.globalAlpha = 0.25;
                    }
                    const oppRad = oppBall.r || BALL_RADIUS;
                    drawBallSkin(ctx, oppBall.x, oppBall.y, oppRad, oppBall.rotation, oppBall.skin || 'neon', r, g, b, oppBall.impactTimer || 0, oppBall.nauseaLevel || 0);
                    
                    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.85)`;
                    ctx.font = 'bold 9px Orbitron';
                    ctx.textAlign = 'center';
                    const displayName = opponent.name ? (opponent.name.length > 8 ? opponent.name.substring(0, 8) + ".." : opponent.name) : "PLAYER";
                    ctx.fillText(displayName.toUpperCase(), oppBall.x, oppBall.y - 12);
                    ctx.restore();
                }
            }
        }
    }
    
    const primaryBall = state.gameState.balls[0];
    if(primaryBall && state.gameState.isAiming && !primaryBall.moving && !primaryBall.isSinking){
        const visualAimLength = state.gameState.aimPower; 
        const aimEndX = primaryBall.x - Math.cos(state.gameState.aimAngle) * visualAimLength; 
        const aimEndY = primaryBall.y - Math.sin(state.gameState.aimAngle) * visualAimLength;
        const actualDeadZonePower = state.currentDynamicMaxDrag * MIN_DRAG_THRESHOLD_FACTOR;
        const isDeadZone = state.gameState.aimPower < actualDeadZonePower;

        ctx.beginPath();ctx.moveTo(primaryBall.x,primaryBall.y);ctx.lineTo(aimEndX,aimEndY);
        ctx.strokeStyle = isDeadZone ? 'rgba(255,0,0,0.7)' : `rgba(${Math.round(primaryBall.colorR)}, ${Math.round(primaryBall.colorG)}, ${Math.round(primaryBall.colorB)}, 0.85)`; 
        ctx.lineWidth = Math.max(1.5, 2.5/state.viewCurrentScale);ctx.stroke();
        
        drawBallSkin(ctx, primaryBall.x, primaryBall.y, primaryBall.r, primaryBall.rotation, state.customBallSkin, primaryBall.colorR, primaryBall.colorG, primaryBall.colorB, primaryBall.impactTimer || 0, primaryBall.nauseaLevel || 0);

        if (!isDeadZone) { 
            let powerRatioForPrediction = 0;
            if (state.gameState.aimPower > actualDeadZonePower) {
                const effectiveAimPower = state.gameState.aimPower - actualDeadZonePower;
                const effectiveMaxDrag = state.currentDynamicMaxDrag - actualDeadZonePower;
                if (effectiveMaxDrag > 0) { 
                    powerRatioForPrediction = effectiveAimPower / effectiveMaxDrag;
                }
            }
            let currentShotPowerMultiplierForPrediction = 1.0;
            let hostPlanetIndexForBunkerShot = -1;
            if (primaryBall.isInBunker) {
                currentShotPowerMultiplierForPrediction = BUNKER_SHOT_POWER_REDUCTION;
                for (let pi = 0; pi < state.gameState.planets.length; pi++) {
                    const p = state.gameState.planets[pi];
                    if (p.bunkers && p.bunkers.length > 0) {
                        const ballRadialDistFromPlanetCenter = Math.hypot(primaryBall.x - p.x, primaryBall.y - p.y);
                        const stickDepthRadius = p.r - (primaryBall.r * BUNKER_STICK_DEPTH_FRAC); 
                        if (ballRadialDistFromPlanetCenter < stickDepthRadius + primaryBall.r * 0.2) { 
                             hostPlanetIndexForBunkerShot = pi;
                             break;
                        }
                    }
                }
            }

            const basePowerForSqrtPrediction = powerRatioForPrediction * POWER_CURVE_SCALER; 
            const powerNormalizedForPrediction = Math.sqrt(Math.max(0, basePowerForSqrtPrediction)) * AIM_POWER_SENSITIVITY_FACTOR * currentShotPowerMultiplierForPrediction; 
            
            const initVx = Math.cos(state.gameState.aimAngle)*powerNormalizedForPrediction*SHOT_POWER_MULTIPLIER; 
            const initVy = Math.sin(state.gameState.aimAngle)*powerNormalizedForPrediction*SHOT_POWER_MULTIPLIER; 
            // Use a stable, fixed step size for trajectory preview to completely eliminate rapid dotted-line flickering caused by dynamic frame rate jitter
            const TOTAL_PRED_DURATION = state.activePowerUps.trajectoryLengthener ? 2.0 : (2 / 3);
            const stepSize = 1 / 60; // stable 60Hz physics step
            const actualPredictionSteps = Math.round(TOTAL_PRED_DURATION / stepSize);
            const trajectory = predictTrajectory(primaryBall.x, primaryBall.y, initVx, initVy, actualPredictionSteps, stepSize, hostPlanetIndexForBunkerShot, state.activePowerUps.linearLaser); 
            
            if (trajectory.length > 1) {
                // 1. Draw main glowing flowing dashed line
                ctx.save();
                ctx.beginPath();
                
                // Density of dashes changes with power ratio!
                // Max power = 1.0 (highly packed, high frequency dashes)
                // Min power = 0.0 (loosely spaced, calm dashes)
                const dashLen = lerp(12, 4, powerRatioForPrediction) / state.viewCurrentScale;
                const dashSpace = lerp(9, 3, powerRatioForPrediction) / state.viewCurrentScale;
                ctx.setLineDash([dashLen, dashSpace]);
                
                // Flow speed changes with power ratio too!
                const flowSpeed = lerp(0.015, 0.08, powerRatioForPrediction);
                ctx.lineDashOffset = -performance.now() * flowSpeed;
                
                ctx.moveTo(trajectory[0].x, trajectory[0].y);
                for (let i = 1; i < trajectory.length; i++) {
                    ctx.lineTo(trajectory[i].x, trajectory[i].y);
                }
                
                // Dynamic power color: green/cyan (low power) -> yellow (medium power) -> red (high power)
                // HSL: 140 (cyan-green) down to 0 (red)
                const mainHue = lerp(140, 0, powerRatioForPrediction);
                const strokeColor = `hsla(${mainHue}, 100%, 55%, 0.85)`;
                const tipColor = `hsla(${mainHue}, 100%, 55%, 0.15)`;
                
                // Create gradient from start to end of trajectory
                const grad = ctx.createLinearGradient(
                    trajectory[0].x, trajectory[0].y, 
                    trajectory[trajectory.length - 1].x, trajectory[trajectory.length - 1].y
                );
                grad.addColorStop(0, strokeColor);
                grad.addColorStop(1, tipColor); // fade out at tip
                
                ctx.strokeStyle = grad;
                ctx.lineWidth = (lerp(2.0, 4.5, powerRatioForPrediction)) / state.viewCurrentScale;
                ctx.shadowBlur = (6 + powerRatioForPrediction * 8) / state.viewCurrentScale;
                ctx.shadowColor = `hsla(${mainHue}, 100%, 55%, 0.65)`;
                ctx.stroke();
                ctx.setLineDash([]); // Reset line dash
                ctx.restore();
                
                // 2. Draw bounce markers at points where bounce is tagged
                trajectory.forEach((pt, idx) => {
                    if (pt.isBounce) {
                        ctx.save();
                        // Dynamic pulse scale based on time for nice micro-interaction
                        const pulseScale = 1 + Math.sin(performance.now() * 0.015) * 0.12;
                        const rBase = 6 / state.viewCurrentScale * pulseScale;
                        
                        // Select color based on bounce type
                        let bColor = 'rgba(0, 255, 255, 0.85)'; // Cyan for standard surface
                        let shadowCol = 'rgba(0, 255, 255, 0.5)';
                        if (pt.bounceType === 'rock') {
                            bColor = 'rgba(255, 90, 0, 0.9)'; // Bright orange for solid core rock
                            shadowCol = 'rgba(255, 90, 0, 0.5)';
                        } else if (pt.bounceType === 'bunker') {
                            bColor = 'rgba(255, 215, 0, 0.9)'; // Sand gold for bunkers
                            shadowCol = 'rgba(255, 215, 0, 0.5)';
                        }
                        
                        // Inner solid target dot
                        ctx.beginPath();
                        ctx.arc(pt.x, pt.y, rBase * 0.35, 0, Math.PI * 2);
                        ctx.fillStyle = bColor;
                        ctx.shadowBlur = 8 / state.viewCurrentScale;
                        ctx.shadowColor = shadowCol;
                        ctx.fill();
                        
                        // Outer glowing ripple circle
                        ctx.beginPath();
                        ctx.arc(pt.x, pt.y, rBase, 0, Math.PI * 2);
                        ctx.strokeStyle = bColor;
                        ctx.lineWidth = 1.2 / state.viewCurrentScale;
                        ctx.stroke();
                        
                        // Crosshair marks
                        ctx.beginPath();
                        ctx.moveTo(pt.x, pt.y - rBase * 1.5);
                        ctx.lineTo(pt.x, pt.y - rBase * 0.75);
                        ctx.moveTo(pt.x, pt.y + rBase * 1.5);
                        ctx.lineTo(pt.x, pt.y + rBase * 0.75);
                        ctx.moveTo(pt.x - rBase * 1.5, pt.y);
                        ctx.lineTo(pt.x - rBase * 0.75, pt.y);
                        ctx.moveTo(pt.x + rBase * 1.5, pt.y);
                        ctx.lineTo(pt.x + rBase * 0.75, pt.y);
                        
                        ctx.lineWidth = 1.0 / state.viewCurrentScale;
                        ctx.stroke();
                        
                        ctx.restore();
                    }
                });
            }
        }
    }
    // Draw front rings of all planets on top of the balls from cache
    state.gameState.planets.forEach(p => {
        if (p.style === 'ringed' && p.ringAngle !== undefined && p.frontRingsCanvas) {
            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.ringAngle);
            const w = (p.r * 5 + 10) * 2;
            ctx.drawImage(p.frontRingsCanvas, -w/2, -w/2, w, w);
            ctx.restore();
        }
    });

    // Render collision particles in world space
    if (state.collisionParticles && state.collisionParticles.length > 0) {
        state.collisionParticles.forEach(p => {
            ctx.save();
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * (p.life / p.maxLife), 0, Math.PI * 2);
            ctx.fillStyle = p.color;
            ctx.globalAlpha = Math.max(0, p.life / p.maxLife);
            ctx.shadowBlur = 8 / state.viewCurrentScale;
            ctx.shadowColor = p.color;
            ctx.fill();
            ctx.restore();
        });
    }

    ctx.restore(); 
    
    

    state.fireworks.forEach(fw => {
        if(!fw.exploded){ctx.beginPath();ctx.arc(fw.x,fw.y,3,0,Math.PI*2);ctx.fillStyle=fw.color;ctx.fill();} 
        fw.particles.forEach(p=>{ctx.beginPath();ctx.arc(p.x,p.y,Math.max(0.5,2*p.alpha),0,Math.PI*2);ctx.fillStyle=`hsla(${parseInt(fw.color.substring(4,fw.color.indexOf(',')))},100%,70%,${p.alpha*0.8})`;ctx.fill();}); 
    });

    // Render full screen swirling wormhole warp transition overlay
    if (state.wormholeActive) {
        const progress = state.wormholeProgress / 0.8; // normalized 0 to 1
        if (progress < 1) {
            ctx.save();
            ctx.fillStyle = `rgba(5, 7, 26, ${Math.sin(progress * Math.PI) * 0.95})`; // fade screen black at peak
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Draw swirling vortex spiral rings
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            const maxRadius = Math.max(canvas.width, canvas.height) * 0.8;
            const ringCount = 8;
            
            ctx.lineWidth = 3;
            for (let i = 0; i < ringCount; i++) {
                const ringProgress = (progress + i / ringCount) % 1.0;
                const radius = maxRadius * (1.0 - ringProgress); // swirl inwards
                const alpha = Math.sin(ringProgress * Math.PI) * 0.45;
                
                ctx.strokeStyle = i % 2 === 0 ? `rgba(0, 255, 255, ${alpha})` : `rgba(255, 0, 255, ${alpha})`;
                ctx.save();
                ctx.translate(centerX, centerY);
                ctx.rotate(ringProgress * Math.PI * 4 + (i * 0.5)); // spin
                
                // Draw a beautiful spiral arc segment
                ctx.beginPath();
                ctx.arc(0, 0, radius, 0, Math.PI * 1.5, false);
                ctx.stroke();
                ctx.restore();
            }
            ctx.restore();
        }
    }
}

let lastIsAiming = null;
function gameLoop(currentTime) {
    state.gameLoopRequestId = requestAnimationFrame(gameLoop);
    
    // If start screen is active, skip updating/rendering to free up 100% CPU/GPU for buttery smooth menu animations!
    const startScreen = document.getElementById('startScreen');
    if (startScreen && startScreen.classList.contains('active')) {
        lastTime = 0; // Reset lastTime so next frame after starting has a fresh deltaTime!
        return;
    }
    
    const isAiming = state.gameState?.isAiming || false;
    if (isAiming !== lastIsAiming) {
        lastIsAiming = isAiming;
        document.body.classList.toggle('aiming-active', isAiming);
    }
    update(currentTime);
    render();
}

// --- Canvas Input Handling ---
let isDragging = false;
let dragStartX = 0, dragStartY = 0;

function getMousePos(canvas, evt) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    let clientX, clientY;
    if (evt.touches && evt.touches.length > 0) {
        clientX = evt.touches[0].clientX;
        clientY = evt.touches[0].clientY;
    } else {
        clientX = evt.clientX;
        clientY = evt.clientY;
    }
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
}

function handleMouseDown(e) {
    e.preventDefault(); 
    checkAudioContext();

    if (state.gameState.goalReached || document.getElementById('startScreen').classList.contains('active') || document.getElementById('gameOverScreen').classList.contains('active')) return;
    if (state.currentPlayingMode === 'powerup' && state.isArcadeRunOver) return;
    
    if (state.initialCourseViewActive) { 
         if (!isViewStable()) { 
            log("Initial view not stable enough for aiming.");
            return;
         }
         state.initialCourseViewActive = false;
         log("Initial course view stable, aiming allowed & view cancelled.");
    } else if (!isViewStable() && !state.isButtonZoomActive) { 
        // Allow aiming even if the camera is still LERPing back to the ball (speedy aim!)
        // We only ignore if the user is currently actively pinching or manual zoom is active.
        if (state.isPinching || state.isInteractingZoom) {
            log("View not stable due to pinch/zoom interaction, shot attempt ignored.");
            return;
        }
    }

    const primaryBall = state.gameState.balls[0];
    if (!primaryBall || primaryBall.moving || primaryBall.isSinking) return;

    const pos = getMousePos(canvas, e);
    isDragging = true;
    dragStartX = pos.x; dragStartY = pos.y;
    state.gameState.isAiming = true;
    state.gameState.aimAngle = 0;
    state.gameState.aimPower = 0;
    startStretchSound();
    
    if (!state.isButtonZoomActive) { 
      state.isInteractingZoom = false; 
      state.zoomInteractionEndTime = Infinity; 
      log("Aiming started. Zoom interaction paused.");
    } else {
        log("Aiming started (button zoom active).");
    }
}

function handleMouseMove(e) {
    e.preventDefault();
    if (!isDragging || state.gameState.goalReached || !state.gameState.isAiming) return;
    if (state.currentPlayingMode === 'powerup' && state.isArcadeRunOver) return;
    
    const pos = getMousePos(canvas, e);
    const dx = dragStartX - pos.x;
    const dy = dragStartY - pos.y;

    state.gameState.aimAngle = Math.atan2(dy, dx);
    let power = Math.hypot(dx, dy);
    power = Math.min(power, state.currentDynamicMaxDrag); 
    state.gameState.aimPower = power;
    
    const maxDrag = state.currentDynamicMaxDrag || 150;
    const powerRatio = clamp(power / maxDrag, 0, 1);
    updateStretchSound(powerRatio);
}

function handleMouseUp(e) {
    e.preventDefault();
    stopStretchSound();
    if (!isDragging || state.gameState.goalReached || !state.gameState.isAiming) return;
    if (state.currentPlayingMode === 'powerup' && state.isArcadeRunOver) return;

    isDragging = false;
    state.gameState.isAiming = false; 
    let primaryBall = state.gameState.balls[0]; 
    if (!primaryBall || primaryBall.moving || primaryBall.isSinking) {
        if (!state.isButtonZoomActive) state.zoomInteractionEndTime = performance.now(); 
        return;
    }
    
    const actualDeadZonePower = state.currentDynamicMaxDrag * MIN_DRAG_THRESHOLD_FACTOR;
    if (state.gameState.aimPower < actualDeadZonePower) {
        log("Shot cancelled: drag below minimum threshold.");
        playShotCancelledSound();
        if (!state.isButtonZoomActive) state.zoomInteractionEndTime = performance.now(); 
        return;
    }

    // Snapshot the state for Chrono Rewind (pre-shot capture)
    if (state.currentPlayingMode === 'powerup') {
        state.rewindState = {
            balls: JSON.parse(JSON.stringify(state.gameState.balls)),
            shotCount: state.shotCount,
            totalScore: state.totalScore,
            totalCoursePar: state.totalCoursePar,
            totalCourseDistance: state.totalCourseDistance,
            totalCourseOptimalDistance: state.totalCourseOptimalDistance,
            previousShotTrail: JSON.parse(JSON.stringify(state.gameState.previousShotTrail)),
            lastShotDistanceTraveled: state.gameState.lastShotDistanceTraveled,
            currentHoleDistanceTraveled: state.gameState.currentHoleDistanceTraveled,
            activePowerUps: JSON.parse(JSON.stringify(state.activePowerUps)),
            coins: JSON.parse(JSON.stringify(state.gameState.coins || [])),
            totalSpaceBucks: state.totalSpaceBucks
        };
    }

    state.shotCount++; 
    if (state.currentPlayingMode === 'multiplayer') {
        sendPacket({ type: 'stroke', holeIndex: state.currentHoleIndex, strokes: state.totalMultiplayerStrokes + state.shotCount });
        updateMPLeaderboardHUD();
        triggerHUDHitFlash(mpState.playerId);
    }
    log(`Shot ${state.shotCount} initiated. Par: ${state.currentPar}.`);

    let powerRatio = 0;
    if (state.gameState.aimPower > actualDeadZonePower) {
        const effectiveAimPower = state.gameState.aimPower - actualDeadZonePower;
        const effectiveMaxDrag = state.currentDynamicMaxDrag - actualDeadZonePower;
        if (effectiveMaxDrag > 0) { 
            powerRatio = effectiveAimPower / effectiveMaxDrag;
        }
    }
    
    let currentShotPowerMultiplier = 1.0;
    let hostPlanetIndexForShotOrigin = -1;
    if (primaryBall.isInBunker) {
        currentShotPowerMultiplier = BUNKER_SHOT_POWER_REDUCTION;
        log("Shooting from bunker, power reduced by " + ((1-BUNKER_SHOT_POWER_REDUCTION)*100).toFixed(0) + "%.");
        for (let pi = 0; pi < state.gameState.planets.length; pi++) {
            const p = state.gameState.planets[pi];
            if (p.bunkers && p.bunkers.length > 0) {
                const ballRadialDistFromPlanetCenter = Math.hypot(primaryBall.x - p.x, primaryBall.y - p.y);
                if (ballRadialDistFromPlanetCenter < p.r + primaryBall.r) { 
                     for (const bunker of p.bunkers) {
                        const bunkerWorldSurfaceX = p.x + p.r * Math.cos(bunker.angle);
                        const bunkerWorldSurfaceY = p.y + p.r * Math.sin(bunker.angle);
                        if (distSq(primaryBall.x, primaryBall.y, bunkerWorldSurfaceX, bunkerWorldSurfaceY) < (bunker.radius * bunker.radius * 1.4)) {
                            hostPlanetIndexForShotOrigin = pi;
                            break;
                        }
                    }
                }
            }
            if (hostPlanetIndexForShotOrigin !== -1) break;
        }
    }

    const basePowerForSqrt = powerRatio * POWER_CURVE_SCALER; 
    const powerNormalized = Math.sqrt(Math.max(0, basePowerForSqrt)) * AIM_POWER_SENSITIVITY_FACTOR * currentShotPowerMultiplier;
    
    const initialVx = Math.cos(state.gameState.aimAngle) * powerNormalized * SHOT_POWER_MULTIPLIER;
    const initialVy = Math.sin(state.gameState.aimAngle) * powerNormalized * SHOT_POWER_MULTIPLIER;

    // Exploit Block: Verify if shooting inward toward planet center
    if (primaryBall.isInBunker && hostPlanetIndexForShotOrigin !== -1) {
        const hostPlanet = state.gameState.planets[hostPlanetIndexForShotOrigin];
        const dx = primaryBall.x - hostPlanet.x;
        const dy = primaryBall.y - hostPlanet.y;
        const dot = initialVx * dx + initialVy * dy;
        
        if (dot <= 0) {
            log("Exploit blocked: cannot shoot inward through the planet body.");
            playSound(thudBounceSynth, "C2", "8n", Tone.now(), 0.5);
            primaryBall.vx = 0;
            primaryBall.vy = 0;
            primaryBall.moving = false;
            if (!state.isButtonZoomActive) state.zoomInteractionEndTime = performance.now();
            handleSingleBallStopped(primaryBall);
            updateInGameUIDisplay();
            return;
        }
    }

    log(`Shot ${state.shotCount} fired: Power Ratio: ${powerRatio.toFixed(2)}, Norm. Power: ${powerNormalized.toFixed(2)}, Vel: (${initialVx.toFixed(1)},${initialVy.toFixed(1)})`);
    playSound(kick, "C2", "8n", Tone.now(), Math.min(1, powerRatio * 1.2));

    const shotStartX = primaryBall.x;
    const shotStartY = primaryBall.y;
    const primaryBallIsInitiallyInBunker = primaryBall.isInBunker;

    // Detect launch planet index to prevent self-collision scraping on takeoff
    let launchPlanetIdx = -1;
    if (state.gameState.planets && state.gameState.planets.length > 0) {
        for (let pi = 0; pi < state.gameState.planets.length; pi++) {
            const p = state.gameState.planets[pi];
            const dist = Math.hypot(shotStartX - p.x, shotStartY - p.y);
            if (dist < p.r + primaryBall.r + 12.0) {
                const dx = shotStartX - p.x;
                const dy = shotStartY - p.y;
                const dot = initialVx * dx + initialVy * dy;
                // Only bypass if the shot is outward or tangent (not inward into the planet)
                if (dot >= -0.01) {
                    launchPlanetIdx = pi;
                }
                break;
            }
        }
    }

    
    let stickyCommittedThisShot = false;
    let laserCommittedThisShot = false;

    if (state.currentPlayingMode === 'powerup') { 
        if (state.activePowerUps.trajectoryLengthener) { 
            state.ownedPowerUps.trajectoryLengthener = Math.max(0, state.ownedPowerUps.trajectoryLengthener - 1); 
            state.activePowerUps.trajectoryLengthener = false; // Consume immediately on launch
            log("Trajectory Predictor consumed.");
        }
        if (state.activePowerUps.gravityGrid) { 
            // Only consume 1 grid count on the first shot of the hole!
            if (state.shotCount === 1) {
                state.ownedPowerUps.gravityGrid = Math.max(0, state.ownedPowerUps.gravityGrid - 1); 
                log("Gravity Grid consumed for this hole.");
            } else {
                log("Gravity Grid remains active.");
            }
        }
        if (state.activePowerUps.stickyBall) { 
            state.ownedPowerUps.stickyBall = Math.max(0, state.ownedPowerUps.stickyBall - 1); 
            stickyCommittedThisShot = true;
            state.activePowerUps.stickyBall = false;
            log("Sticky Ball armed for this shot and consumed.");
        }
        if (state.activePowerUps.linearLaser) { 
            state.ownedPowerUps.linearLaser = Math.max(0, state.ownedPowerUps.linearLaser - 1); 
            laserCommittedThisShot = true;
            state.activePowerUps.linearLaser = false;
            log("Linear Laser armed for this shot and consumed.");
        }
        
        if (state.activePowerUps.multiBall) { 
            state.ownedPowerUps.multiBall = Math.max(0, state.ownedPowerUps.multiBall - 1);
            state.activePowerUps.multiBall = false;
            log("Multi-Ball activated and consumed!");
            state.gameState.multiBallActiveOnScreen = true; 
            state.gameState.balls = []; 
            for (let i = 0; i < MULTI_BALL_COUNT; i++) {
                const angleOffset = (i - Math.floor(MULTI_BALL_COUNT / 2)) * MULTI_BALL_SPREAD_ANGLE;
                const finalAngle = state.gameState.aimAngle + angleOffset;
                const newVx_multi = Math.cos(finalAngle) * powerNormalized * SHOT_POWER_MULTIPLIER; 
                const newVy_multi = Math.sin(finalAngle) * powerNormalized * SHOT_POWER_MULTIPLIER; 
                
                const newBall = createBallState(i, shotStartX, shotStartY, BALL_RADIUS, newVx_multi, newVy_multi, true);
                newBall.angularVelocity = (Math.random() > 0.5 ? 1 : -1) * Math.hypot(newVx_multi, newVy_multi) * 0.04;
                newBall.homingActiveThisShot = false; 
                newBall.isStickyShot = stickyCommittedThisShot;
                newBall.linearLaserActive = laserCommittedThisShot;
                newBall.impactTimer = 1.3;
                if (primaryBallIsInitiallyInBunker && hostPlanetIndexForShotOrigin !== -1) { 
                    const hostPlanet = state.gameState.planets[hostPlanetIndexForShotOrigin];
                    const dx = shotStartX - hostPlanet.x;
                    const dy = shotStartY - hostPlanet.y;
                    const dot = newVx_multi * dx + newVy_multi * dy;
                    if (dot > 0) {
                        newBall.isEscapingBunkerPlanetIdx = hostPlanetIndexForShotOrigin; 
                        newBall.escapingBunkerGraceTime = BUNKER_ESCAPE_GRACE_DURATION;
                    }
                }
                let multiBallGraceApplied = false;
                if (launchPlanetIdx !== -1) {
                    const p = state.gameState.planets[launchPlanetIdx];
                    const dx = shotStartX - p.x;
                    const dy = shotStartY - p.y;
                    const dot = newVx_multi * dx + newVy_multi * dy;
                    if (dot >= -0.01) {
                        multiBallGraceApplied = true;
                    }
                }
                newBall.isEscapingPlanetIdx = multiBallGraceApplied ? launchPlanetIdx : -1;
                newBall.escapingPlanetGraceTime = multiBallGraceApplied ? LAUNCH_ESCAPE_GRACE_DURATION : 0;
                newBall.isInBunker = false; 
                newBall.currentShotStartX = shotStartX;
                newBall.currentShotStartY = shotStartY;
                newBall.trail = [{x:shotStartX,y:shotStartY,alpha:1.0}];
                newBall.launchPlanetIdx = multiBallGraceApplied ? launchPlanetIdx : -1;
                newBall.bouncedPlanetIndices = [];
                state.gameState.balls.push(newBall);
            }
        } else { 
            primaryBall.vx = initialVx;
            primaryBall.vy = initialVy;
            primaryBall.angularVelocity = (Math.random() > 0.5 ? 1 : -1) * Math.hypot(initialVx, initialVy) * 0.04;
            primaryBall.moving = true;
            primaryBall.homingActiveThisShot = false;
            primaryBall.isStickyShot = stickyCommittedThisShot;
            primaryBall.linearLaserActive = laserCommittedThisShot;
            primaryBall.currentShotStartX = shotStartX;
            primaryBall.currentShotStartY = shotStartY;
            primaryBall.currentShotDistanceTraveled = 0;
            primaryBall.impactTimer = 1.3;
            if (primaryBallIsInitiallyInBunker && hostPlanetIndexForShotOrigin !== -1) {
                const hostPlanet = state.gameState.planets[hostPlanetIndexForShotOrigin];
                const dx = shotStartX - hostPlanet.x;
                const dy = shotStartY - hostPlanet.y;
                const dot = initialVx * dx + initialVy * dy;
                if (dot > 0) {
                    primaryBall.isEscapingBunkerPlanetIdx = hostPlanetIndexForShotOrigin;
                    primaryBall.escapingBunkerGraceTime = BUNKER_ESCAPE_GRACE_DURATION;
                }
            }
            primaryBall.isEscapingPlanetIdx = launchPlanetIdx;
            primaryBall.escapingPlanetGraceTime = launchPlanetIdx !== -1 ? LAUNCH_ESCAPE_GRACE_DURATION : 0;
            primaryBall.isInBunker = false; 
            primaryBall.trail = [{x:shotStartX,y:shotStartY,alpha:1.0}];
            primaryBall.launchPlanetIdx = launchPlanetIdx;
            primaryBall.bouncedPlanetIndices = [];
        }
        
        // Deactivate all temporary pre-shot arm states
        state.activePowerUps.trajectoryLengthener = false;
        state.activePowerUps.stickyBall = false;
        state.activePowerUps.linearLaser = false;
        state.activePowerUps.multiBall = false; 
        updatePowerUpActivationButtons();
        saveGameData(); 
    } else { 
        primaryBall.vx = initialVx;
        primaryBall.vy = initialVy;
        primaryBall.angularVelocity = (Math.random() > 0.5 ? 1 : -1) * Math.hypot(initialVx, initialVy) * 0.04;
        primaryBall.moving = true;
        primaryBall.homingActiveThisShot = false; 
        primaryBall.isStickyShot = false;
        primaryBall.currentShotStartX = shotStartX;
        primaryBall.currentShotStartY = shotStartY;
        primaryBall.currentShotDistanceTraveled = 0;
        primaryBall.impactTimer = 1.3;
        if (primaryBallIsInitiallyInBunker && hostPlanetIndexForShotOrigin !== -1) {
            const hostPlanet = state.gameState.planets[hostPlanetIndexForShotOrigin];
            const dx = shotStartX - hostPlanet.x;
            const dy = shotStartY - hostPlanet.y;
            const dot = initialVx * dx + initialVy * dy;
            if (dot > 0) {
                primaryBall.isEscapingBunkerPlanetIdx = hostPlanetIndexForShotOrigin;
                primaryBall.escapingBunkerGraceTime = BUNKER_ESCAPE_GRACE_DURATION;
            }
        }
        primaryBall.isEscapingPlanetIdx = launchPlanetIdx;
        primaryBall.escapingPlanetGraceTime = launchPlanetIdx !== -1 ? LAUNCH_ESCAPE_GRACE_DURATION : 0;
        primaryBall.isInBunker = false; 
        primaryBall.trail = [{x:shotStartX,y:shotStartY,alpha:1.0}];
        primaryBall.launchPlanetIdx = launchPlanetIdx;
        primaryBall.bouncedPlanetIndices = [];
    }
    
    if (state.teeState.visible && state.shotCount === 1) { 
        state.teeState.flying = true;
        state.teeState.fx = state.teeState.tipX;
        state.teeState.fy = state.teeState.tipY;
        const teeFlyPowerFactor = 0.75; 
        const randomAngleOffset = (Math.random() + 5.5) * 0.1; 
        const randomSpeedFactor = 0.9 + Math.random() * 0.2;  
        const teeLaunchBaseAngle = state.gameState.aimAngle; 
        const teeLaunchAngle = teeLaunchBaseAngle + randomAngleOffset;
        const teeLaunchPowerMagnitude = powerNormalized * SHOT_POWER_MULTIPLIER * teeFlyPowerFactor * randomSpeedFactor;
        state.teeState.fvx = Math.cos(teeLaunchAngle) * teeLaunchPowerMagnitude;
        state.teeState.fvy = Math.sin(teeLaunchAngle) * teeLaunchPowerMagnitude;
        state.teeState.fAngle = teeLaunchAngle; 
        state.teeState.fRotationSpeed = (Math.random() - 0.5) * 8; 
        state.teeState.fLife = 2.5 / GAME_SPEED_MULTIPLIER; 
        state.teeState.visible = false; 
        log("Tee launched!");
    }
    
    if (!state.isButtonZoomActive) state.zoomInteractionEndTime = performance.now(); 
    
    // Set non-grazable planet index for takeoff prevention on all active balls
    state.gameState.balls.forEach(ball => {
        ball.nonGrazablePlanetIdx = -1;
        if (state.gameState.planets) {
            for (let pi = 0; pi < state.gameState.planets.length; pi++) {
                const p = state.gameState.planets[pi];
                const dist = Math.hypot(ball.x - p.x, ball.y - p.y);
                if (dist < p.r + ball.r + 15.0) {
                    ball.nonGrazablePlanetIdx = pi;
                    break;
                }
            }
        }
    });
    
    updateInGameUIDisplay();
}

// --- Bootstrap Actions ---
window.addEventListener('resize', resizeCanvas);

// Bind main loops / context callbacks to physics and UI modules
registerArcadeGameOverCallback(triggerArcadeGameOver);
registerUICallbacks(initGame, loadNextLevel, resetBallToStartLocation, updatePowerUpUIDisplay);

// Export sticker transition to animate return to main menu
export function triggerStickerTransition(onPeak) {
    const transitionOverlay = document.getElementById('stickerTransition');
    const stickerContainer = document.getElementById('stickerContainer');
    if (!transitionOverlay || !stickerContainer) {
        if (onPeak) onPeak();
        return;
    }
    
    // Play synthesized cartoon slap frequency-sweep sound
    playStickerSlap();
    
    // Activate visual overlay via CSS class
    transitionOverlay.classList.add('active');
    
    // Wait for the peak of the slam zoom (350ms) to swap screens
    setTimeout(() => {
        if (onPeak) {
            try {
                onPeak();
            } catch (e) {
                console.error("Transition callback failure:", e);
            }
        }
        
        // Sweep the background music loop back in (high-pass filters back down)
        transitionMusicToMenu();
        
        // Deactivate transition to fade back out
        setTimeout(() => {
            transitionOverlay.classList.remove('active');
        }, 350);
    }, 350);
}

export function hideMultiplayerHUD() {
    const mpTRLeaderboard = document.getElementById('mpTRLeaderboard');
    if (mpTRLeaderboard) mpTRLeaderboard.style.display = 'none';
    const mpRacingHUD = document.getElementById('mpRacingHUD');
    if (mpRacingHUD) mpRacingHUD.style.display = 'none';
}

export function showMainMenu() {
    state.currentPlayingMode = null;
    hideMultiplayerHUD();
    const mainHome = document.getElementById('mainHomeMenu');
    const spSub = document.getElementById('singlePlayerSubmenu');
    const mpSub = document.getElementById('multiplayerSubmenu');
    if (mainHome) mainHome.style.display = 'block';
    if (spSub) spSub.style.display = 'none';
    if (mpSub) mpSub.style.display = 'none';
    
    document.getElementById('startScreen').classList.add('active');
    document.getElementById('powerUpControlsContainer').style.display = 'none';
    document.getElementById('powerUpShopBtn').style.display = 'none';
    
    // Ensure chiptune background music loop transitions back to menu volume
    transitionMusicToMenu();
}

function updateMuteUILabels() {
    const startMuteBtn = document.getElementById('startScreenMuteBtn');
    const startMusicBtn = document.getElementById('startScreenMusicBtn');
    const settingsToggleSound = document.getElementById('toggleSound');
    const settingsToggleMusic = document.getElementById('toggleMusic');

    if (startMuteBtn) {
        startMuteBtn.textContent = state.IS_MUTED ? '🔇' : '🔊';
        if (state.IS_MUTED) {
            startMuteBtn.classList.add('opacity-40');
        } else {
            startMuteBtn.classList.remove('opacity-40');
        }
    }
    if (settingsToggleSound) {
        settingsToggleSound.textContent = state.IS_MUTED ? '🔇 Sound: Off' : '🔊 Sound: On';
    }

    if (startMusicBtn) {
        startMusicBtn.textContent = '🎵';
        if (state.IS_MUSIC_MUTED) {
            startMusicBtn.classList.add('opacity-40');
        } else {
            startMusicBtn.classList.remove('opacity-40');
        }
    }
    if (settingsToggleMusic) {
        settingsToggleMusic.textContent = state.IS_MUSIC_MUTED ? '🔇 Music: Off' : '🎵 Music: On';
    }
}

function updateCRTEffectUI() {
    const startCRTBtn = document.getElementById('startScreenCRTBtn');
    const settingsCRTBtn = document.getElementById('toggleCRT');
    const settingsLabel = state.IS_CRT_ACTIVE ? '📺 CRT: On' : '📺 CRT: Off';
    
    if (startCRTBtn) {
        startCRTBtn.textContent = '📺';
        if (!state.IS_CRT_ACTIVE) {
            startCRTBtn.classList.add('opacity-40');
        } else {
            startCRTBtn.classList.remove('opacity-40');
        }
    }
    if (settingsCRTBtn) settingsCRTBtn.textContent = settingsLabel;
    
    if (state.IS_CRT_ACTIVE) {
        document.documentElement.classList.add('crt-active');
    } else {
        document.documentElement.classList.remove('crt-active');
    }
}

// --- Set up UI event listeners ---
function setupUIEventListeners() {
    // Sync initial UI labels
    updateMuteUILabels();
    updateCRTEffectUI();
    setupPlayerNamePromptModalListeners();

    // Secret Arcade Cheat Click Egg (5 clicks on statsModeBadge unlocks unlimited powerups)
    const statsModeBadge = document.getElementById('statsModeBadge');
    if (statsModeBadge) {
        let clicks = 0;
        let lastClickTime = 0;
        statsModeBadge.style.cursor = 'pointer'; // Make it look clickable
        
        statsModeBadge.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            e.preventDefault();
            
            // Only allow in Arcade / Power-up mode!
            if (state.currentPlayingMode !== 'powerup') return;
            
            const now = performance.now();
            if (now - lastClickTime > 2000) {
                clicks = 0; // reset if idle for 2s
            }
            clicks++;
            lastClickTime = now;
            
            console.log(`🕹️ Arcade cheat click registered! Count: ${clicks}/5`);
            
            // Subtle click sound feedback
            playSound(synth, "C5", "32n", Tone.now(), 0.2);
            
            if (clicks === 5) {
                clicks = 0; // reset
                
                // Unlocks 999 of all celestial items and unlimited space bucks!
                state.totalSpaceBucks = 99999;
                state.ownedPowerUps = {
                    trajectoryLengthener: 999,
                    gravityGrid: 999,
                    stickyBall: 999,
                    linearLaser: 999,
                    chronoRewind: 999,
                    multiBall: 999,
                    oobShield: 999
                };
                
                saveGameData();
                updatePowerUpUIDisplay();
                updatePowerUpShopDisplay();
                updatePowerUpActivationButtons();
                
                // Glorious 8-bit retro cheat fanfare!
                const t = Tone.now();
                playSound(synth, "C5", "16n", t);
                playSound(synth, "E5", "16n", t + 0.08);
                playSound(synth, "G5", "16n", t + 0.16);
                playSound(synth, "C6", "8n", t + 0.24);
                
                // Visual screen announcement
                const banner = document.getElementById('largeLevelAnnouncement');
                const title = document.getElementById('largeLevelTitle');
                const subtitle = document.getElementById('largeLevelPar');
                if (banner && title && subtitle) {
                    title.textContent = "CHEAT ACTIVE";
                    subtitle.textContent = "UNLIMITED CELESTIAL POWER!";
                    banner.style.opacity = '1';
                    banner.style.transform = 'scale(1)';
                    setTimeout(() => {
                        banner.style.opacity = '0';
                    }, 2000);
                }
                
                log("🕹️ Arcade cheat unlocked: 999 of all items and 99999 Space Bucks granted!");
            }
        });
    }

    // Persistent start screen mute toggle button speaker control
    const startMuteBtn = document.getElementById('startScreenMuteBtn');
    if (startMuteBtn) {
        startMuteBtn.addEventListener('click', () => {
            state.IS_MUTED = !state.IS_MUTED;
            localStorage.setItem('isMuted', state.IS_MUTED.toString());
            
            syncMuteState();
            updateMuteUILabels();
            
            if (!state.IS_MUTED) {
                if (state.currentPlayingMode === null) {
                    transitionMusicToMenu();
                }
            } else {
                fadeAndStopMenuMusic();
            }
        });
    }

    // Persistent start screen music toggle button control
    const startMusicBtn = document.getElementById('startScreenMusicBtn');
    if (startMusicBtn) {
        startMusicBtn.addEventListener('click', () => {
            state.IS_MUSIC_MUTED = !state.IS_MUSIC_MUTED;
            localStorage.setItem('isMusicMuted', state.IS_MUSIC_MUTED.toString());
            
            updateMuteUILabels();
            
            if (!state.IS_MUSIC_MUTED) {
                if (state.currentPlayingMode === null) {
                    transitionMusicToMenu();
                }
            } else {
                fadeAndStopMenuMusic();
            }
        });
    }

    const startCRTBtn = document.getElementById('startScreenCRTBtn');
    if (startCRTBtn) {
        startCRTBtn.addEventListener('click', () => {
            state.IS_CRT_ACTIVE = !state.IS_CRT_ACTIVE;
            localStorage.setItem('isCRTActive', state.IS_CRT_ACTIVE.toString());
            updateCRTEffectUI();
            playMenuClickSound();
            playRandomFunPhrase();
        });
    }

    const startPlayerBtn = document.getElementById('startScreenPlayerBtn');
    if (startPlayerBtn) {
        startPlayerBtn.addEventListener('click', () => {
            playMenuClickSound();
            const nameModal = document.getElementById('playerNamePromptModal');
            const nameInput = document.getElementById('playerNamePromptInput');
            if (nameInput) {
                nameInput.value = state.currentPlayerName === "Player" ? "" : state.currentPlayerName;
            }
            if (nameModal) {
                nameModal.classList.add('active');
                if (typeof window.syncColorHighlight === 'function') {
                    window.syncColorHighlight(state.customBallColor, state.customBallSkin, state.customBallTrail);
                }
            }
        });
    }

    const settingsCRTBtn = document.getElementById('toggleCRT');
    if (settingsCRTBtn) {
        settingsCRTBtn.addEventListener('click', () => {
            state.IS_CRT_ACTIVE = !state.IS_CRT_ACTIVE;
            localStorage.setItem('isCRTActive', state.IS_CRT_ACTIVE.toString());
            updateCRTEffectUI();
            playMenuClickSound();
            playRandomFunPhrase();
        });
    }

    document.getElementById('homeSinglePlayerBtn').addEventListener('click', () => {
        playMenuClickSound();
        playVoiceOver("SINGLE PLAYER!");
        document.getElementById('mainHomeMenu').style.display = 'none';
        document.getElementById('singlePlayerSubmenu').style.display = 'block';
    });

    document.getElementById('backToHomeBtn').addEventListener('click', () => {
        playMenuClickSound();
        playVoiceOver("MAIN MENU!");
        document.getElementById('singlePlayerSubmenu').style.display = 'none';
        document.getElementById('mainHomeMenu').style.display = 'block';
    });

    document.getElementById('startGame').addEventListener('click', () => {
        playMenuClickSound();
        ensurePlayerNameRegistered(() => {
            state.currentPlayingMode = 'classic';
            state.isPlayingCustomCourse = false;
            state.currentPlayingCourseDefinition = null;
            initGame();
        });
    });

    document.getElementById('arcadeModeBtn').addEventListener('click', () => {
        playMenuClickSound();
        playVoiceOver("ARCADE MODE!");
        ensurePlayerNameRegistered(() => {
            state.currentPlayingMode = 'powerup'; 
            state.isPlayingCustomCourse = false;
            state.currentPlayingCourseDefinition = null;
            initGame();
        });
    });

    document.getElementById('openShopFromGameBtn').addEventListener('click', () => {
        playMenuClickSound();
        updatePowerUpShopDisplay();
        document.getElementById('closePowerUpShopBtn').textContent = "Close";
        document.getElementById('powerUpShopModal').classList.add('active');
        playSound(openShopSound, "C5", "16n", Tone.now());
        
        // Muffle bitcrushed soundtrack back in during shopping
        transitionMusicToMenu();
    });

    document.getElementById('toggleZoom').addEventListener('click', () => {
        if (state.initialCourseViewActive) {
            state.initialCourseViewActive = false;
        }

        state.isButtonZoomActive = !state.isButtonZoomActive; 
        document.getElementById('toggleZoom').textContent = state.isButtonZoomActive ? 'Follow Ball' : 'Show Course';

        if (state.isButtonZoomActive) { 
            state.viewTargetScale = state.MIN_PLAY_AREA_VIEW_SCALE;
            state.viewTargetCenterX = PLAY_AREA_WIDTH / 2;
            state.viewTargetCenterY = PLAY_AREA_HEIGHT / 2;
        } else { 
            state.viewTargetScale = MAX_VIEW_SCALE;
            const primaryBall = state.gameState.balls[0];
            if (primaryBall) {
                state.viewTargetCenterX = primaryBall.x;
                state.viewTargetCenterY = primaryBall.y;
            } else {
                state.viewTargetCenterX = PLAY_AREA_WIDTH / 2;
                state.viewTargetCenterY = PLAY_AREA_HEIGHT / 2;
            }
        }
        state.viewCurrentScale = state.viewTargetScale;
        state.viewCurrentCenterX = state.viewTargetCenterX;
        state.viewCurrentCenterY = state.viewTargetCenterY;
        
        state.initialCourseViewActive = false;
        state.isInteractingZoom = false; 
        state.isPinching = false;
        state.zoomInteractionEndTime = 0; 
        state.gameState.isAiming = false; 
        log(`Zoom toggled by button (snapped). isButtonZoomActive: ${state.isButtonZoomActive}`);
    });

    document.getElementById('toggleSettings').addEventListener('click', () => {
        playMenuClickSound();
        playVoiceOver("SETTINGS!");
        const settingsPanel = document.getElementById('settingsPanel');
        settingsPanel.classList.toggle('active');
        if (settingsPanel.classList.contains('active')) {
            // Muffle bitcrushed soundtrack back in during settings configuration
            transitionMusicToMenu();
            document.getElementById('playerNameInputSettings').value = state.currentPlayerName;
            if (state.currentPlayingMode === 'powerup') {
                document.getElementById('powerUpShopBtn').style.display = 'block';
            } else {
                document.getElementById('powerUpShopBtn').style.display = 'none';
            }
        } else {
            // Sweep soundtrack back out to space vacuum silence on settings close
            if (state.currentPlayingMode !== null) {
                transitionMusicToGame();
            }
        }
    });

    document.getElementById('closeSettings').addEventListener('click', () => {
        playMenuClickSound();
        playVoiceOver("GAME ON!");
        document.getElementById('settingsPanel').classList.remove('active');
        
        // Sweep soundtrack back out to space vacuum silence on settings close
        if (state.currentPlayingMode !== null) {
            transitionMusicToGame();
        }
    });
    
    document.getElementById('savePlayerNameSettingsBtn').addEventListener('click', () => {
        const newName = document.getElementById('playerNameInputSettings').value;
        if (updatePlayerName(newName)) {
            playRandomFunPhrase();
        }
    });

    document.getElementById('nextLevel').addEventListener('click', () => {
        document.getElementById('goalScreen').classList.remove('active');
        
        // Sweep soundtrack back out to space vacuum silence for next hole
        transitionMusicToGame();
        
        if (state.currentPlayingMode !== 'powerup') { 
            state.totalScore += state.shotCount; 
            if (state.currentHole === state.TOTAL_HOLES - 1) { 
                document.getElementById('gameOverTitle').textContent = "Game Over";
                
                // Toggle Classic-only scorecard rows
                document.getElementById('rowFinalScore').style.display = 'flex';
                document.getElementById('rowFinalBucks').style.display = 'none';

                // Set labels and values
                document.getElementById('lblFinalShots').textContent = "Total Shots";
                document.getElementById('txtFinalShots').textContent = state.totalScore;

                document.getElementById('lblCoursePar').textContent = "Course Par";
                document.getElementById('txtCoursePar').textContent = state.totalCoursePar;

                const scoreDiff = state.totalScore - state.totalCoursePar;
                let scoreText = scoreDiff === 0 ? "E" : (scoreDiff > 0 ? `+${scoreDiff}` : `${scoreDiff}`);
                document.getElementById('txtFinalScore').textContent = scoreText;

                // Calculate and display round efficiency (replacing accuracy)
                const finalEfficiency = state.totalCourseDistance > 0 ? Math.min(100, Math.round((state.totalCourseOptimalDistance / state.totalCourseDistance) * 100)) : 100;
                document.getElementById('txtFinalAccuracy').textContent = `${finalEfficiency}%`;

                document.getElementById('txtOptimalDistance').textContent = `${state.totalCourseOptimalDistance.toFixed(0)} IAU`;
                document.getElementById('txtActualDistance').textContent = `${state.totalCourseDistance.toFixed(0)} IAU`;

                const courseIdToSaveFor = state.isPlayingCustomCourse ? state.currentCustomCourseName : RANDOM_COURSE_ID;
                saveHighScore(courseIdToSaveFor, state.totalScore, state.totalCoursePar, state.totalCourseDistance, state.totalCourseOptimalDistance, state.currentPlayerName);

                // Query rank and display dynamically inside the player stamp using our centralized rank stamp helper
                updateGameOverRankStamp(courseIdToSaveFor, state.totalScore, state.totalCoursePar, state.totalCourseDistance);
                
                const restartBtn = document.getElementById('restartGame');
                restartBtn.textContent = 'Play Again (Classic)';
                document.getElementById('gameOverScreen').classList.add('active');
                
                // Play retro chiptune and vocal "COURSE COMPLETED!" win sound
                triggerWinArcadeSequence();
                
                // Sweep background music back in unmuffled on Course Complete screen
                transitionMusicToMenu();
            } else {
                state.currentHole++;
                loadNextLevel();
            }
        } else { 
            loadNextLevel();
        }
    });

    document.getElementById('restartGame').addEventListener('click', () => { 
        playMenuClickSound();
        if (state.currentPlayingMode === 'powerup') {
            state.isPlayingCustomCourse = false;
            state.currentPlayingCourseDefinition = null;
        } else {
            state.currentPlayingMode = 'classic'; 
            state.isPlayingCustomCourse = false; 
            state.currentPlayingCourseDefinition = null;
        }
        initGame();
    });

    document.getElementById('mainMenu').addEventListener('click', () => {
        playMenuClickSound();
        if (state.currentPlayingMode === 'powerup' && state.totalScore > 0) {
            saveHighScore(ARCADE_MODE_ID, state.totalScore, state.totalCoursePar, state.totalCourseDistance, state.totalCourseOptimalDistance, state.currentPlayerName);
        }
        triggerStickerTransition(() => {
            document.getElementById('goalScreen').classList.remove('active');
            showMainMenu();
            document.getElementById('gameUIGroup').style.display = 'none';
        });
    });

    document.getElementById('gameOverMainMenu').addEventListener('click', () => {
        playMenuClickSound();
        triggerStickerTransition(() => {
            document.getElementById('gameOverScreen').classList.remove('active');
            showMainMenu();
            document.getElementById('gameUIGroup').style.display = 'none';
        });
    });

    document.getElementById('rowLeaderboardRank').addEventListener('click', () => {
        playMenuClickSound();
        triggerStickerTransition(() => {
            document.getElementById('gameOverScreen').classList.remove('active');
            document.getElementById('gameUIGroup').style.display = 'none';

            // Show leaderboard directly based on current mode
            const isArcade = state.currentPlayingMode === 'powerup';
            const finishedMode = isArcade ? 'arcade' : 'classic';
            localStorage.setItem('lastLeaderboardMode', finishedMode);

            // Read other last chosen choices or fall back to defaults
            const lastSync = localStorage.getItem('lastLeaderboardSync') || 'global';
            const lastFilter = localStorage.getItem('lastLeaderboardFilter') || 'daily';
            
            // Set active mode in DOM based on what we just finished
            const btnClassic = document.getElementById('hsModeClassic');
            const btnArcade = document.getElementById('hsModeArcade');
            
            if (isArcade) {
                if (btnClassic) btnClassic.classList.remove('active');
                if (btnArcade) btnArcade.classList.add('active');
            } else {
                if (btnClassic) btnClassic.classList.add('active');
                if (btnArcade) btnArcade.classList.remove('active');
            }

            // Sync type in DOM
            const btnLocal = document.getElementById('hsTypeLocal');
            const btnGlobal = document.getElementById('hsTypeGlobal');
            if (lastSync === 'local') {
                if (btnLocal) btnLocal.classList.add('active');
                if (btnGlobal) btnGlobal.classList.remove('active');
            } else {
                if (btnLocal) btnLocal.classList.remove('active');
                if (btnGlobal) btnGlobal.classList.add('active');
            }

            // Duration tabs in DOM
            const hsTabs = ['All', 'Daily', 'Weekly', 'Monthly'];
            hsTabs.forEach(t => {
                const el = document.getElementById(`hsTab${t}`);
                if (el) {
                    if (t.toLowerCase() === lastFilter) el.classList.add('active');
                    else el.classList.remove('active');
                }
            });

            // Show the "Play Again" button inside the leaderboard modal
            const playAgainBtn = document.getElementById('leaderboardPlayAgainBtn');
            if (playAgainBtn) playAgainBtn.style.display = 'block';

            // Ensure backdrop has transparent theme and open
            const hsModal = document.getElementById('highScoreDisplayModal');
            hsModal.classList.remove('has-home-bg');
            hsModal.classList.add('active');

            // Force cache refresh on open from game over
            setTimeout(() => {
                const modeId = isArcade ? 'Arcade Mode' : 'Random Course';
                syncLocalScoresToGlobal(); // Sync local bests to global first!
                displayHighScores(modeId, lastFilter, lastSync);
            }, 20);
        });
    });

    document.getElementById('settingsMainMenu').addEventListener('click', () => {
        playMenuClickSound();
        triggerStickerTransition(() => {
            document.getElementById('settingsPanel').classList.remove('active');
            showMainMenu();
            document.getElementById('gameUIGroup').style.display = 'none';
        });
    });

    document.getElementById('toggleDebug').addEventListener('click', () => {
        playRandomFunPhrase();
        debugInfo.style.display = debugInfo.style.display === 'none' ? 'block' : 'none';
    });

    document.getElementById('toggleSound').addEventListener('click', () => {
        state.IS_MUTED = !state.IS_MUTED;
        localStorage.setItem('isMuted', state.IS_MUTED.toString());
        updateMuteUILabels();
        
        syncMuteState();
        if (!state.IS_MUTED) {
            if (state.currentPlayingMode === null) {
                transitionMusicToMenu();
            }
        } else {
            fadeAndStopMenuMusic();
        }
        log(state.IS_MUTED ? "Sound Muted" : "Sound Enabled");
    });

    document.getElementById('toggleMusic').addEventListener('click', () => {
        state.IS_MUSIC_MUTED = !state.IS_MUSIC_MUTED;
        localStorage.setItem('isMusicMuted', state.IS_MUSIC_MUTED.toString());
        updateMuteUILabels();
        
        if (!state.IS_MUSIC_MUTED) {
            if (state.currentPlayingMode === null) {
                transitionMusicToMenu();
            }
        } else {
            fadeAndStopMenuMusic();
        }
        log(state.IS_MUSIC_MUTED ? "Music Muted" : "Music Enabled");
    });

    // Obsolete level save/load hooks deleted

    document.getElementById('showHighScoresBtn').addEventListener('click', () => {
        playMenuClickSound();
        ensurePlayerNameRegistered(() => {
            playRandomFunPhrase();
            
            // Read from localStorage with fallback defaults
            // Coming from Main Menu: default mode is arcade (or last saved choice), sync type is always global!
            const lastMode = localStorage.getItem('lastLeaderboardMode') || 'arcade';
            const lastSync = 'global'; // Always global from main menu!
            const lastFilter = localStorage.getItem('lastLeaderboardFilter') || 'daily';

            // Set defaults in DOM: Mode
            const btnClassic = document.getElementById('hsModeClassic');
            const btnArcade = document.getElementById('hsModeArcade');
            if (lastMode === 'arcade') {
                if (btnClassic) btnClassic.classList.remove('active');
                if (btnArcade) btnArcade.classList.add('active');
            } else {
                if (btnClassic) btnClassic.classList.add('active');
                if (btnArcade) btnArcade.classList.remove('active');
            }

            // Set defaults in DOM: Sync
            const btnLocal = document.getElementById('hsTypeLocal');
            const btnGlobal = document.getElementById('hsTypeGlobal');
            if (lastSync === 'local') {
                if (btnLocal) btnLocal.classList.add('active');
                if (btnGlobal) btnGlobal.classList.remove('active');
            } else {
                if (btnLocal) btnLocal.classList.remove('active');
                if (btnGlobal) btnGlobal.classList.add('active');
            }
            
            // Set defaults in DOM: Duration tabs
            const hsTabs = ['All', 'Daily', 'Weekly', 'Monthly'];
            hsTabs.forEach(t => {
                const el = document.getElementById(`hsTab${t}`);
                if (el) {
                    if (t.toLowerCase() === lastFilter) el.classList.add('active');
                    else el.classList.remove('active');
                }
            });

            // Hide the "Play Again" button when opened from the home screen menu
            const playAgainBtn = document.getElementById('leaderboardPlayAgainBtn');
            if (playAgainBtn) playAgainBtn.style.display = 'none';

            // Set background class and open
            const hsModal = document.getElementById('highScoreDisplayModal');
            hsModal.classList.add('has-home-bg');
            hsModal.classList.add('active');
            document.getElementById('startScreen').classList.remove('active'); // Hide main start screen completely

            const modeId = lastMode === 'arcade' ? 'Arcade Mode' : 'Random Course';
            syncLocalScoresToGlobal(); // Sync local bests to global first!
            displayHighScores(modeId, lastFilter, lastSync);
        });
    });

    document.getElementById('closeHighScoreDisplayBtn').addEventListener('click', () => {
        playMenuClickSound();
        triggerStickerTransition(() => {
            const hsModal = document.getElementById('highScoreDisplayModal');
            hsModal.classList.remove('has-home-bg');
            hsModal.classList.remove('active');
            showMainMenu();
        });
    });

    document.getElementById('leaderboardPlayAgainBtn').addEventListener('click', () => {
        playMenuClickSound();
        const hsModal = document.getElementById('highScoreDisplayModal');
        hsModal.classList.remove('has-home-bg');
        hsModal.classList.remove('active');
        
        // Restart the game inside the current mode
        if (state.currentPlayingMode === 'powerup') {
            state.isPlayingCustomCourse = false;
            state.currentPlayingCourseDefinition = null;
        } else {
            state.currentPlayingMode = 'classic';
            state.isPlayingCustomCourse = false;
            state.currentPlayingCourseDefinition = null;
        }
        initGame();
    });

    // Leaderboard duration filter tabs listeners with instant visual repainting
    const hsTabs = ['All', 'Daily', 'Weekly', 'Monthly'];
    hsTabs.forEach(tabName => {
        const btn = document.getElementById(`hsTab${tabName}`);
        if (btn) {
            btn.addEventListener('click', () => {
                playMenuClickSound();
                hsTabs.forEach(t => {
                    const el = document.getElementById(`hsTab${t}`);
                    if (el) el.classList.remove('active');
                });
                btn.classList.add('active');

                // Save selected duration filter
                localStorage.setItem('lastLeaderboardFilter', tabName.toLowerCase());

                // Defer the database fetch slightly to guarantee immediate button color change paint
                setTimeout(() => {
                    displayHighScores(activeHighScoreCourseId, tabName.toLowerCase(), activeHighScoreSyncType);
                }, 20);
            });
        }
    });

    // Leaderboard sync type switcher tabs listeners (Local vs Global)
    const btnLocal = document.getElementById('hsTypeLocal');
    const btnGlobal = document.getElementById('hsTypeGlobal');
    
    if (btnLocal && btnGlobal) {
        btnLocal.addEventListener('click', () => {
            playMenuClickSound();
            btnLocal.classList.add('active');
            btnGlobal.classList.remove('active');
            
            // Save selected sync preference
            localStorage.setItem('lastLeaderboardSync', 'local');

            let currentFilter = 'all';
            hsTabs.forEach(t => {
                const el = document.getElementById(`hsTab${t}`);
                if (el && el.classList.contains('active')) {
                    currentFilter = t.toLowerCase();
                }
            });
            
            setTimeout(() => {
                displayHighScores(activeHighScoreCourseId, currentFilter, 'local');
            }, 20);
        });
        
        btnGlobal.addEventListener('click', () => {
            playMenuClickSound();
            btnGlobal.classList.add('active');
            btnLocal.classList.remove('active');
            
            // Save selected sync preference
            localStorage.setItem('lastLeaderboardSync', 'global');

            let currentFilter = 'all';
            hsTabs.forEach(t => {
                const el = document.getElementById(`hsTab${t}`);
                if (el && el.classList.contains('active')) {
                    currentFilter = t.toLowerCase();
                }
            });
            
            setTimeout(() => {
                displayHighScores(activeHighScoreCourseId, currentFilter, 'global');
            }, 20);
        });
    }

    // Leaderboard game mode switcher listeners (Classic vs Arcade)
    const btnClassic = document.getElementById('hsModeClassic');
    const btnArcade = document.getElementById('hsModeArcade');

    if (btnClassic && btnArcade) {
        btnClassic.addEventListener('click', () => {
            playMenuClickSound();
            btnClassic.classList.add('active');
            btnArcade.classList.remove('active');

            // Save selected mode preference
            localStorage.setItem('lastLeaderboardMode', 'classic');

            let currentFilter = 'all';
            hsTabs.forEach(t => {
                const el = document.getElementById(`hsTab${t}`);
                if (el && el.classList.contains('active')) {
                    currentFilter = t.toLowerCase();
                }
            });

            setTimeout(() => {
                displayHighScores('Random Course', currentFilter, activeHighScoreSyncType);
            }, 20);
        });

        btnArcade.addEventListener('click', () => {
            playMenuClickSound();
            btnArcade.classList.add('active');
            btnClassic.classList.remove('active');

            // Save selected mode preference
            localStorage.setItem('lastLeaderboardMode', 'arcade');

            let currentFilter = 'all';
            hsTabs.forEach(t => {
                const el = document.getElementById(`hsTab${t}`);
                if (el && el.classList.contains('active')) {
                    currentFilter = t.toLowerCase();
                }
            });

            setTimeout(() => {
                displayHighScores('Arcade Mode', currentFilter, activeHighScoreSyncType);
            }, 20);
        });
    }

    // Obsolete custom course creation listeners deleted

    document.getElementById('powerUpShopBtn').addEventListener('click', () => {
        playMenuClickSound();
        updatePowerUpShopDisplay();
        document.getElementById('closePowerUpShopBtn').textContent = "Close";
        document.getElementById('powerUpShopModal').classList.add('active');
        document.getElementById('settingsPanel').classList.remove('active');
        playSound(openShopSound, "C5", "16n", Tone.now());
        
        // Muffle bitcrushed soundtrack back in during shopping
        transitionMusicToMenu();
    });

    const goalShopBtn = document.getElementById('goalScreenShopBtn');
    if (goalShopBtn) {
        goalShopBtn.addEventListener('click', () => {
            playMenuClickSound();
            state.openedShopFromGoalScreen = true;
            document.getElementById('goalScreen').classList.remove('active');
            updatePowerUpShopDisplay();
            
            // Set button to say "Next Hole" since closing will immediately advance to the next level
            document.getElementById('closePowerUpShopBtn').textContent = "Next Hole";
            
            document.getElementById('powerUpShopModal').classList.add('active');
            playSound(openShopSound, "C5", "16n", Tone.now());
            
            // Muffle bitcrushed soundtrack back in during shopping
            transitionMusicToMenu();
        });
    }

    document.getElementById('closePowerUpShopBtn').addEventListener('click', () => {
        playMenuClickSound();
        playVoiceOver("GAME ON!");
        document.getElementById('powerUpShopModal').classList.remove('active');
        
        if (state.openedShopFromGoalScreen) {
            state.openedShopFromGoalScreen = false;
            // Sweep soundtrack back out to space vacuum silence for next hole
            transitionMusicToGame();
            loadNextLevel();
        } else {
            // Sweep soundtrack back out to space vacuum silence on shop close
            if (state.currentPlayingMode !== null) {
                transitionMusicToGame();
            }
        }
        
        // Reset label back to default Close
        document.getElementById('closePowerUpShopBtn').textContent = "Close";
    });

    // Shop purchase buttons
    document.getElementById('buyTrajectoryLengthenerBtn').addEventListener('click', () => buyPowerUp('trajectoryLengthener'));
    document.getElementById('buyGravityGridBtn').addEventListener('click', () => buyPowerUp('gravityGrid'));
    document.getElementById('buyStickyBallBtn').addEventListener('click', () => buyPowerUp('stickyBall'));
    document.getElementById('buyLinearLaserBtn').addEventListener('click', () => buyPowerUp('linearLaser'));
    document.getElementById('buyChronoRewindBtn').addEventListener('click', () => buyPowerUp('chronoRewind'));
    document.getElementById('buyMultiBallBtn').addEventListener('click', () => buyPowerUp('multiBall'));
    document.getElementById('buyOobShieldBtn').addEventListener('click', () => buyPowerUp('oobShield'));

    // Power-up activation buttons
    document.getElementById('activateTrajectoryLengthenerBtn').addEventListener('click', () => togglePowerUp('trajectoryLengthener'));
    document.getElementById('activateGravityGridBtn').addEventListener('click', () => togglePowerUp('gravityGrid'));
    document.getElementById('activateStickyBallBtn').addEventListener('click', () => togglePowerUp('stickyBall'));
    document.getElementById('activateLinearLaserBtn').addEventListener('click', () => togglePowerUp('linearLaser'));
    document.getElementById('activateMultiBallBtn').addEventListener('click', () => togglePowerUp('multiBall'));
    document.getElementById('activateChronoRewindBtn').addEventListener('click', () => togglePowerUp('chronoRewind'));
    document.getElementById('activateOobShieldBtn').addEventListener('click', () => togglePowerUp('oobShield'));
    // Leaderboard refresh button listener
    const refreshBtn = document.getElementById('hsRefreshBtn');
    if (refreshBtn) {
        refreshBtn.addEventListener('click', () => {
            playMenuClickSound();
            
            // Spin animation
            refreshBtn.classList.add('animate-spin');
            setTimeout(() => refreshBtn.classList.remove('animate-spin'), 1000);
            
            // Invalidate cache completely to force fresh reload from proxy
            invalidateLeaderboardCache();
            
            // Determine active filter and mode
            let currentFilter = 'all';
            const hsTabs = ['All', 'Daily', 'Weekly', 'Monthly'];
            hsTabs.forEach(t => {
                const el = document.getElementById(`hsTab${t}`);
                if (el && el.classList.contains('active')) {
                    currentFilter = t.toLowerCase();
                }
            });
            
            syncLocalScoresToGlobal(); // Sync local bests to global first!
            displayHighScores(activeHighScoreCourseId, currentFilter, activeHighScoreSyncType);
        });
    }
}

// --- Star, touch, mouse, and pinch canvas attachments ---
canvas.addEventListener('mousedown', handleMouseDown);
canvas.addEventListener('mousemove', handleMouseMove);
canvas.addEventListener('mouseup', handleMouseUp);
canvas.addEventListener('touchstart', handleMouseDown, {passive:false});
canvas.addEventListener('touchmove', handleMouseMove, {passive:false});
canvas.addEventListener('touchend', handleMouseUp, {passive:false});
canvas.addEventListener('touchcancel', handleMouseUp, {passive:false});

canvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    if (state.gameState.multiBallActiveOnScreen || state.isButtonZoomActive) return; 
    if (state.initialCourseViewActive) { state.initialCourseViewActive = false; }

    const pos = getMousePos(canvas, e);
    const worldXBeforeZoom = (pos.x - (canvas.width/2 - state.viewCurrentCenterX * state.viewCurrentScale)) / state.viewCurrentScale;
    const worldYBeforeZoom = (pos.y - (canvas.height/2 - state.viewCurrentCenterY * state.viewCurrentScale)) / state.viewCurrentScale;

    const zoomDelta = e.deltaY * SCROLL_ZOOM_SENSITIVITY;
    const oldScale = state.viewTargetScale;
    state.viewTargetScale = clamp(state.viewTargetScale * (1 - zoomDelta), state.MIN_PLAY_AREA_VIEW_SCALE, MAX_VIEW_SCALE);
    
    const scaleChange = state.viewTargetScale / oldScale;
    state.viewTargetCenterX = worldXBeforeZoom - (worldXBeforeZoom - state.viewTargetCenterX) / scaleChange;
    state.viewTargetCenterY = worldYBeforeZoom - (worldYBeforeZoom - state.viewTargetCenterY) / scaleChange;
    
    const primaryBallRef = state.gameState.balls[0];
    if (primaryBallRef && state.gameState.planets.length > 0 && state.gameState.goal.planetIndex < state.gameState.planets.length) {
        const goalPlanet = state.gameState.planets[state.gameState.goal.planetIndex];
        const goalHoleX = goalPlanet.x + goalPlanet.r * Math.cos(state.gameState.goal.angleOnPlanet);
        const goalHoleY = goalPlanet.y + goalPlanet.r * Math.sin(state.gameState.goal.angleOnPlanet);

        const minX = Math.min(primaryBallRef.x - primaryBallRef.r*2, goalHoleX - state.gameState.goal.holeRadius*2);
        const maxX = Math.max(primaryBallRef.x + primaryBallRef.r*2, goalHoleX + state.gameState.goal.holeRadius*2);
        const minY = Math.min(primaryBallRef.y - primaryBallRef.r*2, goalHoleY - state.gameState.goal.holeRadius*2);
        const maxY = Math.max(primaryBallRef.y + primaryBallRef.r*2, goalHoleY + state.gameState.goal.holeRadius*2);
        
        const requiredWidthBG = (maxX - minX) * 1.2; 
        const requiredHeightBG = (maxY - minY) * 1.2;
        const scaleToSeeBGX = canvas.width / requiredWidthBG;
        const scaleToSeeBGY = canvas.height / requiredHeightBG;
        const scaleToSeeBG = Math.min(scaleToSeeBGX, scaleToSeeBGY);

        if (state.viewTargetScale < scaleToSeeBG * 1.1 && oldScale > state.viewTargetScale) { 
            state.viewTargetCenterX = (minX + maxX) / 2;
            state.viewTargetCenterY = (minY + maxY) / 2;
        }
    }
    
    state.isInteractingZoom = true; 
    state.lastScrollEventTime = performance.now();
    state.zoomInteractionEndTime = Infinity; 
    state.gameState.isAiming = false; 

    log(`Wheel zoom. Target Scale: ${state.viewTargetScale.toFixed(2)}, Center: (${state.viewTargetCenterX.toFixed(0)}, ${state.viewTargetCenterY.toFixed(0)})`);
}, { passive: false });

let lastTouchDistance = 0;
canvas.addEventListener('touchstart', (e) => {
    if (e.touches.length === 2) {
        e.preventDefault();
        if (state.gameState.multiBallActiveOnScreen || state.isButtonZoomActive) return;
        if (state.initialCourseViewActive) { state.initialCourseViewActive = false; }

        state.isPinching = true;
        state.isInteractingZoom = true; 
        state.zoomInteractionEndTime = Infinity; 
        state.gameState.isAiming = false; 

        const t1 = e.touches[0], t2 = e.touches[1];
        lastTouchDistance = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
        state.pinchStartScale = state.viewTargetScale;

        const pinchCenterXClient = (t1.clientX + t2.clientX) / 2;
        const pinchCenterYClient = (t1.clientY + t2.clientY) / 2;
        const pinchCenterCanvas = getMousePos(canvas, { clientX: pinchCenterXClient, clientY: pinchCenterYClient });
        
        const currentOffsetX = canvas.width / 2 - state.viewCurrentCenterX * state.viewCurrentScale;
        const currentOffsetY = canvas.height / 2 - state.viewCurrentCenterY * state.viewCurrentScale;
        
        state.viewTargetCenterX = (pinchCenterCanvas.x - currentOffsetX) / state.viewCurrentScale;
        state.viewTargetCenterY = (pinchCenterCanvas.y - currentOffsetY) / state.viewCurrentScale;
        
        log("Pinch zoom started.");
    }
}, { passive: false });

canvas.addEventListener('touchmove', (e) => {
    if (state.isPinching && e.touches.length === 2) {
        e.preventDefault();
        if (state.gameState.multiBallActiveOnScreen || state.isButtonZoomActive) return;

        const t1 = e.touches[0], t2 = e.touches[1];
        const currentTouchDistance = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
        const zoomFactor = currentTouchDistance / lastTouchDistance;
        
        const oldScale = state.viewTargetScale;
        state.viewTargetScale = clamp(state.pinchStartScale * zoomFactor, state.MIN_PLAY_AREA_VIEW_SCALE, MAX_VIEW_SCALE);
        
        const pinchCenterXClient = (t1.clientX + t2.clientX) / 2;
        const pinchCenterYClient = (t1.clientY + t2.clientY) / 2;
        const pinchCenterCanvas = getMousePos(canvas, { clientX: pinchCenterXClient, clientY: pinchCenterYClient });

        const currentOffsetX = canvas.width / 2 - state.viewCurrentCenterX * state.viewCurrentScale; 
        const currentOffsetY = canvas.height / 2 - state.viewCurrentCenterY * state.viewCurrentScale;
        const worldPinchX = (pinchCenterCanvas.x - currentOffsetX) / state.viewCurrentScale;
        const worldPinchY = (pinchCenterCanvas.y - currentOffsetY) / state.viewCurrentScale;
        
        state.viewTargetCenterX = worldPinchX - (pinchCenterCanvas.x - canvas.width / 2) / state.viewTargetScale;
        state.viewTargetCenterY = worldPinchY - (pinchCenterCanvas.y - canvas.height / 2) / state.viewTargetScale;

        const primaryBallRef = state.gameState.balls[0];
        if (primaryBallRef && state.gameState.planets.length > 0 && state.gameState.goal.planetIndex < state.gameState.planets.length) {
            const goalPlanet = state.gameState.planets[state.gameState.goal.planetIndex];
            const goalHoleX = goalPlanet.x + goalPlanet.r * Math.cos(state.gameState.goal.angleOnPlanet);
            const goalHoleY = goalPlanet.y + goalPlanet.r * Math.sin(state.gameState.goal.angleOnPlanet);

            const minX = Math.min(primaryBallRef.x - primaryBallRef.r*2, goalHoleX - state.gameState.goal.holeRadius*2);
            const maxX = Math.max(primaryBallRef.x + primaryBallRef.r*2, goalHoleX + state.gameState.goal.holeRadius*2);
            const minY = Math.min(primaryBallRef.y - primaryBallRef.r*2, goalHoleY - state.gameState.goal.holeRadius*2);
            const maxY = Math.max(primaryBallRef.y + primaryBallRef.r*2, goalHoleY + state.gameState.goal.holeRadius*2);
            
            const requiredWidthBG = (maxX - minX) * 1.2; 
            const requiredHeightBG = (maxY - minY) * 1.2;
            const scaleToSeeBGX = canvas.width / requiredWidthBG;
            const scaleToSeeBGY = canvas.height / requiredHeightBG;
            const scaleToSeeBG = Math.min(scaleToSeeBGX, scaleToSeeBGY);

            if (state.viewTargetScale < scaleToSeeBG * 1.1 && oldScale > state.viewTargetScale) { 
                state.viewTargetCenterX = (minX + maxX) / 2;
                state.viewTargetCenterY = (minY + maxY) / 2;
            }
        }
    }
}, { passive: false });

canvas.addEventListener('touchend', () => {
    if (state.isPinching) {
        state.isPinching = false;
        state.isInteractingZoom = false; 
        state.zoomInteractionEndTime = performance.now(); 
        log("Pinch zoom ended. Auto-return initiated.");
    }
});

// Sync tab visibility/focus for robust multiplayer AFK behavior
window.addEventListener('blur', () => {
    if (state.currentPlayingMode === 'multiplayer') {
        state.isTabBlurred = true;
        log("Tab blurred: Multiplayer background mode active.");
    }
});

window.addEventListener('focus', () => {
    if (state.currentPlayingMode === 'multiplayer') {
        state.isTabBlurred = false;
        state.mpRecentlyRefocusedTimer = 30; // 0.5s grace period to sync from active peer
        log("Tab focused: Regained focus in multiplayer, initiated grace sync period.");
    }
});

// Dynamic UI Component injection removed per streamlining requirements

export function playFinishedTriumphSound() {
    const now = Tone.now();
    playSound(synth, "C4", "16n", now);
    playSound(synth, "E4", "16n", now + 0.05);
    playSound(synth, "G4", "16n", now + 0.1);
    playSound(synth, "C5", "16n", now + 0.15);
    playSound(synth, "E5", "16n", now + 0.2);
    playSound(synth, "G5", "8n", now + 0.25);
    playSound(synth, "C6", "4n", now + 0.35);
}

export function showMultiplayerToast(message, colorClass = "text-cyan-400 border-cyan-500/30") {
    const container = document.getElementById('mpToastContainer');
    if (!container) return;
    
    const toast = document.createElement('div');
    toast.className = `p-3 bg-gray-950/95 border ${colorClass} rounded-lg shadow-2xl flex items-center justify-between text-xs font-orbitron font-semibold tracking-wider text-center pointer-events-auto transform translate-y-4 opacity-0 transition-all duration-300 w-full`;
    toast.innerHTML = `<span class="w-full">${message}</span>`;
    
    container.appendChild(toast);
    
    // Animate in
    requestAnimationFrame(() => {
        toast.classList.remove('translate-y-4', 'opacity-0');
    });
    
    // Play a notify click sound locally!
    playSound(synth, "G4", "16n", Tone.now(), 0.35);
    
    // Animate out and remove after 4.5 seconds
    setTimeout(() => {
        toast.classList.add('-translate-y-4', 'opacity-0');
        setTimeout(() => {
            toast.remove();
        }, 300);
    }, 4500);
}

// --- Cosmic Rank Trophies & Player Colors Helpers ---
export function getRankIndicator(rank) {
    const rankTrophies = {
        1: "🏆", // Gold Cup
        2: "🥈", // Silver Medal
        3: "🥉", // Bronze Medal
        4: "🏅", // Sports Medal
        5: "🎖️", // Honorary Medal
        6: "⭐",  // Star
        7: "✨",  // Sparkles
        8: "⚡",  // Lightning
        9: "☄️"   // Comet
    };
    return rankTrophies[rank] || "🏅";
}

export function getSlotColorClass(playerId) {
    const slotColors = [
        'text-rose-400',   // YOU (0)
        'text-green-400',  // Bot 1 (1) (Lime)
        'text-cyan-400',   // Bot 2 (2) (Cyan)
        'text-pink-400',   // Bot 3 (3) (Neon Pink)
        'text-amber-400',  // Bot 4 (4) (Gold)
        'text-orange-400', // Bot 5 (5) (Orange)
        'text-purple-400', // Bot 6 (6) (Purple)
        'text-teal-400',   // Bot 7 (7) (Teal)
        'text-indigo-400'  // Bot 8 (8) (Indigo)
    ];
    return slotColors[playerId % slotColors.length] || 'text-purple-400';
}

export function updateMPLeaderboardHUD() {
    if (state.currentPlayingMode !== 'multiplayer') return;
    
    // Sync local player variables dynamically inside mpState.players list
    const localPlayer = mpState.players.find(p => p.id === mpState.playerId);
    if (localPlayer) {
        localPlayer.holeIndex = state.currentHoleIndex;
        localPlayer.strokes = state.totalMultiplayerStrokes;
        localPlayer.finished = state.multiplayerLocalFinished;
        localPlayer.completedAll = state.multiplayerLocalCompletedAll;
    }
    
    const statsHoleDisplay = document.getElementById('statsHoleDisplay');
    const localCurrentShots = document.getElementById('mpLocalCurrentShots');
    const localScoreVal = document.getElementById('mpLocalScoreVal');
    const localHoleLabel = document.getElementById('mpLocalHoleLabel');
    const opponentsContainer = document.getElementById('mpOpponentsHUDContainer');
    
    const mpTRLeaderboard = document.getElementById('mpTRLeaderboard');
    const mpTRSpectateLabel = document.getElementById('mpTRSpectateLabel');
    const opponentsContainerRight = document.getElementById('mpOpponentsHUDContainerRight');
    
    const holeText = state.currentHoleIndex + 1 === 9 ? "FINAL HOLE" : `HOLE ${state.currentHoleIndex + 1}/9`;
    
    if (statsHoleDisplay) {
        statsHoleDisplay.textContent = holeText;
    }
    
    if (localCurrentShots) localCurrentShots.textContent = state.shotCount;
    
    const currentLocalStrokes = state.totalMultiplayerStrokes + state.shotCount;
    if (localScoreVal) localScoreVal.textContent = state.multiplayerLocalFinished ? state.totalMultiplayerStrokes : currentLocalStrokes;
    
    if (localHoleLabel) {
        if (state.multiplayerLocalFinished) {
            localHoleLabel.textContent = `(FIN)`;
        } else {
            localHoleLabel.textContent = `(H${state.currentHoleIndex + 1})`;
        }
    }
    
    if (opponentsContainer) {
        opponentsContainer.innerHTML = '';
    }
    
    if (mpTRLeaderboard) {
        mpTRLeaderboard.style.display = 'flex';
    }
    
    // Spectating header label
    if (mpTRSpectateLabel) {
        if (state.multiplayerLocalFinished) {
            const activeSpec = mpState.players.find(p => p.id === state.spectatingPlayerId);
            const specName = activeSpec ? activeSpec.name.toUpperCase() : "LOBBY";
            mpTRSpectateLabel.textContent = `📺 SPECTATING: ${specName}`;
            mpTRSpectateLabel.style.display = 'block';
        } else {
            mpTRSpectateLabel.style.display = 'none';
        }
    }
    
    if (opponentsContainerRight) {
        // Dynamically compute real-time competitive rankings for all active players
        const sortedRankings = [...mpState.players].map(p => {
            return { 
                id: p.id, 
                finished: p.finished, 
                completedAll: p.completedAll, 
                finalTime: p.finalTime || 0, 
                finalStrokes: p.finalStrokes || 0, 
                holeIndex: p.holeIndex
            };
        }).sort((a, b) => {
            if (a.finished && !b.finished) return -1;
            if (!a.finished && b.finished) return 1;
            if (a.finished && b.finished) {
                if (a.finalTime !== b.finalTime) return a.finalTime - b.finalTime;
                return a.finalStrokes - b.finalStrokes;
            }
            if (a.holeIndex !== b.holeIndex) return b.holeIndex - a.holeIndex; // further hole index is better
            
            // Stable secondary sort when on the same hole: total strokes (lower is better)
            const pA = mpState.players.find(pl => pl.id === a.id);
            const pB = mpState.players.find(pl => pl.id === b.id);
            const strokesA = pA ? (pA.strokes + (pA.currentHoleStrokes || 0)) : 0;
            const strokesB = pB ? (pB.strokes + (pB.currentHoleStrokes || 0)) : 0;
            if (strokesA !== strokesB) return strokesA - strokesB;
            
            return a.id - b.id; // stable fallback by player ID
        });

        // Clean up any row that is no longer present in sortedRankings to prevent stale DOM nodes
        const activeIds = sortedRankings.map(item => `mp-hud-row-${item.id}`);
        Array.from(opponentsContainerRight.children).forEach(child => {
            if (!activeIds.includes(child.id)) {
                child.remove();
            }
        });
        
        // Loop through all ranked players to build/update the Top-Right interactive scoreboard in-place
        sortedRankings.forEach((item, index) => {
            const p = mpState.players.find(pl => pl.id === item.id);
            if (!p) return;
            
            const isLocal = p.id === mpState.playerId;
            const slotColor = getSlotColorClass(p.id);
            
            let displayName = "PLAYER";
            if (isLocal) {
                displayName = "YOU";
            } else if (p.name) {
                displayName = p.name.length > 8 ? p.name.substring(0, 8) + ".." : p.name;
            }
            
            const holeStatus = p.finished ? 'FIN' : `H${p.holeIndex + 1}`;
            
            // Calculate total display strokes: previous holes strokes + current hole live in-progress strokes
            let displayStrokes = 0;
            if (isLocal) {
                displayStrokes = state.multiplayerLocalFinished ? state.totalMultiplayerStrokes : (state.totalMultiplayerStrokes + state.shotCount);
            } else {
                displayStrokes = p.finished ? p.finalStrokes : (p.strokes + (p.currentHoleStrokes || 0));
            }
            
            const isSpectated = state.spectatingPlayerId === p.id && state.multiplayerLocalFinished;
            
            // Find or create the button element to keep click event handlers alive (never destroy active buttons)
            const rowId = `mp-hud-row-${p.id}`;
            let row = document.getElementById(rowId);
            let isNew = false;
            if (!row) {
                row = document.createElement('button');
                row.id = rowId;
                row.style.outline = 'none';
                isNew = true;
            }
            
            let borderStyle = 'hover:bg-white/5 border-transparent';
            if (isSpectated) {
                borderStyle = 'bg-cyan-500/15 border-r-2 border-cyan-400 shadow-[inset_0_0_8px_rgba(6,182,212,0.15)]';
            } else if (isLocal) {
                borderStyle = 'bg-green-500/10 border-l-2 border-green-400 shadow-[inset_0_0_8px_rgba(34,197,94,0.1)]';
            }
            
            const hasFlash = row.classList.contains('animate-hud-flash') ? ' animate-hud-flash' : '';
            row.className = `flex items-center justify-between px-2.5 py-1 text-right rounded transition-all duration-200 pointer-events-auto w-full select-none ${borderStyle}${hasFlash}`;
            
            // Determine active real-time Rank Trophy
            const rank = sortedRankings.findIndex(item => item.id === p.id) + 1;
            const rankIndicator = getRankIndicator(rank);
            
            const isSpectatedIndicator = isSpectated ? `<span class="w-1.5 h-1.5 rounded-full bg-cyan-400 mr-1 shadow-[0_0_8px_#06b6d4]"></span>` : '';
            const isLocalIndicator = isLocal ? `<span class="w-1.5 h-1.5 rounded-full bg-green-400 mr-1 shadow-[0_0_8px_#22c55e]"></span>` : '';
            
            const newHTML = `
                <div class="flex items-center space-x-1.5 text-left">
                    ${isSpectatedIndicator || isLocalIndicator}
                    <span class="text-xs mr-0.5">${rankIndicator}</span>
                    <span class="text-[10px] text-gray-300 font-semibold tracking-wide">${displayName.toUpperCase()}</span>
                </div>
                <div class="flex items-center space-x-2">
                    <span class="font-bold text-[11px] text-gray-400">${displayStrokes}</span>
                    <span class="font-bold text-[11px] ${slotColor} uppercase">${holeStatus}</span>
                </div>
            `;
            
            // Only update innerHTML if it has changed to prevent DOM thrashing
            if (row.innerHTML !== newHTML) {
                row.innerHTML = newHTML;
            }
            
            // Bind click event directly on the row on every update to capture current closures cleanly
            row.onclick = () => {
                const latestP = mpState.players.find(pl => pl.id === p.id);
                if (!latestP) return;
                const isLocalFinished = state.multiplayerLocalFinished || (mpState.players.find(pl => pl.id === mpState.playerId)?.finished);
                if (isLocalFinished && latestP.id !== mpState.playerId && !latestP.finished) {
                    state.spectatingPlayerId = latestP.id;
                    
                    // Instantly sync the hole index to the spectated bot's active hole to force level loading
                    if (state.currentHoleIndex !== latestP.holeIndex) {
                        state.currentHoleIndex = latestP.holeIndex;
                        loadNextLevel();
                    }
                    
                    updateMPLeaderboardHUD(); // Instantly update active spectated neon highlights
                    console.log(`[Spectator] Switched spectator target to ${latestP.name} (ID: ${latestP.id}) on hole ${latestP.holeIndex + 1}`);
                }
            };
            
            // Adjust cursor style in-place based on spectator status (cannot spectate finished competitors)
            if (state.multiplayerLocalFinished && !isLocal && !p.finished) {
                row.classList.remove('cursor-default');
                row.classList.add('cursor-pointer');
                row.classList.remove('opacity-60');
            } else {
                row.classList.remove('cursor-pointer');
                row.classList.add('cursor-default');
                if (!isLocal) {
                    row.classList.add('opacity-60');
                } else {
                    row.classList.remove('opacity-60');
                }
            }
            
            // Only append or insert if it's not already in the correct position to avoid DOM reflow and jiggling!
            if (opponentsContainerRight.children[index] !== row) {
                const refNode = opponentsContainerRight.children[index];
                if (refNode) {
                    opponentsContainerRight.insertBefore(row, refNode);
                } else {
                    opponentsContainerRight.appendChild(row);
                }
            }
        });
    }
}

export function triggerHUDHitFlash(playerId) {
    const rowId = `mp-hud-row-${playerId}`;
    const row = document.getElementById(rowId);
    if (row) {
        row.classList.remove('animate-hud-flash');
        void row.offsetWidth; // Force reflow to restart CSS animation
        row.classList.add('animate-hud-flash');
        setTimeout(() => {
            row.classList.remove('animate-hud-flash');
        }, 400);
    }
}
window.triggerHUDHitFlash = triggerHUDHitFlash;

export function startMultiplayerMatch() {
    // Set multiplayer variables
    state.currentPlayingMode = 'multiplayer';
    state.isPlayingCustomCourse = false;
    state.currentPlayingCourseDefinition = null;
    state.currentHoleIndex = 0;
    state.shotCount = 0;
    state.totalMultiplayerStrokes = 0;
    state.multiplayerLocalFinished = false;
    state.multiplayerLocalCompletedAll = false;
    
    mpState.startTime = performance.now();
    mpState.endTime = 0;
    
    // Reset each player's in-game metrics for the speedrun
    mpState.players.forEach(p => {
        p.finished = false;
        p.completedAll = false;
        p.strokes = 0;
        p.holeIndex = 0;
        p.finalStrokes = 0;
        p.finalTime = 0;
        p.ballState = {
            x: 0,
            y: 0,
            vx: 0,
            vy: 0,
            r: 8,
            isSinking: false,
            colorR: p.ballState?.colorR || 255,
            colorG: p.ballState?.colorG || 255,
            colorB: p.ballState?.colorB || 255,
            skin: p.ballState?.skin || 'neon',
            trailEffect: p.ballState?.trailEffect || 'neon',
            trail: []
        };
    });
    
    // Load first level
    loadNextLevel();
    
    // Hide main menu / Start game view
    document.getElementById('startScreen').classList.remove('active');
    document.getElementById('gameUIGroup').style.display = 'block';
    
    // Show Multiplayer Live HUD (part of statsCard)
    updateMPLeaderboardHUD();
    
    checkAudioContext();
    transitionMusicToGame();
    
    // Boot up the canvas animation rendering frames & hide other modal dialogs cleanly
    initGame(true);
}

export function checkAndResolveMPFinish() {
    const activePlayers = mpState.players;
    const unfinishedPlayers = activePlayers.filter(p => !p.finished);

    console.log(`Checking match resolution. Active: ${activePlayers.length}, Unfinished: ${unfinishedPlayers.length}`);

    // If only one player remains unfinished, auto-place them in the last spot
    if (activePlayers.length >= 2 && unfinishedPlayers.length === 1) {
        const lastPlayer = unfinishedPlayers[0];
        console.log(`Only one player remaining unfinished: ${lastPlayer.name || lastPlayer.id}. Auto-placing in last spot!`);
        
        lastPlayer.finished = true;
        lastPlayer.completedAll = false;
        lastPlayer.finalStrokes = lastPlayer.strokes;
        lastPlayer.finalTime = Infinity; // DNF
        
        // If the last remaining player is the local player:
        if (lastPlayer.id === mpState.playerId && !state.multiplayerLocalFinished) {
            state.multiplayerLocalFinished = true;
            state.multiplayerLocalCompletedAll = false;
            state.totalMultiplayerStrokes += state.shotCount;
            state.shotCount = 0; // reset for safety
            
            // Hide local ball
            const primaryBall = state.gameState.balls[0];
            if (primaryBall) {
                primaryBall.moving = false;
                primaryBall.x = -9999;
                primaryBall.y = -9999;
            }
        }
    }
    
    if (!mpState.endTime && activePlayers.every(p => p.finished)) {
        mpState.endTime = performance.now();
    }
    
    checkMPMatchFinished();
}

function checkMPMatchFinished() {
    const activePlayers = mpState.players;
    const allFinished = activePlayers.length > 0 && activePlayers.every(p => p.finished);
    
    if (allFinished) {
        // Hide gameplay and show scoreboard modal!
        document.getElementById('gameUIGroup').style.display = 'none';
        
        // Sort: Completed All (fastest time) -> Strokes -> DNF (furthest hole index) -> Strokes
        const sortedPlayers = [...activePlayers].sort((a, b) => {
            if (a.completedAll && !b.completedAll) return -1;
            if (!a.completedAll && b.completedAll) return 1;
            
            if (a.completedAll && b.completedAll) {
                if (a.finalTime !== b.finalTime) {
                    return a.finalTime - b.finalTime;
                }
                return a.finalStrokes - b.finalStrokes;
            } else {
                if (a.holeIndex !== b.holeIndex) {
                    return b.holeIndex - a.holeIndex; // further hole first
                }
                return a.strokes - b.strokes; // fewer strokes first
            }
        });
        
        const localPlayerIndex = sortedPlayers.findIndex(p => p.id === mpState.playerId);
        const won = localPlayerIndex === 0;
        
        const winnerBanner = document.getElementById('mpFinalWinnerBanner');
        if (won) {
            winnerBanner.textContent = "👑 YOU WIN! 👑";
            winnerBanner.className = "text-2xl font-black text-center py-4 bg-green-500 bg-opacity-10 border border-green-500 border-opacity-30 rounded-lg text-green-400 mb-6 tracking-wide animate-pulse";
        } else {
            const winnerName = sortedPlayers[0]?.name || "ANOTHER PLAYER";
            winnerBanner.textContent = `${winnerName.toUpperCase()} WINS! 🛸`;
            winnerBanner.className = "text-2xl font-black text-center py-4 bg-rose-500 bg-opacity-10 border border-rose-500 border-opacity-30 rounded-lg text-rose-400 mb-6 tracking-wide";
        }
        
        const scoreboardContainer = document.getElementById('mpGameOverScoreboard');
        if (scoreboardContainer) {
            scoreboardContainer.innerHTML = '';
            
            sortedPlayers.forEach((p, index) => {
                const isLocal = p.id === mpState.playerId;
                const rank = index + 1;
                const rankIndicator = getRankIndicator(rank);
                const slotColor = getSlotColorClass(p.id);
                const borderColor = isLocal ? 'border-green-500 border-opacity-40' : 'border-gray-800';
                const bgColor = isLocal ? 'bg-green-950/20' : 'bg-gray-900/80';
                
                let performanceText = '';
                let strokesDisplay = '';
                
                if (p.completedAll) {
                    performanceText = `Completed in ${p.finalTime.toFixed(1)}s`;
                    strokesDisplay = p.finalStrokes;
                } else {
                    performanceText = `<span class="text-rose-400 font-bold">DNF</span> (Hole ${p.holeIndex + 1})`;
                    strokesDisplay = p.strokes;
                }
                
                const row = document.createElement('div');
                row.className = `flex items-center justify-between p-4 ${bgColor} border ${borderColor} rounded-lg transform transition-all duration-300 hover:scale-[1.01]`;
                row.innerHTML = `
                    <div class="flex items-center space-x-3">
                        <div class="w-10 h-10 bg-slate-950 text-white border border-slate-800 rounded-full flex items-center justify-center font-black text-lg shadow-[2px_2px_0px_#000000]">${rankIndicator}</div>
                        <div class="text-left">
                            <p class="font-bold text-white ${isLocal ? 'underline' : ''}">${p.name} ${isLocal ? '(You)' : ''}</p>
                            <p class="text-xs text-gray-400">${performanceText}</p>
                        </div>
                    </div>
                    <div class="text-right">
                        <span class="text-2xl font-black ${slotColor}">${strokesDisplay}</span>
                        <span class="text-xs text-gray-400 ml-1">shots</span>
                    </div>
                `;
                scoreboardContainer.appendChild(row);
            });
        }
        
        const playAgainBtn = document.getElementById('mpGameOverPlayAgain');
        if (playAgainBtn) {
            playAgainBtn.style.display = state.isBotGame ? 'block' : 'none';
        }
        
        document.getElementById('mpGameOverScreen').classList.add('active');
        
        if (won) {
            triggerWinArcadeSequence();
        } else {
            triggerGameOverSequence();
        }
        
        transitionMusicToMenu();
    }
}

function handleMPMessageReceived(packet) {
    if (packet.type === 'init') {
        mpState.courseLevels = packet.levels;
    } else if (packet.type === 'start_match') {
        startMultiplayerMatch();
    } else if (packet.type === 'finish') {
        const senderId = packet.playerId;
        const p = mpState.players.find(p => p.id === senderId);
        if (p) {
            p.finished = true;
            p.completedAll = true;
            p.finalStrokes = packet.totalStrokes;
            p.finalTime = packet.totalTime;
            
            // Trigger finishing announcement notification
            const rank = mpState.players.filter(pl => pl.finished && pl.completedAll).length;
            let placeSuffix = "th";
            if (rank === 1) placeSuffix = "st";
            else if (rank === 2) placeSuffix = "nd";
            else if (rank === 3) placeSuffix = "rd";
            
            showMultiplayerToast(`🏁 ${p.name.toUpperCase()} FINISHED! (${rank}${placeSuffix} Place)`, "text-pink-400 border-pink-500/30");
        }
        checkAndResolveMPFinish();
    }
}

function bindMultiplayerUIEvents() {
    // Multiplayer Mode Select Button - Opens the Multistep Submenu
    document.getElementById('multiplayerModeBtn').addEventListener('click', () => {
        playMenuClickSound();
        playVoiceOver("MULTIPLAYER SPEEDRUN!");
        
        document.getElementById('mainHomeMenu').style.display = 'none';
        document.getElementById('multiplayerSubmenu').style.display = 'flex';
    });

    // 9-Hole Speedrun Button - Opens the matchmaking modal
    document.getElementById('mp1v1SpeedrunBtn').addEventListener('click', () => {
        playMenuClickSound();
        playVoiceOver("1V1 SPEEDRUN!");
        
        ensurePlayerNameRegistered(() => {
            document.getElementById('startScreen').classList.remove('active');
            document.getElementById('mpSetupScreen').classList.add('active');
            
            // Check url params for direct join link
            const urlParams = new URLSearchParams(window.location.search);
            const joinRoomId = urlParams.get('join');
            if (joinRoomId) {
                document.getElementById('mpRoomInput').value = joinRoomId.toUpperCase();
            }
        });
    });

    // 1v3 Bot Speedrun (Practice) Option (Beautiful custom difficulty modal)
    const botDiffModal = document.getElementById('mpBotDifficultyModal');
    document.getElementById('mpBotRaceBtn').addEventListener('click', () => {
        playMenuClickSound();
        ensurePlayerNameRegistered(() => {
            if (botDiffModal) botDiffModal.classList.add('active');
        });
    });

    // Custom Difficulty buttons click listeners
    const handleDiffSelection = (difficulty) => {
        playMenuClickSound();
        if (botDiffModal) botDiffModal.classList.remove('active');
        document.getElementById('startScreen').classList.remove('active');
        document.getElementById('multiplayerSubmenu').style.display = 'none';
        document.getElementById('mainHomeMenu').style.display = 'block';
        initLocalBotMatch(difficulty);
    };

    document.getElementById('btnDiffEasy').addEventListener('click', () => handleDiffSelection(1));
    document.getElementById('btnDiffMedium').addEventListener('click', () => handleDiffSelection(2));
    document.getElementById('btnDiffHard').addEventListener('click', () => handleDiffSelection(3));
    
    document.getElementById('btnDiffCancel').addEventListener('click', () => {
        playMenuClickSound();
        if (botDiffModal) botDiffModal.classList.remove('active');
    });

    // Back to Home Button in Multiplayer Submenu
    document.getElementById('mpBackToHomeBtn').addEventListener('click', () => {
        playMenuClickSound();
        document.getElementById('multiplayerSubmenu').style.display = 'none';
        document.getElementById('mainHomeMenu').style.display = 'block';
    });

    // Back button inside Setup
    document.getElementById('mpSetupMainMenuBtn').addEventListener('click', () => {
        playMenuClickSound();
        triggerStickerTransition(() => {
            disconnectMultiplayer();
            document.getElementById('mpSetupScreen').classList.remove('active');
            showMainMenu();
        });
    });

    // Leave Lobby
    document.getElementById('mpLeaveBtn').addEventListener('click', () => {
        disconnectMultiplayer();
        document.getElementById('mpLobbyScreen').style.display = 'none';
        document.getElementById('mpMenuOptions').style.display = 'grid';
        document.getElementById('mpLobbyMainMenuBtnContainer').style.display = 'block';
    });

    // Host Button Click
    document.getElementById('mpHostBtn').addEventListener('click', () => {
        const roomId = generateRoomId();
        document.getElementById('mpMenuOptions').style.display = 'none';
        document.getElementById('mpLobbyMainMenuBtnContainer').style.display = 'none';
        document.getElementById('mpConnectingMsg').style.display = 'block';
        document.getElementById('mpSpinnerMsg').textContent = "Creating network lobby...";
        
        initHost(roomId, 
            (id) => {
                document.getElementById('mpConnectingMsg').style.display = 'none';
                document.getElementById('mpLobbyScreen').style.display = 'block';
                document.getElementById('mpRoomCodeDisplay').textContent = id;
                updateLobbyUI();
            },
            (err) => {
                alert("Multiplayer error: " + err);
                document.getElementById('mpConnectingMsg').style.display = 'none';
                document.getElementById('mpMenuOptions').style.display = 'grid';
                document.getElementById('mpLobbyMainMenuBtnContainer').style.display = 'block';
                disconnectMultiplayer();
            }
        );
    });

    // Join Button Click
    document.getElementById('mpJoinBtn').addEventListener('click', () => {
        const roomIdInput = document.getElementById('mpRoomInput').value.trim().toUpperCase();
        if (roomIdInput.length !== 5) {
            alert("Please enter a valid 5-character Room Code.");
            return;
        }
        
        document.getElementById('mpMenuOptions').style.display = 'none';
        document.getElementById('mpLobbyMainMenuBtnContainer').style.display = 'none';
        document.getElementById('mpConnectingMsg').style.display = 'block';
        document.getElementById('mpSpinnerMsg').textContent = `Connecting to room ${roomIdInput}...`;
        
        initClient(roomIdInput,
            () => {
                document.getElementById('mpConnectingMsg').style.display = 'none';
                document.getElementById('mpLobbyScreen').style.display = 'block';
                document.getElementById('mpRoomCodeDisplay').textContent = roomIdInput;
                updateLobbyUI();
            },
            (err) => {
                alert("Connection failed: " + err);
                document.getElementById('mpConnectingMsg').style.display = 'none';
                document.getElementById('mpMenuOptions').style.display = 'grid';
                document.getElementById('mpLobbyMainMenuBtnContainer').style.display = 'block';
                disconnectMultiplayer();
            }
        );
    });

    // Ready Button Click
    let clientReady = false;
    document.getElementById('mpReadyBtn').addEventListener('click', () => {
        clientReady = !clientReady;
        document.getElementById('mpReadyBtn').textContent = clientReady ? 'Cancel Ready' : 'Ready Up';
        document.getElementById('mpReadyBtn').className = clientReady ? 'button-primary flex-grow bg-yellow-600 hover:bg-yellow-700 !m-0 !py-2.5' : 'button-primary flex-grow bg-green-600 hover:bg-green-700 !m-0 !py-2.5';
        sendPacket({ type: 'ready', isReady: clientReady });
    });

    // Start Match Click (Host only)
    document.getElementById('mpStartBtn').addEventListener('click', () => {
        if (mpState.role !== 'host') return;
        
        console.log("Generating Course of 9 identical planetary levels...");
        const course = generateCourse(9);
        mpState.courseLevels = course;
        
        // Sync levels to client and trigger match start
        sendPacket({ type: 'init', levels: course });
        sendPacket({ type: 'start_match' });
        
        startMultiplayerMatch();
    });

    // Cancel Button Inside connecting spinner
    document.getElementById('mpCancelBtn').addEventListener('click', () => {
        disconnectMultiplayer();
        document.getElementById('mpConnectingMsg').style.display = 'none';
        document.getElementById('mpMenuOptions').style.display = 'grid';
        document.getElementById('mpLobbyMainMenuBtnContainer').style.display = 'block';
    });

    // Return to main menu from final scoreboard
    document.getElementById('mpGameOverMainMenu').addEventListener('click', () => {
        playMenuClickSound();
        triggerStickerTransition(() => {
            document.getElementById('mpGameOverScreen').classList.remove('active');
            document.getElementById('mpLobbyScreen').style.display = 'none';
            document.getElementById('mpMenuOptions').style.display = 'grid';
            document.getElementById('mpLobbyMainMenuBtnContainer').style.display = 'block';
            
            disconnectMultiplayer();
            showMainMenu();
        });
    });

    // Play again inside bot speedrun mode (same difficulty)
    document.getElementById('mpGameOverPlayAgain').addEventListener('click', () => {
        playMenuClickSound();
        document.getElementById('mpGameOverScreen').classList.remove('active');
        initLocalBotMatch(state.botDifficulty);
    });

    // Enable Start Button on Host when opponent is ready
    registerMPMessageCallback(handleMPMessageReceived);
    
    // Add logic to check ready states continuously in Lobby UI updates
    const lobbyTimer = setInterval(() => {
        if (mpState.isMultiplayer && mpState.role === 'host') {
            const startBtn = document.getElementById('mpStartBtn');
            if (startBtn) {
                const hasClients = mpState.players.length > 1;
                const allReady = mpState.players.every(p => p.id === 0 || p.ready);
                startBtn.disabled = !hasClients || !allReady;
            }
        }
    }, 250);
}

// Global trigger for opponent ball level warp/spawns pop-in effect
window.triggerOpponentPopIn = function(x, y) {
    if (state.collisionParticles) {
        const particleCount = 25;
        for (let i = 0; i < particleCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = Math.random() * 160 + 60;
            state.collisionParticles.push({
                x: x,
                y: y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                color: Math.random() > 0.4 ? '#FF00FF' : '#9D00FF', // Neon magenta and electric purple
                size: Math.random() * 4 + 3,
                life: Math.random() * 0.6 + 0.3,
                maxLife: 0.9
            });
        }
    }
    // Play a sci-fi warp pop sound!
    playSound(synth, "F5", "16n", Tone.now(), 0.35);
};

// Global trigger for a massive Hole-in-One victory celebration
window.triggerMassiveHoleInOneCelebration = function() {
    const canvas = document.getElementById('gameCanvas');
    const w = canvas ? canvas.width : PLAY_AREA_WIDTH;
    const h = canvas ? canvas.height : PLAY_AREA_HEIGHT;
    
    // Spawn staggered fireworks bursts across the screen (left, center, right)
    createFireworks(w * 0.25, h * 0.6);
    setTimeout(() => {
        createFireworks(w * 0.5, h * 0.45);
    }, 180);
    setTimeout(() => {
        createFireworks(w * 0.75, h * 0.6);
    }, 360);
    
    // Play a glorious retro synth fanfare!
    const now = Tone.now();
    playSound(synth, "C4", "16n", now);
    playSound(synth, "E4", "16n", now + 0.05);
    playSound(synth, "G4", "16n", now + 0.1);
    playSound(synth, "C5", "16n", now + 0.15);
    playSound(synth, "E5", "16n", now + 0.2);
    playSound(synth, "G5", "8n", now + 0.25);
    playSound(synth, "C6", "4n", now + 0.35);
};

// --- Celestial Power-Ups Warp and Rewind Main Triggers ---

function spawnWarpParticles(x, y, color = '#22D3EE') {
    if (!state.collisionParticles) return;
    const particleCount = 30;
    for (let i = 0; i < particleCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 200 + 80;
        state.collisionParticles.push({
            x: x,
            y: y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            color: color,
            size: Math.random() * 3 + 2,
            life: Math.random() * 0.5 + 0.2,
            maxLife: 0.7
        });
    }
}

export function triggerWarpFlash(colorClass = 'bg-cyan-400') {
    return; // Ditch the screen flash transition entirely per user request
}

export function triggerChronoRewind() {
    if (!state.rewindState) return;
    
    // Restore variables
    state.gameState.balls = JSON.parse(JSON.stringify(state.rewindState.balls));
    state.shotCount = state.rewindState.shotCount;
    state.totalScore = state.rewindState.totalScore;
    state.totalCoursePar = state.rewindState.totalCoursePar;
    state.totalCourseDistance = state.rewindState.totalCourseDistance;
    state.totalCourseOptimalDistance = state.rewindState.totalCourseOptimalDistance;
    state.gameState.previousShotTrail = JSON.parse(JSON.stringify(state.rewindState.previousShotTrail));
    state.gameState.lastShotDistanceTraveled = state.rewindState.lastShotDistanceTraveled;
    state.gameState.currentHoleDistanceTraveled = state.rewindState.currentHoleDistanceTraveled;
    state.activePowerUps = JSON.parse(JSON.stringify(state.rewindState.activePowerUps));
    state.gameState.coins = JSON.parse(JSON.stringify(state.rewindState.coins || []));
    state.totalSpaceBucks = state.rewindState.totalSpaceBucks;
    
    // Force stop velocities
    state.gameState.balls.forEach(ball => {
        ball.vx = 0;
        ball.vy = 0;
        ball.moving = false;
        ball.isSinking = false;
    });
    
    const primaryBall = state.gameState.balls[0];
    if (primaryBall) {
        state.viewTargetScale = 0.5; // Pull back zoom more during rewind
        state.viewTargetCenterX = primaryBall.x;
        state.viewTargetCenterY = primaryBall.y;
        state.viewCurrentScale = 0.5;
        state.viewCurrentCenterX = primaryBall.x;
        state.viewCurrentCenterY = primaryBall.y;
        state.zoomInteractionEndTime = 0;
    }
    
    state.rewindState = null; // Clear the rewindState slot
    
    // Trigger stardust cyber flash transition
    triggerWarpFlash('bg-cyan-400');
    
    // Play sci-fi rewind sound effect
    playSound(synth, "C3", "4n", Tone.now());
    
    // Spawns particles at the ball's location
    if (primaryBall) {
        spawnWarpParticles(primaryBall.x, primaryBall.y, '#22D3EE');
    }
    
    updateInGameUIDisplay();
}

// Register on window for ui.js callback access
window.triggerChronoRewind = triggerChronoRewind;

// --- Dynamic GPU-Accelerated CRT Bulge Map Generator ---


// --- Application Init Bootstrapper ---
function bootstrap() {
    // Disable right click context menu to prevent browser popups and keep arcade input focused
    window.addEventListener('contextmenu', (e) => e.preventDefault());
    
    resizeCanvas();
    loadGameData(); 
    preloadGlobalLeaderboardData();
    syncLocalScoresToGlobal();
    loadSavedLevels();
    initStars(); 
    setupUIEventListeners();
    setupTutorialModalListeners();
    bindMultiplayerUIEvents();
    
    // Synced initial visual updates
    showMainMenu(); 
    
    // Set UI sound/music and CRT buttons text
    updateMuteUILabels();
    updateCRTEffectUI();
    
    syncMuteState();
    if (!state.IS_MUTED) {
        transitionMusicToMenu();
    }

    // Check if the user scanned a QR code or used a join link
    const urlParams = new URLSearchParams(window.location.search);
    const joinRoomId = urlParams.get('join');
    if (joinRoomId) {
        // Clear the query param from the URL to keep it clean and prevent automatic re-joins
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);

        // Gate behind name registration if using the default "Player" name
        ensurePlayerNameRegistered(() => {
            // Transition from Home Screen to Multiplayer Setup Screen
            document.getElementById('startScreen').classList.remove('active');
            document.getElementById('mpSetupScreen').classList.add('active');

            // Prefill the room input field with the join code
            const inputField = document.getElementById('mpRoomInput');
            if (inputField) {
                inputField.value = joinRoomId.toUpperCase();
            }

            // Programmatically trigger the Join button click to connect to the lobby straight away
            const joinBtn = document.getElementById('mpJoinBtn');
            if (joinBtn) {
                joinBtn.click();
            }
        });
    }
}

// --- Procedural Arcade Coin Generation & Logic ---
function generateCoinsForLevel() {
    state.gameState.coins = [];
    if (state.currentPlayingMode !== 'powerup') return;
    if (!state.gameState.planets || state.gameState.planets.length === 0) return;
    
    state.gameState.planets.forEach((planet, planetIdx) => {
        const isStartPlanet = (planetIdx === 0);
        const isGoalPlanet = (planetIdx === state.gameState.goal.planetIndex);
        
        // 75% chance to spawn coins on any given planet
        if (Math.random() > 0.75) return;
        
        // 20% chance to spawn a single majestic 10-coin, otherwise a cluster of 3-5 normal coins
        const isRareTen = Math.random() < 0.20;
        
        if (isRareTen) {
            const angle = findValidCoinAngle(planet, planetIdx, isStartPlanet, isGoalPlanet, 1);
            if (angle !== null) {
                const coinOffset = 15; // pixels off surface
                const dist = planet.r + coinOffset;
                const x = planet.x + dist * Math.cos(angle);
                const y = planet.y + dist * Math.sin(angle);
                
                state.gameState.coins.push({
                    id: `coin_${planetIdx}_rare_${Math.random().toString(36).substr(2, 5)}`,
                    x, y,
                    r: 9,
                    value: 10,
                    collected: false,
                    planetIndex: planetIdx,
                    angle: angle,
                    pulseOffset: Math.random() * 100
                });
            }
        } else {
            const clusterSize = Math.floor(Math.random() * 3) + 3; // 3 to 5 coins
            const baseAngle = findValidCoinAngle(planet, planetIdx, isStartPlanet, isGoalPlanet, clusterSize);
            if (baseAngle !== null) {
                const angleSpacing = 0.14;
                const coinOffset = 13; // pixels off surface
                
                for (let i = 0; i < clusterSize; i++) {
                    const angle = baseAngle + (i - (clusterSize - 1) / 2) * angleSpacing;
                    const dist = planet.r + coinOffset;
                    const x = planet.x + dist * Math.cos(angle);
                    const y = planet.y + dist * Math.sin(angle);
                    
                    state.gameState.coins.push({
                        id: `coin_${planetIdx}_norm_${i}_${Math.random().toString(36).substr(2, 5)}`,
                        x, y,
                        r: 6,
                        value: 1,
                        collected: false,
                        planetIndex: planetIdx,
                        angle: angle,
                        pulseOffset: Math.random() * 100
                    });
                }
            }
        }
    });
}

function findValidCoinAngle(planet, planetIdx, isStartPlanet, isGoalPlanet, clusterSize = 1) {
    const angleSpacing = 0.14;
    const halfWidth = (clusterSize * angleSpacing) / 2;
    
    for (let attempt = 0; attempt < 50; attempt++) {
        const testAngle = Math.random() * 2 * Math.PI;
        let valid = true;
        
        const minTestAngle = testAngle - halfWidth - 0.05;
        const maxTestAngle = testAngle + halfWidth + 0.05;
        
        // Avoid Bunkers
        if (planet.bunkers && planet.bunkers.length > 0) {
            for (const b of planet.bunkers) {
                const bunkerWidth = b.radius / planet.r;
                const minB = b.angle - bunkerWidth - 0.1;
                const maxB = b.angle + bunkerWidth + 0.1;
                if (rangesOverlap(minTestAngle, maxTestAngle, minB, maxB)) {
                    valid = false;
                    break;
                }
            }
        }
        if (!valid) continue;
        
        // Avoid Goal Hole
        if (isGoalPlanet) {
            const goalAngle = state.gameState.goal.angleOnPlanet;
            const minG = goalAngle - 0.25;
            const maxG = goalAngle + 0.25;
            if (rangesOverlap(minTestAngle, maxTestAngle, minG, maxG)) {
                valid = false;
                continue;
            }
        }
        
        // Avoid Start Tee Spawn
        if (isStartPlanet) {
            const spawnAngle = state.gameState.levelSpawnAngle || 0;
            const minS = spawnAngle - 0.25;
            const maxS = spawnAngle + 0.25;
            if (rangesOverlap(minTestAngle, maxTestAngle, minS, maxS)) {
                valid = false;
                continue;
            }
        }
        
        if (valid) return testAngle;
    }
    return null;
}

function rangesOverlap(minA, maxA, minB, maxB) {
    const norm = (a) => (a % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
    const centerA = (minA + maxA) / 2;
    const centerB = (minB + maxB) / 2;
    const halfSizeA = (maxA - minA) / 2;
    const halfSizeB = (maxB - minB) / 2;
    
    let diff = Math.abs(norm(centerA) - norm(centerB));
    if (diff > Math.PI) {
        diff = 2 * Math.PI - diff;
    }
    return diff < (halfSizeA + halfSizeB);
}

function playCoinSound(isMega) {
    if (state.IS_MUTED) return;
    const now = Tone.now();
    if (isMega) {
        playSound(synth, "C5", "16n", now, 0.45);
        playSound(synth, "E5", "16n", now + 0.05, 0.45);
        playSound(synth, "G5", "16n", now + 0.1, 0.45);
        playSound(synth, "C6", "8n", now + 0.15, 0.45);
    } else {
        playSound(synth, "G5", "16n", now, 0.4);
        playSound(synth, "C6", "8n", now + 0.07, 0.4);
    }
}

function spawnCoinCollectParticles(x, y, isMega) {
    if (!state.collisionParticles) return;
    const particleCount = isMega ? 22 : 12;
    const colors = isMega ? ['#C084FC', '#F472B6', '#F59E0B', '#FFFFFF'] : ['#FBBF24', '#F59E0B', '#FFFFFF'];
    
    for (let i = 0; i < particleCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 120 + 40;
        const color = colors[Math.floor(Math.random() * colors.length)];
        
        state.collisionParticles.push({
            x: x,
            y: y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            color: color,
            size: Math.random() * 3 + (isMega ? 2.5 : 1.5),
            life: Math.random() * 0.4 + 0.2,
            maxLife: 0.6
        });
    }
}

bootstrap();

registerBotCallbacks({
    startMultiplayerMatch,
    checkAndResolveMPFinish,
    updateMPLeaderboardHUD,
    generateCourse,
    showMultiplayerToast
});

// Start background chiptune menu soundtrack on first user gesture (comply with browser autoplay policies)
document.addEventListener('click', () => {
    if (state.IS_MUTED) return;
    if (!state.isArcadeRunOver && state.currentPlayingMode === null) {
        transitionMusicToMenu();
    }
}, { once: true });
